import { useMemo } from 'react';
import { useTrackEvent } from '@jotpsych/statsig';

import { EventNames } from '../constants';

/**
 * High-level hook for tracking events across the application
 * Provides pre-configured tracking methods for all features
 */
export function useEventTracking() {
  const { trackEvent } = useTrackEvent();

  // Single memoization for all metrics
  return useMemo(
    () => ({
      knowledgeBase: {
        entered: () => trackEvent(EventNames.KNOWLEDGE_BASE_ENTERED),
        articleSelected: () => trackEvent(EventNames.KNOWLEDGE_BASE_ARTICLE_SELECTED),
        searchOpened: () => trackEvent(EventNames.KNOWLEDGE_BASE_SEARCH_OPENED),
        searchChanged: () => trackEvent(EventNames.KNOWLEDGE_BASE_SEARCH_CHANGED),
        linkCopyClicked: () => trackEvent(EventNames.KNOWLEDGE_BASE_LINK_COPY_CLICKED),
        printClicked: () => trackEvent(EventNames.KNOWLEDGE_BASE_PRINT_CLICKED),
        retryClicked: () => trackEvent(EventNames.KNOWLEDGE_BASE_RETRY_CLICKED),
      },
      // Intake Hub typed entry — stable generated identifiers only, never field values or labels.
      intakeHub: {
        agreementActionClicked: (metadata: {
          action: 'prepare' | 'sign' | 'reload' | 'continue';
          document_key?: 'msa' | 'order_form' | 'baa';
        }) => {
          trackEvent(EventNames.INTAKE_HUB_AGREEMENT_ACTION_CLICKED, { metadata });
        },
        providerRosterJoinClicked: () => {
          trackEvent(EventNames.INTAKE_HUB_PROVIDER_ROSTER_JOIN_CLICKED);
        },
        stediConnectionActionClicked: (metadata: {
          action: 'connect' | 'reconnect' | 'cancel';
        }) => {
          trackEvent(EventNames.INTAKE_HUB_STEDI_CONNECTION_ACTION_CLICKED, { metadata });
        },
        serviceSelectionChanged: (metadata: { service_keys: string; selected_count: number }) => {
          trackEvent(EventNames.INTAKE_HUB_SERVICE_SELECTION_CHANGED, { metadata });
        },
        fieldEditAttempted: (
          metadata: Record<string, unknown> & {
            destination_kind: string;
            field_id: string;
            field_type: string;
          },
        ) => {
          trackEvent(EventNames.INTAKE_HUB_FIELD_EDIT_ATTEMPTED, { metadata });
        },
        documentPreviewOpened: (metadata: { artifact_id: string; file_type: string }) => {
          trackEvent(EventNames.INTAKE_HUB_DOCUMENT_PREVIEW_OPENED, { metadata });
        },
        documentDownloadClicked: (metadata: { artifact_id: string; file_type: string }) => {
          trackEvent(EventNames.INTAKE_HUB_DOCUMENT_DOWNLOAD_CLICKED, { metadata });
        },
        folderUploadClicked: (metadata: { surface: 'dashboard' | 'document_canvas' }) => {
          trackEvent(EventNames.INTAKE_HUB_FOLDER_UPLOAD_CLICKED, { metadata });
        },
        suggestionActionClicked: (metadata: {
          action: 'accept' | 'accept_all' | 'discard';
          suggestion_count: number;
        }) => {
          trackEvent(EventNames.INTAKE_HUB_SUGGESTION_ACTION_CLICKED, { metadata });
        },
        reviewConfirmedClicked: (metadata: { section: 'clinic_setup' | 'your_intake' }) => {
          trackEvent(EventNames.INTAKE_HUB_REVIEW_CONFIRMED_CLICKED, { metadata });
        },
        forecastAcknowledgedClicked: (metadata: { total_required: number }) => {
          trackEvent(EventNames.INTAKE_HUB_FORECAST_ACKNOWLEDGED_CLICKED, { metadata });
        },
        completionDashboardClicked: (metadata: {
          active_page_count: number;
          /** Active pages satisfying the same predicate that unlocks terminal completion. */
          completed_page_count: number;
          percent_complete: number;
        }) => {
          trackEvent(EventNames.INTAKE_HUB_COMPLETION_DASHBOARD_CLICKED, { metadata });
        },
        trailingItemsClicked: (metadata: {
          /** False once every answerable trailing field has a durable value. */
          items_remain: boolean;
          /** Catalog canvas key of the trailing page, e.g. 'T1'. */
          page_key: string;
        }) => {
          trackEvent(EventNames.INTAKE_HUB_TRAILING_ITEMS_CLICKED, { metadata });
        },
        payerSelected: (metadata: {
          /** Which roster section the payer was added to. */
          enrollment_status: 'already_enrolled' | 'wants_to_enroll';
          /** Public STEDI primary payer ID, e.g. '60054'. Never the payer name. */
          payer_id: string;
          /** Roster size after the add. */
          roster_size: number;
        }) => {
          trackEvent(EventNames.INTAKE_HUB_PAYER_SELECTED, { metadata });
        },
        payerRemoveClicked: (metadata: {
          /** Public STEDI primary payer ID, e.g. '60054'. Never the payer name. */
          payer_id: string;
          /** Roster size after the removal. */
          roster_size: number;
        }) => {
          trackEvent(EventNames.INTAKE_HUB_PAYER_REMOVE_CLICKED, { metadata });
        },
        payerServiceChanged: (metadata: {
          /** Whether the service is on after the change. */
          enabled: boolean;
          /** Public STEDI primary payer ID, e.g. '60054'. Never the payer name. */
          payer_id: string;
          /** Stable service key. */
          service: 'jotbill' | 'jotcred';
        }) => {
          trackEvent(EventNames.INTAKE_HUB_PAYER_SERVICE_CHANGED, { metadata });
        },
      },
      // Payer Connections — the Intake Hub's clinic-facing payer section.
      // Counts and Stedi's own status/rail slugs only; payer names, payer
      // ids, and enrollment ids all stay on the client.
      payerConnections: {
        /**
         * Fires once per mount, after the live read has succeeded AND the
         * claim count has settled — `live_count` and `ready_count` both
         * depend on the claim count (AC-4), so firing earlier would report
         * zero live payers for every organization.
         *
         * `payer_count` is every distinct payer the organization has an
         * enrollment for, live ones included; the other three are its
         * subsets.
         */
        sectionOpened: (metadata: {
          payer_count: number;
          needs_you_count: number;
          ready_count: number;
          live_count: number;
        }) => {
          trackEvent(EventNames.PAYER_CONNECTIONS_SECTION_OPENED, { metadata });
        },
        stediLinkClicked: (metadata: {
          /** Stedi's status verbatim, e.g. 'PROVIDER_ACTION_REQUIRED'. */
          enrollment_status: string;
          /** '837P' or '835'. */
          transaction_type: string;
        }) => {
          trackEvent(EventNames.PAYER_CONNECTIONS_STEDI_LINK_CLICKED, { metadata });
        },
        /** No metadata: the question is only whether Ready to bill leads to a claim. */
        firstClaimLinkClicked: () => {
          trackEvent(EventNames.PAYER_CONNECTIONS_FIRST_CLAIM_LINK_CLICKED);
        },
      },
      // JotBill Revenue Analytics
      jotbillAnalytics: {
        revenueOpened: (metadata: { view: 'clean' | 'ar' }) =>
          trackEvent(EventNames.JOTBILL_REVENUE_OPENED, { metadata }),
        revenueClicked: (metadata: { view: 'clean' | 'ar'; action: string }) =>
          trackEvent(EventNames.JOTBILL_REVENUE_CLICKED, { metadata }),
        pageViewed: (metadata?: Record<string, unknown> & { organization_id?: string }) => {
          trackEvent(EventNames.JOTBILL_ANALYTICS_PAGE_VIEWED, { metadata });
        },
        filterChanged: (
          metadata?: Record<string, unknown> & { organization_id?: string; filter_type?: string },
        ) => {
          trackEvent(EventNames.JOTBILL_ANALYTICS_FILTER_CHANGED, { metadata });
        },
        widgetRetry: (
          metadata?: Record<string, unknown> & { organization_id?: string; widget_type?: string },
        ) => {
          trackEvent(EventNames.JOTBILL_ANALYTICS_WIDGET_RETRY, { metadata });
        },
      },
      // Practice Metrics (semantic layer) — the attention → intent funnel.
      // HIPAA-safe: ids, metric slugs (a metric type, never patient data), counts.
      practiceMetrics: {
        homeSectionShown: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            tile_count?: number;
            has_opportunity?: boolean;
          },
        ) => {
          trackEvent(EventNames.PRACTICE_METRICS_HOME_SECTION_SHOWN, { metadata });
        },
        tileShown: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            metric_slug?: string;
            position?: number;
            surface?: 'home' | 'dashboard';
          },
        ) => {
          trackEvent(EventNames.PRACTICE_METRICS_TILE_SHOWN, { metadata });
        },
        dashboardOpened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            tile_count?: number;
            gated_count?: number;
            opportunity_count?: number;
            source?: 'home_link' | 'direct';
          },
        ) => {
          trackEvent(EventNames.PRACTICE_METRICS_DASHBOARD_OPENED, { metadata });
        },
        dashboardExited: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            duration_seconds?: number;
          },
        ) => {
          trackEvent(EventNames.PRACTICE_METRICS_DASHBOARD_EXITED, { metadata });
        },
        tileHovered: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            metric_slug?: string;
            surface?: 'home' | 'dashboard';
          },
        ) => {
          trackEvent(EventNames.PRACTICE_METRICS_TILE_HOVERED, { metadata });
        },
        tileClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            metric_slug?: string;
            surface?: 'home' | 'dashboard';
          },
        ) => {
          trackEvent(EventNames.PRACTICE_METRICS_TILE_CLICKED, { metadata });
        },
        detailOpened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            metric_slug?: string;
            value_pct?: number | null;
            meets_gate?: boolean;
            has_opportunity?: boolean;
          },
        ) => {
          trackEvent(EventNames.PRACTICE_METRICS_DETAIL_OPENED, { metadata });
        },
        improveClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            metric_slug?: string;
            surface?: 'home' | 'dashboard' | 'detail';
            value_pct?: number | null;
            target?: number | null;
            gap?: number | null;
          },
        ) => {
          trackEvent(EventNames.PRACTICE_METRICS_IMPROVE_CLICKED, { metadata });
        },
        optimizationBookingClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            metric_slug?: string;
            surface?: 'home' | 'dashboard' | 'detail';
            target?: number | null;
            gap?: number | null;
          },
        ) => {
          trackEvent(EventNames.PRACTICE_METRICS_OPTIMIZATION_BOOKING_CLICKED, { metadata });
        },
        optimizationClosed: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            metric_slug?: string;
            surface?: 'home' | 'dashboard' | 'detail';
            reached_booking?: boolean;
          },
        ) => {
          trackEvent(EventNames.PRACTICE_METRICS_OPTIMIZATION_CLOSED, { metadata });
        },
      },
      // Practice cleanup wizard (GDN-2295) — open → steps → apply funnel.
      // HIPAA-safe: org id, step labels (fixed strings), and counts only.
      practiceCleanup: {
        wizardOpened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            source?: string;
            data_source?: 'fixture' | 'live';
          },
        ) => {
          trackEvent(EventNames.CLEANUP_WIZARD_OPENED, { metadata });
        },
        stepSubmitted: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            step_label?: string;
            step_index?: number;
            total_steps?: number;
          },
        ) => {
          trackEvent(EventNames.CLEANUP_WIZARD_STEP_SUBMITTED, { metadata });
        },
        applyClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            archive_count?: number;
            restore_count?: number;
            merge_count?: number;
            promote_count?: number;
            rename_count?: number;
            data_source?: 'fixture' | 'live';
          },
        ) => {
          trackEvent(EventNames.CLEANUP_WIZARD_APPLY_CLICKED, { metadata });
        },
        wizardClosed: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            last_step_label?: string;
            last_step_index?: number;
            total_steps?: number;
            applied?: boolean;
            duration_seconds?: number;
          },
        ) => {
          trackEvent(EventNames.CLEANUP_WIZARD_CLOSED, { metadata });
        },
      },
      // Evergreen appointment-type catalog (GDN-2335) — browse → filter →
      // act. HIPAA-safe: org/type ids, fixed enums, and counts only (never
      // the search text — has_search boolean instead).
      typeCatalog: {
        opened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            total_types?: number;
            archived_count?: number;
            shared_count?: number;
            personal_count?: number;
          },
        ) => {
          trackEvent(EventNames.TYPE_CATALOG_OPENED, { metadata });
        },
        filterChanged: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            scope?: 'all' | 'shared' | 'personal';
            owners_count?: number;
            has_search?: boolean;
            show_archived?: boolean;
          },
        ) => {
          trackEvent(EventNames.TYPE_CATALOG_FILTER_CHANGED, { metadata });
        },
        sortChanged: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            sort_key?: string;
            direction?: 'asc' | 'desc';
          },
        ) => {
          trackEvent(EventNames.TYPE_CATALOG_SORT_CHANGED, { metadata });
        },
        rowDefaultsOpened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            encounter_type_id?: string;
          },
        ) => {
          trackEvent(EventNames.TYPE_CATALOG_ROW_DEFAULTS_OPENED, { metadata });
        },
        actionSubmitted: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            action?: 'archive' | 'restore';
            count?: number;
          },
        ) => {
          trackEvent(EventNames.TYPE_CATALOG_ACTION_SUBMITTED, { metadata });
        },
        undoClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            action?: 'archive' | 'restore';
            count?: number;
          },
        ) => {
          trackEvent(EventNames.TYPE_CATALOG_UNDO_CLICKED, { metadata });
        },
        manageTypesClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
          },
        ) => {
          trackEvent(EventNames.TYPE_CATALOG_MANAGE_TYPES_CLICKED, {
            metadata,
          });
        },
      },
      // Note-template catalog (GDN-2482): browse → filter → act. HIPAA-safe:
      // org/template ids, fixed enums, counts only — never template titles.
      templateCatalog: {
        opened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            total_templates?: number;
            archived_count?: number;
            shared_count?: number;
            personal_count?: number;
            public_count?: number;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_CATALOG_OPENED, { metadata });
        },
        filterChanged: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            scope?: 'all' | 'shared' | 'personal';
            owners_count?: number;
            has_search?: boolean;
            show_archived?: boolean;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_CATALOG_FILTER_CHANGED, { metadata });
        },
        sortChanged: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            sort_key?: string;
            direction?: 'asc' | 'desc';
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_CATALOG_SORT_CHANGED, { metadata });
        },
        rowDetailOpened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            row_id?: string;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_CATALOG_ROW_DETAIL_OPENED, { metadata });
        },
        actionSubmitted: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            action?: 'archive' | 'restore';
            count?: number;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_CATALOG_ACTION_SUBMITTED, { metadata });
        },
        undoClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            action?: 'archive' | 'restore';
            count?: number;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_CATALOG_UNDO_CLICKED, { metadata });
        },
      },
      // Patient Requirements catalog (GDN-3224, AC-1): the practice-admin org
      // settings surface. HIPAA-safe — org/definition ids, fixed kind enums and
      // counts only. `label` is practice-authored patient-facing free text and
      // MUST NOT appear in any payload; send label_length if length matters.
      patientRequirementsCatalog: {
        opened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            definition_count?: number;
            active_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_CATALOG_OPENED, { metadata });
        },
        labelChanged: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            definition_id?: string;
            kind?: string;
            label_length?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_DEFINITION_LABEL_CHANGED, {
            metadata,
          });
        },
        activeToggled: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            definition_id?: string;
            kind?: string;
            active?: boolean;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_DEFINITION_ACTIVE_TOGGLED, {
            metadata,
          });
        },
        createOpened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            available_kinds_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_DEFINITION_CREATE_OPENED, {
            metadata,
          });
        },
        createSubmitted: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            kind?: string;
            default_for_new_patients?: boolean;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_DEFINITION_CREATE_SUBMITTED, {
            metadata,
          });
        },
      },
      // Missing-requirement worklist (GDN-3226). Same HIPAA rule as the catalog
      // above: ids, fixed kind enums and counts only — never a requirement
      // label, which is practice-authored patient-facing free text.
      patientRequirementsWorklist: {
        opened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            kind?: string;
            total?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_WORKLIST_OPENED, { metadata });
        },
        pageChanged: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            kind?: string;
            page?: number;
            direction?: 'next' | 'previous';
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_WORKLIST_PAGE_CHANGED, { metadata });
        },
        retryClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            kind?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_WORKLIST_RETRY_CLICKED, { metadata });
        },
        patientClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_id?: string;
            kind?: string;
            row_index?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_WORKLIST_PATIENT_CLICKED, {
            metadata,
          });
        },
        deliveryHistoryOpened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_id?: string;
            touchpoint_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_DELIVERY_HISTORY_OPENED, {
            metadata,
          });
        },
      },
      // Provider alert in the recording window (GDN-3225, AC-15). Ids, counts
      // and the encounter only — never a requirement label, and no financial
      // detail while Q3 (who may see money in this role) is open.
      patientRequirementsProviderAlert: {
        opened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_id?: string;
            encounter_id?: string;
            open_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_PROVIDER_ALERT_OPENED, { metadata });
        },
        checklistOpened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_id?: string;
            encounter_id?: string;
            open_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_PROVIDER_ALERT_CHECKLIST_OPENED, {
            metadata,
          });
        },
        remindClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_id?: string;
            encounter_id?: string;
            open_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_PROVIDER_ALERT_REMIND_CLICKED, {
            metadata,
          });
        },
        openRecordClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_id?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_PROVIDER_ALERT_OPEN_RECORD_CLICKED, {
            metadata,
          });
        },
      },
      // GDN-3226: the Requirements section on the patient record.
      // No free text ever reaches these payloads — a waive reason is written by
      // a staff member about a patient, so only the fact of the waive is sent.
      patientRequirementsRecord: {
        opened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_id?: string;
            row_count?: number;
            missing_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_RECORD_SECTION_OPENED, {
            metadata,
          });
        },
        sendClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_id?: string;
            /** 'Send request' or 'Remind' — which half of AC-4 fired. */
            action?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_RECORD_SEND_CLICKED, { metadata });
        },
        waiveSubmitted: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_id?: string;
            /** Length only. The reason itself is free text and never sent. */
            reason_length?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_RECORD_WAIVE_SUBMITTED, {
            metadata,
          });
        },
        reopenClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_id?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_RECORD_REOPEN_CLICKED, {
            metadata,
          });
        },
      },

      /**
       * Bulk launch from the Settings catalog row (GDN-3230, AC-10 … AC-14).
       *
       * Ids, kinds and counts only. The requirement label and the message copy
       * are both practice-authored patient-facing text and never enter a
       * payload.
       */
      patientRequirementsBulkLaunch: {
        /** The preview panel opened on a requirement's row. */
        opened: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_requirement_definition_id?: string;
            kind?: string;
            eligible_count?: number;
            already_complete_count?: number;
            excluded_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_BULK_LAUNCH_OPENED, { metadata });
        },
        /**
         * Launch confirmed. `message_count` is deliberately separate from
         * `eligible_count`: one patient reachable on two channels is one
         * eligible patient and two messages, and reporting only one of them
         * makes the funnel unreadable later.
         */
        submitted: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_requirement_definition_id?: string;
            kind?: string;
            eligible_count?: number;
            message_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_BULK_LAUNCH_SUBMITTED, { metadata });
        },
        /** The preview was dismissed without launching — the abandon half. */
        closed: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_requirement_definition_id?: string;
            kind?: string;
            eligible_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_BULK_LAUNCH_CLOSED, { metadata });
        },
        /**
         * Cancel requested on a running batch (AC-14). The counts are taken at
         * the moment of the click, which is what says how much was stopped.
         */
        stopClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_requirement_definition_id?: string;
            batch_id?: string;
            sent_count?: number;
            queued_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_BULK_STOP_CLICKED, { metadata });
        },
      },
      /**
       * The patients-list rail Requirements card (GDN-3422, AC-23).
       * Ids, kinds and counts only — never the practice-authored label.
       */
      patientRequirementsRail: {
        /** A requirement row was clicked and the list filtered to it. */
        filterSelected: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_requirement_definition_id?: string;
            kind?: string;
            /** The count on the row at the moment it was clicked. */
            missing_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_RAIL_FILTER_SELECTED, { metadata });
        },
        /**
         * The requirement filter was removed — by clicking the active row again
         * or deleting the chip.
         *
         * `_clicked` rather than `_cleared` deliberately: `_cleared` is not on
         * the Metabase suffix allowlist and would be dropped without an error.
         */
        clearClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            patient_requirement_definition_id?: string;
            kind?: string;
            source?: 'rail_row' | 'chip';
          },
        ) => {
          trackEvent(EventNames.PATIENT_REQUIREMENTS_RAIL_CLEAR_CLICKED, { metadata });
        },
      },
      // Recording metrics
      recording: {
        start: (
          metadata?: Record<string, unknown> & {
            template_id?: string;
            recording_type?: string;
            session_id?: string;
            recording_pipeline?: 'streaming';
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.RECORDING_STARTED, { metadata });
        },
        futureEncounterDialogOpened: (metadata?: { encounter_id?: string; visit_day?: string }) => {
          trackEvent(EventNames.RECORDING_FUTURE_ENCOUNTER_DIALOG_OPENED, { metadata });
        },
        futureEncounterProceedClicked: (metadata?: { encounter_id?: string }) => {
          trackEvent(EventNames.RECORDING_FUTURE_ENCOUNTER_PROCEED_CLICKED, { metadata });
        },
        futureEncounterCancelClicked: (metadata?: { encounter_id?: string }) => {
          trackEvent(EventNames.RECORDING_FUTURE_ENCOUNTER_CANCEL_CLICKED, { metadata });
        },
        stop: (
          metadata?: Record<string, unknown> & {
            audio_quality?: string;
            file_size?: number;
            duration_seconds?: number;
            session_id?: string;
            recording_pipeline?: 'streaming';
          },
        ) => {
          trackEvent(EventNames.RECORDING_STOPPED, {
            metadata: {
              ...(metadata?.file_size && { file_size: String(metadata.file_size) }),
              ...(metadata?.duration_seconds && {
                duration_seconds: String(metadata.duration_seconds),
              }),
              ...metadata,
            },
          });
        },
        error: (
          error: string,
          metadata?: Record<string, unknown> & {
            session_id?: string;
            recording_pipeline?: 'streaming';
          },
        ) => {
          trackEvent(EventNames.RECORDING_ERROR, {
            metadata: { error_message: error, ...metadata },
          });
        },
        startFailed: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            error_type?: string;
            error_message?: string;
            recording_pipeline?: 'streaming';
            had_api_session?: boolean;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.RECORDING_START_FAILED, {
            metadata: {
              ...metadata,
              ...(metadata?.had_api_session !== undefined && {
                had_api_session: metadata.had_api_session ? 'true' : 'false',
              }),
            },
          });
        },
        shortDismissed: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            encounter_id?: string;
            duration_seconds?: number;
            recording_pipeline?: 'streaming';
          },
        ) => {
          trackEvent(EventNames.RECORDING_SHORT_DISMISSED, {
            metadata: {
              ...metadata,
              ...(metadata?.duration_seconds !== undefined && {
                duration_seconds: String(metadata.duration_seconds),
              }),
            },
          });
        },
        noAudioDetected: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            encounter_id?: string;
            recording_pipeline?: 'streaming';
            audio_device_service_active?: boolean;
            duration_at_detection_seconds?: number;
          },
        ) => {
          trackEvent(EventNames.RECORDING_NO_AUDIO_DETECTED, {
            metadata: {
              ...metadata,
              ...(metadata?.audio_device_service_active !== undefined && {
                audio_device_service_active: String(metadata.audio_device_service_active),
              }),
              ...(metadata?.duration_at_detection_seconds !== undefined && {
                duration_at_detection_seconds: String(metadata.duration_at_detection_seconds),
              }),
            },
          });
        },

        // Recording observability events (stuck-state detection)
        stuckStateDetected: (
          metadata?: Record<string, unknown> & {
            stuck_type?: string;
            recording_pipeline?: 'streaming';
            recording_status_snapshot?: string;
            recording_starter_id?: string;
            current_recording_session_id?: string;
            elapsed_time?: string;
            is_leaving?: string;
            url_path?: string;
          },
        ) => {
          trackEvent(EventNames.RECORDING_STUCK_STATE_DETECTED, { metadata });
        },
        readyToRecordHackTriggered: (
          metadata?: Record<string, unknown> & {
            recording_pipeline?: 'streaming';
            recording_status_snapshot?: string;
            trigger_location?: string;
          },
        ) => {
          trackEvent(EventNames.RECORDING_READY_TO_RECORD_HACK_TRIGGERED, { metadata });
        },
        startedSuccessfully: (
          metadata?: Record<string, unknown> & {
            recording_pipeline?: 'streaming';
            time_in_starting_ms?: string;
          },
        ) => {
          trackEvent(EventNames.RECORDING_STARTED_SUCCESSFULLY, { metadata });
        },
        cleanupCompleted: (
          metadata?: Record<string, unknown> & {
            cleanup_type?: 'full' | 'soft_navigation';
            recording_pipeline?: 'streaming';
            final_status_snapshot?: string;
            recording_paused_reset?: string;
            is_reuploading_reset?: string;
          },
        ) => {
          trackEvent(EventNames.RECORDING_CLEANUP_COMPLETED, { metadata });
        },
        staleStateAutoRecovered: (
          metadata?: Record<string, unknown> & {
            stuck_type?: 'stale_active' | 'dead_button';
            recording_pipeline?: 'streaming';
            previous_status_snapshot?: string;
            recording_starter_id?: string;
            current_recording_session_id?: string;
          },
        ) => {
          trackEvent(EventNames.RECORDING_STALE_STATE_AUTO_RECOVERED, { metadata });
        },
      },

      // Note generation metrics
      note: {
        cosignDismissClicked: (noteId: string) => {
          trackEvent(EventNames.NOTE_COSIGN_DISMISS_CLICKED, {
            metadata: { note_id: noteId },
          });
        },
        cosignSnoozeClicked: (noteId: string) => {
          trackEvent(EventNames.NOTE_COSIGN_SNOOZE_CLICKED, {
            metadata: { note_id: noteId },
          });
        },
        cosignRestoreClicked: (noteId: string) => {
          trackEvent(EventNames.NOTE_COSIGN_RESTORE_CLICKED, {
            metadata: { note_id: noteId },
          });
        },
        cosignShowDismissedToggled: (showDismissed: boolean) => {
          trackEvent(EventNames.NOTE_COSIGN_SHOW_DISMISSED_TOGGLED, {
            metadata: { show_dismissed: String(showDismissed) },
          });
        },
        resignDismissClicked: (noteId: string) => {
          trackEvent(EventNames.NOTE_RESIGN_DISMISS_CLICKED, {
            metadata: { note_id: noteId },
          });
        },
        resignSnoozeClicked: (noteId: string) => {
          trackEvent(EventNames.NOTE_RESIGN_SNOOZE_CLICKED, {
            metadata: { note_id: noteId },
          });
        },
        resignRestoreClicked: (noteId: string) => {
          trackEvent(EventNames.NOTE_RESIGN_RESTORE_CLICKED, {
            metadata: { note_id: noteId },
          });
        },
        resignShowDismissedToggled: (showDismissed: boolean) => {
          trackEvent(EventNames.NOTE_RESIGN_SHOW_DISMISSED_TOGGLED, {
            metadata: { show_dismissed: String(showDismissed) },
          });
        },
        cosignBulkSelectionChanged: (selectedCount: number) => {
          trackEvent(EventNames.NOTE_COSIGN_BULK_SELECTION_CHANGED, {
            value: selectedCount,
            metadata: { selected_count: String(selectedCount) },
          });
        },
        cosignBulkSubmitted: (selectedCount: number) => {
          trackEvent(EventNames.NOTE_COSIGN_BULK_SUBMITTED, {
            value: selectedCount,
            metadata: { selected_count: String(selectedCount) },
          });
        },
        cosignBulkCompleted: (signedCount: number, failedCount: number) => {
          trackEvent(EventNames.NOTE_COSIGN_BULK_COMPLETED, {
            value: signedCount,
            metadata: {
              signed_count: String(signedCount),
              failed_count: String(failedCount),
            },
          });
        },
        startGeneration: (
          metadata?: Record<string, unknown> & {
            template_type?: string;
            source?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_AI_GENERATION_STARTED, { metadata });
        },
        completeGeneration: (
          metadata?: Record<string, unknown> & {
            word_count?: number;
            section_count?: number;
            note_id?: string;
            total_duration_ms?: string;
            sections_generated?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_AI_GENERATION_COMPLETED, {
            metadata: {
              ...metadata,
            },
          });
        },
        edit: (
          noteId: string,
          metadata?: {
            edit_type?: string;
            changes_count?: number;
            edit_method?: string;
            title_length_before?: number;
            title_length_after?: number;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_EDITED, {
            metadata: { note_id: noteId, ...metadata },
          });
        },
        sign: (
          noteId: string,
          metadata?: Record<string, unknown> & {
            time_to_sign?: number;
            edits_made?: string;
            time_to_sign_ms?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_SIGNED, {
            value: metadata?.time_to_sign,
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        futureDosDialogOpened: (
          noteId: string,
          metadata?: {
            encounter_id?: string;
            days_in_future?: number;
            action?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_FUTURE_DOS_DIALOG_OPENED, {
            metadata: { note_id: noteId, ...metadata },
          });
        },
        futureDosUpdateDateClicked: (
          noteId: string,
          metadata?: {
            encounter_id?: string;
            days_in_future?: number;
            action?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_FUTURE_DOS_UPDATE_DATE_CLICKED, {
            metadata: { note_id: noteId, ...metadata },
          });
        },
        futureDosProceedAnywayClicked: (
          noteId: string,
          metadata?: {
            encounter_id?: string;
            days_in_future?: number;
            action?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_FUTURE_DOS_PROCEED_ANYWAY_CLICKED, {
            metadata: { note_id: noteId, ...metadata },
          });
        },
        export: (
          noteId: string,
          metadata?: Record<string, unknown> & {
            export_type?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_EXPORTED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        copied: (
          noteId: string,
          metadata?: {
            copy_type?: 'all' | 'selected';
            sections_count?: number;
            sections_copied?: number;
            include_header?: boolean;
            include_html?: boolean;
            source?: 'button' | 'keyboard';
          },
        ) => {
          trackEvent(EventNames.NOTE_COPIED, {
            metadata: {
              note_id: noteId,
              copy_type: metadata?.copy_type,
              sections_count: metadata?.sections_count
                ? String(metadata.sections_count)
                : undefined,
              sections_copied: metadata?.sections_copied
                ? String(metadata.sections_copied)
                : undefined,
              include_header: metadata?.include_header ? 'true' : 'false',
              include_html: metadata?.include_html ? 'true' : 'false',
              source: metadata?.source,
            },
          });
        },
        viewed: (noteId: string, metadata?: { source?: string }) => {
          trackEvent(EventNames.NOTE_VIEWED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        emailed: (noteId: string, metadata?: { action?: string }) => {
          trackEvent(EventNames.NOTE_EMAILED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        templateEditClicked: (
          noteId: string,
          metadata?: {
            template_id?: string;
            template_name?: string;
            source?: string;
            section_count?: number;
          },
        ) => {
          trackEvent(EventNames.NOTE_TEMPLATE_EDIT_CLICKED, {
            metadata: {
              note_id: noteId,
              ...(metadata?.section_count !== undefined && {
                section_count: String(metadata.section_count),
              }),
              ...metadata,
            },
          });
        },
        // Note view interaction events
        viewTranscriptClicked: (
          noteId: string,
          metadata?: {
            previous_view?: string;
            time_in_previous_view_ms?: number;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_VIEW_TRANSCRIPT_CLICKED, {
            metadata: {
              note_id: noteId,
              ...(metadata?.time_in_previous_view_ms !== undefined && {
                time_in_previous_view_ms: String(metadata.time_in_previous_view_ms),
              }),
              ...metadata,
            },
          });
        },
        viewNoteClicked: (
          noteId: string,
          metadata?: {
            previous_view?: string;
            time_in_previous_view_ms?: number;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_VIEW_NOTE_CLICKED, {
            metadata: {
              note_id: noteId,
              ...(metadata?.time_in_previous_view_ms !== undefined && {
                time_in_previous_view_ms: String(metadata.time_in_previous_view_ms),
              }),
              ...metadata,
            },
          });
        },
        transcriptTextCopied: (
          noteId: string,
          metadata: {
            text_length: number;
            text_percent: number;
            copy_method?: 'keyboard' | 'context_menu';
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TRANSCRIPT_TEXT_COPIED, {
            metadata: {
              note_id: noteId,
              text_length: String(metadata.text_length),
              text_percent: String(metadata.text_percent),
              ...(metadata.copy_method && { copy_method: metadata.copy_method }),
              ...(metadata.encounter_id && { encounter_id: metadata.encounter_id }),
            },
          });
        },
        findReplaceOpened: (metadata?: {
          note_id?: string;
          source?: string;
          prefilled_term?: boolean;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_FIND_REPLACE_OPENED, {
            metadata: {
              ...(metadata?.prefilled_term !== undefined && {
                prefilled_term: String(metadata.prefilled_term),
              }),
              ...metadata,
            },
          });
        },
        findReplaceReplaceClicked: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          match_count?: number;
          case_sensitive?: boolean;
        }) => {
          trackEvent(EventNames.NOTE_FIND_REPLACE_REPLACE_CLICKED, {
            metadata: {
              ...(metadata?.match_count !== undefined && {
                match_count: String(metadata.match_count),
              }),
              ...(metadata?.case_sensitive !== undefined && {
                case_sensitive: String(metadata.case_sensitive),
              }),
              ...metadata,
            },
          });
        },
        findReplaceCancelled: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          had_preview?: boolean;
        }) => {
          trackEvent(EventNames.NOTE_FIND_REPLACE_CANCELLED, {
            metadata: {
              ...(metadata?.had_preview !== undefined && {
                had_preview: String(metadata.had_preview),
              }),
              ...metadata,
            },
          });
        },
        // Template switching funnel events
        templateSwitchClicked: (
          noteId: string,
          metadata?: {
            current_template_id?: string;
            current_template_name?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TEMPLATE_SWITCH_CLICKED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        templateSwitchModalOpened: (
          noteId: string,
          metadata?: {
            current_template_id?: string;
            current_template_name?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TEMPLATE_SWITCH_MODAL_OPENED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        templateSelected: (metadata?: {
          note_id?: string;
          current_template_id?: string;
          new_template_id?: string;
          new_template_name?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_TEMPLATE_SELECTED, { metadata });
        },
        templateRegenerateClicked: (
          noteId: string,
          metadata?: {
            current_template_id?: string;
            new_template_id?: string;
            new_template_name?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TEMPLATE_REGENERATE_CLICKED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        templateDropdownOpened: (
          noteId: string,
          metadata?: {
            current_template_id?: string;
            current_template_name?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TEMPLATE_DROPDOWN_OPENED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        templateRegenerateConfirmed: (
          noteId: string,
          metadata?: {
            current_template_id?: string;
            new_template_id?: string;
            new_template_name?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TEMPLATE_REGENERATE_CONFIRMED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        templateRegenerateCancelled: (
          noteId: string,
          metadata?: {
            current_template_id?: string;
            new_template_id?: string;
            new_template_name?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TEMPLATE_REGENERATE_CANCELLED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        // Signing flow events (Phase 3)
        reviewSignClicked: (
          noteId: string,
          metadata?: {
            sections_count?: number;
            has_patient?: boolean;
            has_note_signing?: boolean;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_REVIEW_SIGN_CLICKED, {
            metadata: {
              note_id: noteId,
              ...(metadata?.sections_count !== undefined && {
                sections_count: String(metadata.sections_count),
              }),
              ...metadata,
            },
          });
        },
        previewEntered: (
          noteId: string,
          metadata?: {
            has_patient?: boolean;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_PREVIEW_ENTERED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        completeClicked: (
          noteId: string,
          metadata?: {
            time_in_preview_ms?: number;
            has_patient?: boolean;
            source?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_COMPLETE_CLICKED, {
            metadata: {
              note_id: noteId,
              ...(metadata?.time_in_preview_ms !== undefined && {
                time_in_preview_ms: String(metadata.time_in_preview_ms),
              }),
              ...metadata,
            },
          });
        },
        amendClicked: (
          noteId: string,
          metadata?: {
            was_signed?: boolean;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_AMEND_CLICKED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        backToEditClicked: (
          noteId: string,
          metadata?: {
            time_in_preview_ms?: number;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_BACK_TO_EDIT_CLICKED, {
            metadata: {
              note_id: noteId,
              ...(metadata?.time_in_preview_ms !== undefined && {
                time_in_preview_ms: String(metadata.time_in_preview_ms),
              }),
              ...metadata,
            },
          });
        },
        completionFailed: (
          noteId: string,
          metadata?: {
            error_message?: string;
            /** Backend's stable error_code (e.g. "pdf_render_failed") — never the message, which names note sections. */
            error_code?: string;
            action_type?: 'sign' | 'complete' | 'amend';
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_COMPLETION_FAILED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        // Navigation/tab events (Phase 4)
        tabsTranscriptClicked: (
          noteId: string,
          metadata?: {
            previous_tab?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TABS_TRANSCRIPT_CLICKED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        tabsNoteClicked: (
          noteId: string,
          metadata?: {
            previous_tab?: string;
            encounter_id?: string;
            note_id?: string;
            source?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TABS_NOTE_CLICKED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        tabsJotdocClicked: (
          jotdocId: string,
          metadata?: {
            note_id?: string;
            encounter_id?: string;
            source?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TABS_JOTDOC_CLICKED, {
            metadata: {
              jotdoc_id: jotdocId,
              ...metadata,
            },
          });
        },
        // Approval banner events (Phase 6)
        approvalBannerShown: (
          noteId: string,
          metadata?: {
            affected_sections_count?: number;
            message?: string;
            note_id?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_APPROVAL_BANNER_SHOWN, {
            metadata: {
              note_id: noteId,
              ...(metadata?.affected_sections_count !== undefined && {
                affected_sections_count: String(metadata.affected_sections_count),
              }),
              ...metadata,
            },
          });
        },
        approvalNavigationClicked: (
          noteId: string,
          metadata?: {
            direction?: 'next' | 'previous';
            current_section_index?: number;
            total_sections?: number;
            note_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_APPROVAL_NAVIGATION_CLICKED, {
            metadata: {
              note_id: noteId,
              ...(metadata?.current_section_index !== undefined && {
                current_section_index: String(metadata.current_section_index),
              }),
              ...(metadata?.total_sections !== undefined && {
                total_sections: String(metadata.total_sections),
              }),
              ...metadata,
            },
          });
        },
        // Chat interface events (Phase 2)
        chatMessageSent: (
          noteId: string,
          metadata?: {
            message_length?: number;
            message_type?: 'text' | 'command';
            note_id?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_CHAT_MESSAGE_SENT, {
            metadata: {
              note_id: noteId,
              ...(metadata?.message_length !== undefined && {
                message_length: String(metadata.message_length),
              }),
              ...metadata,
            },
          });
        },
        chatResponseReceived: (
          noteId: string,
          metadata?: {
            response_length?: number;
            response_time_ms?: number;
            note_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_CHAT_RESPONSE_RECEIVED, {
            metadata: {
              note_id: noteId,
              ...(metadata?.response_length !== undefined && {
                response_length: String(metadata.response_length),
              }),
              ...(metadata?.response_time_ms !== undefined && {
                response_time_ms: String(metadata.response_time_ms),
              }),
              ...metadata,
            },
          });
        },
        chatSuggestedActionClicked: (
          noteId: string,
          metadata?: {
            action_type?: string;
            action_id?: string;
            action_label?: string;
            note_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_CHAT_SUGGESTED_ACTION_CLICKED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        chatInputFocused: (
          noteId: string,
          metadata?: {
            note_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_CHAT_INPUT_FOCUSED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        chatErrorOccurred: (
          noteId: string,
          metadata?: {
            error_type?: string;
            error_message?: string;
            note_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_CHAT_ERROR_OCCURRED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        chatSessionStarted: (
          noteId: string,
          metadata?: {
            note_id?: string;
            encounter_id?: string;
            initial_message_count?: number;
          },
        ) => {
          trackEvent(EventNames.NOTE_CHAT_SESSION_STARTED, {
            metadata: {
              note_id: noteId,
              ...(metadata?.initial_message_count !== undefined && {
                initial_message_count: String(metadata.initial_message_count),
              }),
              ...metadata,
            },
          });
        },
        chatThreadCleared: (
          noteId: string,
          metadata?: {
            note_id?: string;
            encounter_id?: string;
            message_count?: number;
          },
        ) => {
          trackEvent(EventNames.NOTE_CHAT_THREAD_CLEARED, {
            metadata: {
              note_id: noteId,
              ...(metadata?.message_count !== undefined && {
                message_count: String(metadata.message_count),
              }),
              ...metadata,
            },
          });
        },
        chatSSEPerformance: (
          noteId: string,
          metadata: {
            time_to_connection_ms: number;
            time_to_first_event_ms: number;
            time_to_first_token_ms: number;
            total_duration_ms: number;
            run_id: string;
            thread_id?: string;
            outcome: 'success' | 'interrupt' | 'error';
            event_count: number;
            exceeded_threshold: boolean;
            threshold_ms?: number;
          },
        ) => {
          trackEvent(EventNames.NOTE_CHAT_SSE_PERFORMANCE, {
            value: Math.round(metadata.total_duration_ms),
            metadata: {
              note_id: noteId,
              time_to_connection_ms: String(Math.round(metadata.time_to_connection_ms)),
              time_to_first_event_ms: String(Math.round(metadata.time_to_first_event_ms)),
              time_to_first_token_ms: String(Math.round(metadata.time_to_first_token_ms)),
              total_duration_ms: String(Math.round(metadata.total_duration_ms)),
              run_id: metadata.run_id,
              ...(metadata.thread_id && { thread_id: metadata.thread_id }),
              outcome: metadata.outcome,
              event_count: String(metadata.event_count),
              exceeded_threshold: metadata.exceeded_threshold ? 'true' : 'false',
              ...(metadata.threshold_ms !== undefined && {
                threshold_ms: String(Math.round(metadata.threshold_ms)),
              }),
            },
          });
        },
        // AI Chat Onboarding/Discovery events
        aiChatOnboardingShown: (
          noteId: string,
          metadata?: {
            note_id?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_AI_CHAT_ONBOARDING_SHOWN, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        aiChatOnboardingDismissed: (
          noteId: string,
          metadata?: {
            note_id?: string;
            encounter_id?: string;
            dismiss_method?: 'manual' | 'auto';
          },
        ) => {
          trackEvent(EventNames.NOTE_AI_CHAT_ONBOARDING_DISMISSED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        // NoteTopBar consolidated menu actions
        topbarMenuOpened: (
          noteId: string,
          metadata?: {
            view_mode?: 'edit' | 'preview' | 'completed' | 'transcript';
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TOPBAR_MENU_OPENED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        topbarDeleteClicked: (
          noteId: string,
          metadata?: {
            view_mode?: 'edit' | 'preview' | 'completed' | 'transcript';
            is_signed?: boolean;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TOPBAR_DELETE_CLICKED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        topbarCopyPasteSettingsClicked: (
          noteId: string,
          metadata?: {
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TOPBAR_COPY_PASTE_SETTINGS_CLICKED, {
            metadata: {
              note_id: noteId,
              view_mode: 'edit',
              ...metadata,
            },
          });
        },
        topbarFindReplaceClicked: (
          noteId: string,
          metadata?: {
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TOPBAR_FIND_REPLACE_CLICKED, {
            metadata: {
              note_id: noteId,
              view_mode: 'edit',
              ...metadata,
            },
          });
        },
        topbarChatToggleClicked: (
          noteId: string,
          metadata?: {
            chat_visible?: boolean;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TOPBAR_CHAT_TOGGLE_CLICKED, {
            metadata: {
              note_id: noteId,
              view_mode: 'edit',
              ...metadata,
            },
          });
        },
        topbarDownloadSignedPdfClicked: (
          noteId: string,
          metadata?: {
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TOPBAR_DOWNLOAD_SIGNED_PDF_CLICKED, {
            metadata: {
              note_id: noteId,
              view_mode: 'completed',
              ...metadata,
            },
          });
        },

        // Patient assignment tracking
        patientAssignmentClicked: (
          noteId: string,
          metadata?: {
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_PATIENT_ASSIGNMENT_CLICKED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        patientAssignmentDialogOpened: (
          noteId: string,
          metadata?: {
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_PATIENT_ASSIGNMENT_DIALOG_OPENED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        patientAssigned: (
          noteId: string,
          metadata?: {
            patient_id?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_PATIENT_ASSIGNED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        patientUnassignClicked: (
          noteId: string,
          metadata?: {
            patient_id?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_PATIENT_UNASSIGN_CLICKED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        patientUnassigned: (
          noteId: string,
          metadata?: {
            patient_id?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_PATIENT_UNASSIGNED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        patientNameClicked: (
          noteId: string,
          metadata?: {
            patient_id?: string;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_PATIENT_NAME_CLICKED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        patientAssignmentCancelled: (
          noteId: string,
          metadata?: {
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_PATIENT_ASSIGNMENT_CANCELLED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },

        payerChipClicked: (
          noteId: string,
          metadata?: {
            encounter_id?: string;
            patient_id?: string;
            has_insurance?: boolean;
            payer_name?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_PAYER_CHIP_CLICKED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },

        // Title editing tracking
        titleEditStarted: (
          noteId: string,
          metadata?: {
            title_length?: number;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TITLE_EDIT_STARTED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },
        titleEditCancelled: (
          noteId: string,
          metadata?: {
            title_length_before?: number;
            edit_duration_ms?: number;
            encounter_id?: string;
          },
        ) => {
          trackEvent(EventNames.NOTE_TITLE_EDIT_CANCELLED, {
            metadata: {
              note_id: noteId,
              ...metadata,
            },
          });
        },

        // Encounter time editing tracking
        encounterTimeEditorOpened: (metadata?: { note_id?: string; encounter_id?: string }) => {
          trackEvent(EventNames.NOTE_ENCOUNTER_TIME_EDITOR_OPENED, {
            metadata,
          });
        },
        encounterTimeSaved: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          duration_minutes?: number;
          is_custom_duration?: boolean;
        }) => {
          trackEvent(EventNames.NOTE_ENCOUNTER_TIME_SAVED, {
            metadata,
          });
        },

        // Encounter CPT codes editing tracking
        encounterCptEditorOpened: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          has_existing_codes?: boolean;
        }) => {
          trackEvent(EventNames.NOTE_ENCOUNTER_CPT_EDITOR_OPENED, {
            metadata,
          });
        },
        encounterCptCodesSaved: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          cpt_codes?: string[];
          cpt_codes_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_ENCOUNTER_CPT_CODES_SAVED, {
            metadata,
          });
        },

        // Encounter ICD-10 codes editing tracking
        encounterIcd10EditorOpened: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          has_existing_codes?: boolean;
        }) => {
          trackEvent(EventNames.NOTE_ENCOUNTER_ICD10_EDITOR_OPENED, {
            metadata,
          });
        },
        encounterIcd10CodesSaved: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          icd10_codes?: string[];
          icd10_codes_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_ENCOUNTER_ICD10_CODES_SAVED, {
            metadata,
          });
        },
      },

      // Note section metrics
      noteSection: {
        editSaved: (metadata?: {
          section_id?: string;
          section_type?: string;
          note_id?: string;
          has_changes?: boolean;
          edit_source?: 'manual' | 'ai_edit' | 'regeneration';
        }) => {
          trackEvent(EventNames.NOTE_SECTION_EDIT_SAVED, {
            metadata: {
              ...(metadata?.has_changes !== undefined && {
                has_changes: String(metadata.has_changes),
              }),
              ...metadata,
            },
          });
        },
        editCancelled: (metadata?: {
          section_id?: string;
          section_type?: string;
          note_id?: string;
          had_changes?: boolean;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_EDIT_CANCELLED, {
            metadata: {
              ...(metadata?.had_changes !== undefined && {
                had_changes: String(metadata.had_changes),
              }),
              ...metadata,
            },
          });
        },
        regenerated: (metadata?: {
          section_id?: string;
          section_type?: string;
          note_id?: string;
          regeneration_reason?: string;
          has_custom_context?: boolean;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_REGENERATED, {
            metadata: {
              ...(metadata?.has_custom_context !== undefined && {
                has_custom_context: String(metadata.has_custom_context),
              }),
              ...metadata,
            },
          });
        },
        aiEditRequested: (metadata?: {
          section_id?: string;
          section_type?: string;
          note_id?: string;
          instruction_type?: string;
          instruction_length?: number;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_AI_EDIT_REQUESTED, {
            metadata: {
              ...(metadata?.instruction_length !== undefined && {
                instruction_length: String(metadata.instruction_length),
              }),
              ...metadata,
            },
          });
        },
        reverted: (metadata?: {
          section_id?: string;
          section_type?: string;
          note_id?: string;
          revert_reason?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_REVERTED, { metadata });
        },
        copied: (metadata?: {
          section_id?: string;
          section_type?: string;
          note_id?: string;
          copy_method?: 'button' | 'keyboard' | 'menu';
          include_formatting?: boolean;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_COPIED, {
            metadata: {
              ...(metadata?.include_formatting !== undefined && {
                include_formatting: String(metadata.include_formatting),
              }),
              ...metadata,
            },
          });
        },
        attestationViewed: (metadata?: {
          section_id?: string;
          note_id?: string;
          encounter_id?: string;
          rule_id?: string;
          title?: string;
          source?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_ATTESTATION_VIEWED, { metadata });
        },
        attestationAccepted: (metadata?: {
          section_id?: string;
          note_id?: string;
          encounter_id?: string;
          rule_id?: string;
          title?: string;
          source?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_ATTESTATION_ACCEPTED, { metadata });
        },
        attestationRejected: (metadata?: {
          section_id?: string;
          note_id?: string;
          encounter_id?: string;
          rule_id?: string;
          title?: string;
          source?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_ATTESTATION_REJECTED, { metadata });
        },
        added: (metadata?: {
          section_id?: string;
          section_type?: string;
          note_id?: string;
          add_method?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_ADDED, { metadata });
        },
        removed: (metadata?: {
          section_id?: string;
          section_type?: string;
          note_id?: string;
          remove_reason?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_REMOVED, { metadata });
        },
        reordered: (metadata?: {
          note_id?: string;
          section_count?: number;
          from_index?: number;
          to_index?: number;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_REORDERED, {
            metadata: {
              ...(metadata?.section_count !== undefined && {
                section_count: String(metadata.section_count),
              }),
              ...(metadata?.from_index !== undefined && {
                from_index: String(metadata.from_index),
              }),
              ...(metadata?.to_index !== undefined && {
                to_index: String(metadata.to_index),
              }),
              ...metadata,
            },
          });
        },
        addSectionClicked: (metadata?: {
          note_id?: string;
          insertion_index?: number;
          section_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_TEMPLATE_ADD_SECTION_CLICKED, {
            metadata: {
              ...(metadata?.insertion_index !== undefined && {
                insertion_index: String(metadata.insertion_index),
              }),
              ...(metadata?.section_count !== undefined && {
                section_count: String(metadata.section_count),
              }),
              ...metadata,
            },
          });
        },
        sectionDragStarted: (metadata?: {
          note_id?: string;
          section_id?: string;
          section_index?: number;
          section_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_TEMPLATE_SECTION_DRAG_STARTED, {
            metadata: {
              ...(metadata?.section_index !== undefined && {
                section_index: String(metadata.section_index),
              }),
              ...(metadata?.section_count !== undefined && {
                section_count: String(metadata.section_count),
              }),
              ...metadata,
            },
          });
        },
        removeSectionClicked: (metadata?: {
          note_id?: string;
          section_id?: string;
          section_type?: string;
          section_index?: number;
        }) => {
          trackEvent(EventNames.NOTE_TEMPLATE_REMOVE_SECTION_CLICKED, {
            metadata: {
              ...(metadata?.section_index !== undefined && {
                section_index: String(metadata.section_index),
              }),
              ...metadata,
            },
          });
        },
        // Section editing interaction events
        editClicked: (metadata?: {
          section_id?: string;
          section_type?: string;
          section_name?: string;
          source?: string;
          note_id?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_EDIT_CLICKED, { metadata });
        },
        editDoneClicked: (metadata?: {
          section_id?: string;
          section_type?: string;
          section_name?: string;
          had_changes?: boolean;
          edit_duration_ms?: number;
          note_id?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_EDIT_DONE_CLICKED, {
            metadata: {
              ...(metadata?.had_changes !== undefined && {
                had_changes: String(metadata.had_changes),
              }),
              ...(metadata?.edit_duration_ms !== undefined && {
                edit_duration_ms: String(metadata.edit_duration_ms),
              }),
              ...metadata,
            },
          });
        },
        aiEditAcceptClicked: (metadata?: {
          section_id?: string;
          section_type?: string;
          section_name?: string;
          edit_mode?: string;
          note_id?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_AI_EDIT_ACCEPT_CLICKED, { metadata });
        },
        aiEditRejectClicked: (metadata?: {
          section_id?: string;
          section_type?: string;
          section_name?: string;
          edit_mode?: string;
          note_id?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_AI_EDIT_REJECT_CLICKED, { metadata });
        },
        revertClicked: (metadata?: {
          section_id?: string;
          section_type?: string;
          section_name?: string;
          note_id?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_REVERT_CLICKED, { metadata });
        },
        copyClicked: (metadata?: {
          section_id?: string;
          section_type?: string;
          section_name?: string;
          note_id?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_COPY_CLICKED, { metadata });
        },
        selectorClicked: (metadata?: {
          section_id?: string;
          section_name?: string;
          action?: 'select' | 'deselect';
          source?: 'section' | 'toc';
          note_id?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_SELECTOR_CLICKED, { metadata });
        },
        deselectAllClicked: (metadata?: {
          source?: 'toc' | 'topbar';
          selected_count?: number;
          note_id?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_DESELECT_ALL_CLICKED, { metadata });
        },
        // AI interaction events
        aiModeSelected: (metadata?: {
          section_id?: string;
          section_name?: string;
          mode?: string;
          previous_mode?: string;
          note_id?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_AI_MODE_SELECTED, { metadata });
        },
        aiInstructionSubmitted: (metadata?: {
          section_id?: string;
          section_name?: string;
          mode?: string;
          instruction_length?: number;
          used_voice?: boolean;
          note_id?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_AI_INSTRUCTION_SUBMITTED, {
            metadata: {
              ...(metadata?.instruction_length !== undefined && {
                instruction_length: String(metadata.instruction_length),
              }),
              ...(metadata?.used_voice !== undefined && {
                used_voice: String(metadata.used_voice),
              }),
              ...metadata,
            },
          });
        },
        aiInstructionRendered: (metadata?: {
          section_id?: string;
          section_name?: string;
          mode?: string;
          result_length?: number;
          has_diffs?: boolean;
          note_id?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_AI_INSTRUCTION_RENDERED, {
            metadata: {
              ...(metadata?.result_length !== undefined && {
                result_length: String(metadata.result_length),
              }),
              ...(metadata?.has_diffs !== undefined && {
                has_diffs: String(metadata.has_diffs),
              }),
              ...metadata,
            },
          });
        },
      },

      // Note audit sidebar metrics (in-note JotAudit feature)
      noteAudit: {
        /**
         * Track when user clicks the Audit tab in sidebar toggle
         */
        tabClicked: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          previous_mode?: 'chat' | 'audit';
          issue_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_TAB_CLICKED, {
            metadata: {
              ...(metadata?.issue_count !== undefined && {
                issue_count: String(metadata.issue_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when audit sidebar is displayed to user
         */
        sidebarViewed: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          issue_count?: number;
          non_compliant_count?: number;
          warning_count?: number;
          compliant_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_SIDEBAR_VIEWED, {
            metadata: {
              ...(metadata?.issue_count !== undefined && {
                issue_count: String(metadata.issue_count),
              }),
              ...(metadata?.non_compliant_count !== undefined && {
                non_compliant_count: String(metadata.non_compliant_count),
              }),
              ...(metadata?.warning_count !== undefined && {
                warning_count: String(metadata.warning_count),
              }),
              ...(metadata?.compliant_count !== undefined && {
                compliant_count: String(metadata.compliant_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks red audit badge on section header
         */
        sectionBadgeClicked: (metadata?: {
          note_id?: string;
          section_id?: string;
          section_name?: string;
          encounter_id?: string;
          issue_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_SECTION_AUDIT_BADGE_CLICKED, {
            metadata: {
              ...(metadata?.issue_count !== undefined && {
                issue_count: String(metadata.issue_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks a compliance rule to scroll to section
         */
        ruleClicked: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          rule_id?: string;
          compliance_status?: 'yes' | 'no' | 'warning';
          has_relevant_sections?: boolean;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RULE_CLICKED, {
            metadata: {
              ...(metadata?.has_relevant_sections !== undefined && {
                has_relevant_sections: String(metadata.has_relevant_sections),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks "Fix with AI" on a rule
         */
        ruleFixClicked: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          rule_id?: string;
          compliance_status?: 'yes' | 'no' | 'warning';
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RULE_FIX_CLICKED, { metadata });
        },
        /**
         * Track when user hovers to view rule details popover
         */
        ruleInfoViewed: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          rule_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RULE_INFO_VIEWED, { metadata });
        },
        /**
         * Track when user expands/collapses a compliance section accordion
         */
        sectionToggled: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          section_type?: 'non_compliant' | 'warning' | 'compliant';
          expanded?: boolean;
          item_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_SECTION_TOGGLED, {
            metadata: {
              ...(metadata?.expanded !== undefined && {
                expanded: String(metadata.expanded),
              }),
              ...(metadata?.item_count !== undefined && {
                item_count: String(metadata.item_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks Re-analyze button
         */
        reanalyzeClicked: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          current_issue_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_REANALYZE_CLICKED, {
            metadata: {
              ...(metadata?.current_issue_count !== undefined && {
                current_issue_count: String(metadata.current_issue_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when reaudit workflow completes
         */
        reanalyzeCompleted: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          status?: 'completed' | 'error';
          new_issue_count?: number;
          duration_ms?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_REANALYZE_COMPLETED, {
            metadata: {
              ...(metadata?.new_issue_count !== undefined && {
                new_issue_count: String(metadata.new_issue_count),
              }),
              ...(metadata?.duration_ms !== undefined && {
                duration_ms: String(metadata.duration_ms),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when CPT analysis section is viewed
         */
        cptViewed: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          cpt_match?: boolean;
          provider_codes_count?: number;
          recommended_codes_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_CPT_VIEWED, {
            metadata: {
              ...(metadata?.cpt_match !== undefined && {
                cpt_match: String(metadata.cpt_match),
              }),
              ...(metadata?.provider_codes_count !== undefined && {
                provider_codes_count: String(metadata.provider_codes_count),
              }),
              ...(metadata?.recommended_codes_count !== undefined && {
                recommended_codes_count: String(metadata.recommended_codes_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user applies AI-suggested CPT codes
         */
        suggestedCodesApplied: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          codes_applied?: string[];
          source?: 'audit_sidebar' | 'metadata_bar';
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_SUGGESTED_CODES_APPLIED, {
            metadata: {
              ...(metadata?.codes_applied && {
                codes_applied: metadata.codes_applied.join(','),
                codes_count: String(metadata.codes_applied.length),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when audit sidebar is opened
         */
        sidebarOpened: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          source?: 'tab_toggle' | 'snackbar' | 'section_badge';
          issue_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_SIDEBAR_OPENED, {
            metadata: {
              ...(metadata?.issue_count !== undefined && {
                issue_count: String(metadata.issue_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when audit sidebar is closed
         */
        sidebarClosed: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          duration_ms?: number;
          dismissed_rules_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_SIDEBAR_CLOSED, {
            metadata: {
              ...(metadata?.duration_ms !== undefined && {
                duration_ms: String(metadata.duration_ms),
              }),
              ...(metadata?.dismissed_rules_count !== undefined && {
                dismissed_rules_count: String(metadata.dismissed_rules_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user switches between At Risk and Compliant tabs
         */
        tabSwitched: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          previous_tab?: 'at-risk' | 'compliant';
          new_tab?: 'at-risk' | 'compliant';
          at_risk_count?: number;
          compliant_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_TAB_SWITCHED, {
            metadata: {
              ...(metadata?.at_risk_count !== undefined && {
                at_risk_count: String(metadata.at_risk_count),
              }),
              ...(metadata?.compliant_count !== undefined && {
                compliant_count: String(metadata.compliant_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks eye icon to jump to section in note
         */
        ruleJumpToSectionClicked: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          rule_id?: string;
          section_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RULE_JUMP_TO_SECTION_CLICKED, { metadata });
        },
        /**
         * Track when user marks a rule as done (dismisses it)
         */
        ruleDismissed: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          rule_id?: string;
          compliance_status?: 'no' | 'warning';
          dismissed_count?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RULE_DISMISSED, {
            metadata: {
              ...(metadata?.dismissed_count !== undefined && {
                dismissed_count: String(metadata.dismissed_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks Analytics button in rule modal
         */
        ruleAnalyticsClicked: (metadata?: {
          note_id?: string;
          rule_id?: string;
          source?: 'audit_sidebar';
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RULE_ANALYTICS_CLICKED, { metadata });
        },
        /**
         * Track when user clicks Edit button in rule modal
         */
        ruleEditClicked: (metadata?: {
          note_id?: string;
          rule_id?: string;
          source?: 'audit_sidebar';
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RULE_EDIT_CLICKED, { metadata });
        },
        /**
         * Track when user disables a rule from modal
         */
        ruleDisabled: (metadata?: {
          note_id?: string;
          rule_id?: string;
          source?: 'audit_sidebar';
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RULE_DISABLED, { metadata });
        },
        /**
         * Track when user enables a rule from modal
         */
        ruleEnabled: (metadata?: {
          note_id?: string;
          rule_id?: string;
          source?: 'audit_sidebar';
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RULE_ENABLED, { metadata });
        },
        /**
         * Track when user clicks Run Audit button (alias for reanalyzeClicked)
         * @param metadata.source - What triggered the audit run:
         *   - 'manual': User clicked the Run Audit button
         *   - 'cpt_code_change': Automatic reaudit triggered by CPT code change
         *   - 'suggested_codes_applied': User applied suggested CPT codes
         *   - 'suggestion_timeout_retry': User retried after the billing-code
         *     suggestion window lapsed with nothing to show (gdn-2614)
         */
        runClicked: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          current_issue_count?: number;
          source?:
            | 'manual'
            | 'cpt_code_change'
            | 'suggested_codes_applied'
            | 'suggestion_timeout_retry';
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RUN_CLICKED, {
            metadata: {
              ...(metadata?.current_issue_count !== undefined && {
                current_issue_count: String(metadata.current_issue_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when audit run completes
         */
        runCompleted: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          status?: 'success' | 'error';
          duration_ms?: number;
          previous_issue_count?: number;
          new_issue_count?: number;
          issues_resolved?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RUN_COMPLETED, {
            metadata: {
              ...(metadata?.duration_ms !== undefined && {
                duration_ms: String(metadata.duration_ms),
              }),
              ...(metadata?.previous_issue_count !== undefined && {
                previous_issue_count: String(metadata.previous_issue_count),
              }),
              ...(metadata?.new_issue_count !== undefined && {
                new_issue_count: String(metadata.new_issue_count),
              }),
              ...(metadata?.issues_resolved !== undefined && {
                issues_resolved: String(metadata.issues_resolved),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when CPT section is displayed in the audit sidebar
         */
        cptDisplayed: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          target_cpt_source?: 'user' | 'ai';
          target_codes_count?: number;
          cpt_match?: boolean;
          has_different_suggestion?: boolean;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_CPT_DISPLAYED, {
            metadata: {
              ...(metadata?.target_codes_count !== undefined && {
                target_codes_count: String(metadata.target_codes_count),
              }),
              ...(metadata?.cpt_match !== undefined && {
                cpt_match: String(metadata.cpt_match),
              }),
              ...(metadata?.has_different_suggestion !== undefined && {
                has_different_suggestion: String(metadata.has_different_suggestion),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user approves AI-suggested codes (checkmark button)
         */
        cptApproved: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          codes_approved?: string[];
          target_cpt_source?: 'user' | 'ai';
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_CPT_APPROVED, {
            metadata: {
              ...(metadata?.codes_approved && {
                codes_approved: metadata.codes_approved.join(','),
                codes_count: String(metadata.codes_approved.length),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user opens the CPT picker from the audit sidebar
         */
        cptPickerOpened: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          trigger?: 'chip_click' | 'edit_button';
          target_cpt_source?: 'user' | 'ai';
          current_codes?: string[];
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_CPT_PICKER_OPENED, {
            metadata: {
              ...(metadata?.current_codes && {
                current_codes: metadata.current_codes.join(','),
                current_codes_count: String(metadata.current_codes.length),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user changes CPT codes via the picker
         */
        cptChanged: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          previous_codes?: string[];
          new_codes?: string[];
          target_cpt_source?: 'user' | 'ai';
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_CPT_CHANGED, {
            metadata: {
              ...(metadata?.previous_codes && {
                previous_codes: metadata.previous_codes.join(','),
                previous_codes_count: String(metadata.previous_codes.length),
              }),
              ...(metadata?.new_codes && {
                new_codes: metadata.new_codes.join(','),
                new_codes_count: String(metadata.new_codes.length),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks "your clinic's rules" link
         */
        rulesLinkClicked: (metadata?: { note_id?: string }) => {
          trackEvent(EventNames.NOTE_AUDIT_RULES_LINK_CLICKED, { metadata });
        },
        /**
         * Track when payer-aware template banner is shown to user
         */
        payerAwareBannerShown: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          current_template_id?: string;
          current_compliance?: number;
          target_template_id?: string;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_PAYER_AWARE_BANNER_SHOWN, {
            metadata: {
              ...(metadata?.current_compliance !== undefined && {
                current_compliance: String(metadata.current_compliance),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user dismisses payer-aware template banner
         */
        payerAwareBannerDismissed: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          current_template_id?: string;
          current_compliance?: number;
          time_visible_ms?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_PAYER_AWARE_BANNER_DISMISSED, {
            metadata: {
              ...(metadata?.current_compliance !== undefined && {
                current_compliance: String(metadata.current_compliance),
              }),
              ...(metadata?.time_visible_ms !== undefined && {
                time_visible_ms: String(metadata.time_visible_ms),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks Regenerate button on payer-aware banner
         */
        payerAwareRegenerateClicked: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          current_template_id?: string;
          target_template_id?: string;
          current_compliance?: number;
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_PAYER_AWARE_REGENERATE_CLICKED, {
            metadata: {
              ...(metadata?.current_compliance !== undefined && {
                current_compliance: String(metadata.current_compliance),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user manually marks a rule as resolved via overflow menu
         */
        ruleMarkedResolved: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          rule_id?: string;
          compliance_status?: 'no' | 'warning';
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RULE_MARKED_RESOLVED, { metadata });
        },
        /**
         * Track when user undoes a completed or ignored card action
         */
        ruleUndone: (metadata?: {
          note_id?: string;
          encounter_id?: string;
          rule_id?: string;
          previous_status?: 'completed' | 'ignored';
          completed_via?: 'ai' | 'manual';
        }) => {
          trackEvent(EventNames.NOTE_AUDIT_RULE_UNDONE, { metadata });
        },
        /**
         * Track when user clicks Learn More button in sidebar
         */
        learnMoreButtonClicked: (metadata?: { note_id?: string; encounter_id?: string }) => {
          trackEvent(EventNames.JOTAUDIT_LEARN_MORE_BUTTON_CLICKED, { metadata });
        },
        /**
         * Track when user closes learn more video modal
         */
        learnMoreVideoClosed: (metadata?: { video_time?: number }) => {
          trackEvent(EventNames.JOTAUDIT_LEARN_MORE_VIDEO_CLOSED, { metadata });
        },
      },

      // JotAudit dashboard metrics (clinic admin audit center)
      jotauditDashboard: {
        /**
         * Track when JotAudit page is viewed
         */
        pageViewed: (metadata?: {
          current_tab?: string;
          provider_count?: number;
          note_count?: number;
          source?: 'direct' | 'nav_menu' | 'note_link' | 'notification';
          time_of_day?: string;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_PAGE_VIEWED, {
            metadata: {
              ...(metadata?.provider_count !== undefined && {
                provider_count: String(metadata.provider_count),
              }),
              ...(metadata?.note_count !== undefined && {
                note_count: String(metadata.note_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user changes tab in dashboard
         */
        tabChanged: (metadata?: { previous_tab?: string; new_tab?: string }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_TAB_CHANGED, { metadata });
        },
        /**
         * Track when user clicks a provider row
         */
        providerClicked: (metadata?: { provider_id?: string; provider_name?: string }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_PROVIDER_CLICKED, { metadata });
        },
        /**
         * Track when provider detail page is viewed
         */
        providerDetailViewed: (metadata?: {
          provider_id?: string;
          total_notes_audited?: number;
          avg_quality_score?: number;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_PROVIDER_DETAIL_VIEWED, {
            metadata: {
              ...(metadata?.total_notes_audited !== undefined && {
                total_notes_audited: String(metadata.total_notes_audited),
              }),
              ...(metadata?.avg_quality_score !== undefined && {
                avg_quality_score: String(metadata.avg_quality_score),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks a note audit
         */
        noteClicked: (metadata?: { note_id?: string; action?: 'view_audit' | 'open_note' }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_NOTE_CLICKED, { metadata });
        },
        /**
         * Track when note audit detail page is viewed
         */
        noteDetailViewed: (metadata?: {
          note_id?: string;
          quality_score?: number;
          compliance_percentage?: number;
          total_flags?: number;
          cpt_match?: boolean;
          revision_count?: number;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_NOTE_DETAIL_VIEWED, {
            metadata: {
              ...(metadata?.quality_score !== undefined && {
                quality_score: String(metadata.quality_score),
              }),
              ...(metadata?.compliance_percentage !== undefined && {
                compliance_percentage: String(metadata.compliance_percentage),
              }),
              ...(metadata?.total_flags !== undefined && {
                total_flags: String(metadata.total_flags),
              }),
              ...(metadata?.revision_count !== undefined && {
                revision_count: String(metadata.revision_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks to open the actual note
         */
        openNoteClicked: (metadata?: { note_id?: string; source?: 'detail_page' | 'list_row' }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_OPEN_NOTE_CLICKED, { metadata });
        },
        /**
         * Track when user changes filter (legacy - use specific filter events)
         */
        filterChanged: (metadata?: { filter_value?: string; previous_filter?: string }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_FILTER_CHANGED, { metadata });
        },

        // Providers Tab Events
        /**
         * Track when user changes date filter on providers list
         */
        providersFilterChanged: (metadata?: {
          filter_type?: 'days' | 'date_range';
          previous_value?: string;
          new_value?: string;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_PROVIDERS_FILTER_CHANGED, { metadata });
        },
        /**
         * Track when filter changed on provider's notes list
         */
        providerNotesFiltered: (metadata?: {
          provider_id?: string;
          filter_type?: 'completion_status' | 'date_range';
          previous_value?: string;
          new_value?: string;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_PROVIDER_NOTES_FILTERED, { metadata });
        },
        /**
         * Track when user pages through provider's notes
         */
        providerNotesPaginated: (metadata?: {
          provider_id?: string;
          page_number?: number;
          direction?: 'next' | 'previous';
          total_pages?: number;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_PROVIDER_NOTES_PAGINATED, {
            metadata: {
              ...(metadata?.page_number !== undefined && {
                page_number: String(metadata.page_number),
              }),
              ...(metadata?.total_pages !== undefined && {
                total_pages: String(metadata.total_pages),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user switches between provider subtabs
         */
        providerSubtabChanged: (metadata?: {
          provider_id?: string;
          previous_tab?: string;
          new_tab?: string;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_PROVIDER_SUBTAB_CHANGED, { metadata });
        },

        // Rules Tab Events
        /**
         * Track when user changes filter on rules view
         */
        rulesFilterChanged: (metadata?: {
          filter_type?: 'days' | 'date_range' | 'clinicians' | 'rules';
          previous_value?: string;
          new_value?: string;
          clinicians_count?: number;
          rules_count?: number;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_RULES_FILTER_CHANGED, {
            metadata: {
              ...(metadata?.clinicians_count !== undefined && {
                clinicians_count: String(metadata.clinicians_count),
              }),
              ...(metadata?.rules_count !== undefined && {
                rules_count: String(metadata.rules_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user toggles comparison mode
         */
        comparisonModeToggled: (metadata?: { enabled?: boolean; clinicians_count?: number }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_COMPARISON_MODE_TOGGLED, {
            metadata: {
              ...(metadata?.clinicians_count !== undefined && {
                clinicians_count: String(metadata.clinicians_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks a rule row in table
         */
        ruleClicked: (metadata?: {
          rule_id?: string;
          rule_name?: string;
          compliance_rate?: number;
          violation_count?: number;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_RULE_CLICKED, {
            metadata: {
              ...(metadata?.compliance_rate !== undefined && {
                compliance_rate: String(metadata.compliance_rate),
              }),
              ...(metadata?.violation_count !== undefined && {
                violation_count: String(metadata.violation_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks Add Rule card
         */
        addRuleClicked: (metadata?: { source?: 'rules_tab' | 'empty_state' }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_ADD_RULE_CLICKED, { metadata });
        },
        /**
         * Track when user clicks Upload button
         */
        uploadRulesClicked: (metadata?: { source?: 'rules_tab' }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_UPLOAD_RULES_CLICKED, { metadata });
        },
        /**
         * Track when user clicks a stat card
         */
        statCardClicked: (metadata?: {
          card_type?: 'most_violated' | 'most_compliant' | 'overview';
          rule_id?: string;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_STAT_CARD_CLICKED, { metadata });
        },

        // Rule Analytics Page Events
        /**
         * Track when rule analytics page loads
         */
        ruleAnalyticsViewed: (metadata?: {
          rule_id?: string;
          rule_name?: string;
          total_audits?: number;
          compliance_rate?: number;
          violation_count?: number;
          is_disabled?: boolean;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_RULE_ANALYTICS_VIEWED, {
            metadata: {
              ...(metadata?.total_audits !== undefined && {
                total_audits: String(metadata.total_audits),
              }),
              ...(metadata?.compliance_rate !== undefined && {
                compliance_rate: String(metadata.compliance_rate),
              }),
              ...(metadata?.violation_count !== undefined && {
                violation_count: String(metadata.violation_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user expands rule content
         */
        ruleContentExpanded: (metadata?: { rule_id?: string; expanded?: boolean }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_RULE_CONTENT_EXPANDED, { metadata });
        },
        /**
         * Track when user clicks View Violations button
         */
        viewViolationsClicked: (metadata?: { rule_id?: string; violation_count?: number }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_VIEW_VIOLATIONS_CLICKED, {
            metadata: {
              ...(metadata?.violation_count !== undefined && {
                violation_count: String(metadata.violation_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks Edit Rule button from analytics
         */
        ruleEditClicked: (metadata?: { rule_id?: string; source?: 'analytics_page' }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_RULE_EDIT_CLICKED, { metadata });
        },
        /**
         * Track when user disables a rule from analytics
         */
        ruleDisabled: (metadata?: { rule_id?: string; source?: 'analytics_page' }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_RULE_DISABLED, { metadata });
        },
        /**
         * Track when user enables a rule from analytics
         */
        ruleEnabled: (metadata?: { rule_id?: string; source?: 'analytics_page' }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_RULE_ENABLED, { metadata });
        },
        /**
         * Track when user clicks provider in breakdown table
         */
        analyticsProviderClicked: (metadata?: {
          rule_id?: string;
          provider_id?: string;
          provider_compliance_rate?: number;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_ANALYTICS_PROVIDER_CLICKED, {
            metadata: {
              ...(metadata?.provider_compliance_rate !== undefined && {
                provider_compliance_rate: String(metadata.provider_compliance_rate),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user changes filter on analytics page
         */
        analyticsFilterChanged: (metadata?: {
          rule_id?: string;
          filter_type?: 'days' | 'date_range' | 'clinicians';
          previous_value?: string;
          new_value?: string;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_ANALYTICS_FILTER_CHANGED, { metadata });
        },

        // Notes Tab Events
        /**
         * Track when user changes any filter on notes list
         */
        notesFilterChanged: (metadata?: {
          filter_type?: 'completion_status' | 'days' | 'date_range' | 'clinicians';
          previous_value?: string;
          new_value?: string;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_NOTES_FILTER_CHANGED, { metadata });
        },
        /**
         * Track when user pages through notes list
         */
        notesPaginated: (metadata?: {
          page_number?: number;
          direction?: 'next' | 'previous';
          total_pages?: number;
          total_notes?: number;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_NOTES_PAGINATED, {
            metadata: {
              ...(metadata?.page_number !== undefined && {
                page_number: String(metadata.page_number),
              }),
              ...(metadata?.total_pages !== undefined && {
                total_pages: String(metadata.total_pages),
              }),
              ...(metadata?.total_notes !== undefined && {
                total_notes: String(metadata.total_notes),
              }),
              ...metadata,
            },
          });
        },

        // Note Audit Detail Page Events
        /**
         * Track when CPT review panel is viewed
         */
        cptReviewViewed: (metadata?: {
          note_id?: string;
          cpt_match?: boolean;
          provider_codes?: string;
          recommended_codes?: string;
          has_billing_concerns?: boolean;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_CPT_REVIEW_VIEWED, { metadata });
        },
        /**
         * Track when revision history section is viewed
         */
        revisionHistoryViewed: (metadata?: { note_id?: string; revision_count?: number }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_REVISION_HISTORY_VIEWED, {
            metadata: {
              ...(metadata?.revision_count !== undefined && {
                revision_count: String(metadata.revision_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks a specific revision
         */
        revisionSelected: (metadata?: {
          note_id?: string;
          revision_number?: number;
          total_revisions?: number;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_REVISION_SELECTED, {
            metadata: {
              ...(metadata?.revision_number !== undefined && {
                revision_number: String(metadata.revision_number),
              }),
              ...(metadata?.total_revisions !== undefined && {
                total_revisions: String(metadata.total_revisions),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user clicks rule in compliance table
         */
        ruleComplianceRowClicked: (metadata?: {
          note_id?: string;
          rule_id?: string;
          compliance_status?: 'yes' | 'no' | 'warning';
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_RULE_COMPLIANCE_ROW_CLICKED, { metadata });
        },

        // Error & Retry Events
        /**
         * Track when error alert is shown to user
         */
        errorDisplayed: (metadata?: {
          error_type?: 'providers' | 'notes' | 'rules' | 'analytics' | 'detail';
          error_message?: string;
          page?: string;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_ERROR_DISPLAYED, { metadata });
        },
        /**
         * Track when user clicks Retry button after error
         */
        retryClicked: (metadata?: {
          error_type?: 'providers' | 'notes' | 'rules' | 'analytics' | 'detail';
          page?: string;
        }) => {
          trackEvent(EventNames.JOTAUDIT_DASHBOARD_RETRY_CLICKED, { metadata });
        },

        // Rules Introduction Card Events
        /**
         * Track when rules introduction card is viewed
         */
        rulesIntroViewed: (metadata?: { page?: string }) => {
          trackEvent(EventNames.JOTAUDIT_RULES_INTRO_VIEWED, { metadata });
        },
        /**
         * Track when user clicks Try It Out button
         */
        rulesIntroTryItClicked: (metadata?: { source?: string }) => {
          trackEvent(EventNames.JOTAUDIT_RULES_INTRO_TRY_IT_CLICKED, { metadata });
        },
        /**
         * Track when user clicks Learn More button
         */
        rulesIntroLearnMoreClicked: (metadata?: { url?: string }) => {
          trackEvent(EventNames.JOTAUDIT_RULES_INTRO_LEARN_MORE_CLICKED, { metadata });
        },
        /**
         * Track when video starts playing
         */
        rulesIntroVideoPlayed: (metadata?: { video_time?: number }) => {
          trackEvent(EventNames.JOTAUDIT_RULES_INTRO_VIDEO_PLAYED, { metadata });
        },
        /**
         * Track when user dismisses rules introduction card
         */
        rulesIntroDismissed: (metadata?: { card_id?: string }) => {
          trackEvent(EventNames.JOTAUDIT_RULES_INTRO_DISMISSED, { metadata });
        },
      },

      // Rules AI Chat metrics (Audit Center rules editing)
      rulesChat: {
        /**
         * Track when user opens the rules AI chat
         */
        opened: (ruleId: string, metadata?: { rule_id?: string }) => {
          trackEvent(EventNames.RULES_CHAT_OPENED, {
            metadata: { rule_id: ruleId, ...metadata },
          });
        },
        /**
         * Track when user closes the rules AI chat
         */
        closed: (ruleId: string, metadata?: { rule_id?: string; message_count?: number }) => {
          trackEvent(EventNames.RULES_CHAT_CLOSED, {
            metadata: {
              rule_id: ruleId,
              ...(metadata?.message_count !== undefined && {
                message_count: String(metadata.message_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user sends a chat message
         */
        messageSent: (
          ruleId: string,
          metadata?: {
            message_length?: number;
            message_type?: 'text' | 'command';
            rule_id?: string;
          },
        ) => {
          trackEvent(EventNames.RULES_CHAT_MESSAGE_SENT, {
            metadata: {
              rule_id: ruleId,
              ...(metadata?.message_length !== undefined && {
                message_length: String(metadata.message_length),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when AI response is received
         */
        responseReceived: (
          ruleId: string,
          metadata?: {
            response_length?: number;
            response_time_ms?: number;
            rule_id?: string;
          },
        ) => {
          trackEvent(EventNames.RULES_CHAT_RESPONSE_RECEIVED, {
            metadata: {
              rule_id: ruleId,
              ...(metadata?.response_length !== undefined && {
                response_length: String(metadata.response_length),
              }),
              ...(metadata?.response_time_ms !== undefined && {
                response_time_ms: String(metadata.response_time_ms),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when chat error occurs
         */
        errorOccurred: (
          ruleId: string,
          metadata?: {
            error_type?: string;
            error_message?: string;
            rule_id?: string;
          },
        ) => {
          trackEvent(EventNames.RULES_CHAT_ERROR_OCCURRED, {
            metadata: { rule_id: ruleId, ...metadata },
          });
        },
        /**
         * Track when chat session starts
         */
        sessionStarted: (
          ruleId: string,
          metadata?: {
            rule_id?: string;
            initial_message_count?: number;
          },
        ) => {
          trackEvent(EventNames.RULES_CHAT_SESSION_STARTED, {
            metadata: {
              rule_id: ruleId,
              ...(metadata?.initial_message_count !== undefined && {
                initial_message_count: String(metadata.initial_message_count),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when chat thread is cleared
         */
        threadCleared: (ruleId: string, metadata?: { rule_id?: string }) => {
          trackEvent(EventNames.RULES_CHAT_THREAD_CLEARED, {
            metadata: { rule_id: ruleId, ...metadata },
          });
        },
        /**
         * Track SSE streaming performance metrics
         */
        ssePerformance: (
          ruleId: string,
          metadata: {
            time_to_connection_ms: number;
            time_to_first_event_ms: number;
            time_to_first_token_ms: number;
            total_duration_ms: number;
            run_id: string;
            thread_id?: string;
            outcome: 'success' | 'interrupt' | 'error';
            event_count: number;
            exceeded_threshold: boolean;
            threshold_ms?: number;
          },
        ) => {
          trackEvent(EventNames.RULES_CHAT_SSE_PERFORMANCE, {
            value: Math.round(metadata.total_duration_ms),
            metadata: {
              rule_id: ruleId,
              time_to_connection_ms: String(Math.round(metadata.time_to_connection_ms)),
              time_to_first_event_ms: String(Math.round(metadata.time_to_first_event_ms)),
              time_to_first_token_ms: String(Math.round(metadata.time_to_first_token_ms)),
              total_duration_ms: String(Math.round(metadata.total_duration_ms)),
              run_id: metadata.run_id,
              ...(metadata.thread_id && { thread_id: metadata.thread_id }),
              outcome: metadata.outcome,
              event_count: String(metadata.event_count),
              exceeded_threshold: metadata.exceeded_threshold ? 'true' : 'false',
              ...(metadata.threshold_ms !== undefined && {
                threshold_ms: String(Math.round(metadata.threshold_ms)),
              }),
            },
          });
        },
        /**
         * Track when interrupt is shown to user
         */
        interruptShown: (
          ruleId: string,
          metadata?: {
            interrupt_type?: string;
            interrupt_id?: string;
            rule_id?: string;
          },
        ) => {
          trackEvent(EventNames.RULES_CHAT_INTERRUPT_SHOWN, {
            metadata: { rule_id: ruleId, ...metadata },
          });
        },
        /**
         * Track when user responds to interrupt
         */
        interruptResponded: (
          ruleId: string,
          metadata?: {
            interrupt_type?: string;
            interrupt_id?: string;
            response_type?: 'confirm' | 'cancel' | 'select' | 'text';
            selected_count?: number;
            rule_id?: string;
          },
        ) => {
          trackEvent(EventNames.RULES_CHAT_INTERRUPT_RESPONDED, {
            metadata: {
              rule_id: ruleId,
              ...(metadata?.selected_count !== undefined && {
                selected_count: String(metadata.selected_count),
              }),
              ...metadata,
            },
          });
        },
      },

      // Template Build metrics (AI-powered template creation)
      templateBuild: {
        /**
         * Track when template build is started
         */
        started: (metadata?: { task_id?: string }) => {
          trackEvent(EventNames.TEMPLATE_BUILD_STARTED, { metadata });
        },
        /**
         * Track when template build is started from an existing template
         */
        startedFromTemplate: (metadata: { task_id: string; source_template_id: string }) => {
          trackEvent(EventNames.TEMPLATE_BUILD_STARTED_FROM_TEMPLATE, { metadata });
        },
        /**
         * Track upload type (PDF or text paste)
         */
        uploadType: (metadata: { upload_type: 'pdf' | 'text'; file_size_kb?: number }) => {
          trackEvent(EventNames.TEMPLATE_BUILD_UPLOAD_TYPE, {
            metadata: {
              upload_type: metadata.upload_type,
              ...(metadata.file_size_kb !== undefined && {
                file_size_kb: String(Math.round(metadata.file_size_kb)),
              }),
            },
          });
        },
        /**
         * Track when user sends a chat message
         */
        messageSent: (
          taskId: string,
          metadata?: {
            message_length?: number;
            has_section_context?: boolean;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_BUILD_CHAT_MESSAGE_SENT, {
            metadata: {
              task_id: taskId,
              ...(metadata?.message_length !== undefined && {
                message_length: String(metadata.message_length),
              }),
              ...(metadata?.has_section_context !== undefined && {
                has_section_context: String(metadata.has_section_context),
              }),
            },
          });
        },
        /**
         * Track when user selects a section for context
         */
        sectionSelected: (
          taskId: string,
          metadata?: { section_id?: string; section_title?: string },
        ) => {
          trackEvent(EventNames.TEMPLATE_BUILD_SECTION_SELECTED, {
            metadata: { task_id: taskId, ...metadata },
          });
        },
        /**
         * Track when template build is completed
         */
        completed: (
          taskId: string,
          metadata?: {
            template_id?: string;
            section_count?: number;
            duration_ms?: number;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_BUILD_COMPLETED, {
            metadata: {
              task_id: taskId,
              ...(metadata?.section_count !== undefined && {
                section_count: String(metadata.section_count),
              }),
              ...(metadata?.duration_ms !== undefined && {
                duration_ms: String(Math.round(metadata.duration_ms)),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when user abandons template build
         */
        abandoned: (
          taskId: string,
          metadata?: { build_status?: string; time_spent_ms?: number },
        ) => {
          trackEvent(EventNames.TEMPLATE_BUILD_ABANDONED, {
            metadata: {
              task_id: taskId,
              ...(metadata?.time_spent_ms !== undefined && {
                time_spent_ms: String(Math.round(metadata.time_spent_ms)),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track when error occurs during template build
         */
        error: (taskId: string, metadata?: { error_type?: string; error_message?: string }) => {
          trackEvent(EventNames.TEMPLATE_BUILD_ERROR, {
            metadata: { task_id: taskId, ...metadata },
          });
        },
      },

      // Rule Build metrics (AI-powered rule creation)
      ruleBuild: {
        /**
         * Track page view
         */
        pageViewed: (metadata?: { task_id?: string; has_drafts?: boolean }) => {
          trackEvent(EventNames.RULE_BUILD_PAGE_VIEWED, {
            metadata: {
              ...(metadata?.has_drafts !== undefined && {
                has_drafts: String(metadata.has_drafts),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track task creation
         */
        taskCreated: (
          taskId: string,
          metadata?: { source?: 'empty' | 'document' | 'prompt' | 'rule' },
        ) => {
          trackEvent(EventNames.RULE_BUILD_TASK_CREATED, {
            metadata: { task_id: taskId, ...metadata },
          });
        },
        /**
         * Track task created from document upload
         */
        taskCreatedFromDocument: (
          taskId: string,
          metadata?: { document_id?: string; file_size_kb?: number },
        ) => {
          trackEvent(EventNames.RULE_BUILD_TASK_CREATED_FROM_DOCUMENT, {
            metadata: {
              task_id: taskId,
              ...(metadata?.file_size_kb !== undefined && {
                file_size_kb: String(Math.round(metadata.file_size_kb)),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track task created from prompt
         */
        taskCreatedFromPrompt: (taskId: string, metadata?: { prompt_length?: number }) => {
          trackEvent(EventNames.RULE_BUILD_TASK_CREATED_FROM_PROMPT, {
            metadata: {
              task_id: taskId,
              ...(metadata?.prompt_length !== undefined && {
                prompt_length: String(metadata.prompt_length),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track task created from existing rule
         */
        taskCreatedFromRule: (taskId: string, metadata?: { source_rule_id?: string }) => {
          trackEvent(EventNames.RULE_BUILD_TASK_CREATED_FROM_RULE, {
            metadata: { task_id: taskId, ...metadata },
          });
        },
        /**
         * Track draft selection
         */
        draftSelected: (taskId: string, draftId: string, metadata?: { draft_status?: string }) => {
          trackEvent(EventNames.RULE_BUILD_DRAFT_SELECTED, {
            metadata: { task_id: taskId, draft_id: draftId, ...metadata },
          });
        },
        /**
         * Track draft edit
         */
        draftEdited: (taskId: string, draftId: string, metadata?: { field_changed?: string }) => {
          trackEvent(EventNames.RULE_BUILD_DRAFT_EDITED, {
            metadata: { task_id: taskId, draft_id: draftId, ...metadata },
          });
        },
        /**
         * Track draft marked ready
         */
        draftMarkedReady: (taskId: string, draftId: string) => {
          trackEvent(EventNames.RULE_BUILD_DRAFT_MARKED_READY, {
            metadata: { task_id: taskId, draft_id: draftId },
          });
        },
        /**
         * Track draft marked rejected
         */
        draftMarkedRejected: (taskId: string, draftId: string) => {
          trackEvent(EventNames.RULE_BUILD_DRAFT_MARKED_REJECTED, {
            metadata: { task_id: taskId, draft_id: draftId },
          });
        },
        /**
         * Track draft deleted
         */
        draftDeleted: (taskId: string, draftId: string) => {
          trackEvent(EventNames.RULE_BUILD_DRAFT_DELETED, {
            metadata: { task_id: taskId, draft_id: draftId },
          });
        },
        /**
         * Track single draft saved as rule
         */
        draftSaved: (
          taskId: string,
          draftId: string,
          metadata?: { is_update?: boolean; already_saved?: boolean },
        ) => {
          trackEvent(EventNames.RULE_BUILD_DRAFT_SAVED, {
            metadata: {
              task_id: taskId,
              draft_id: draftId,
              ...(metadata?.is_update !== undefined && {
                is_update: String(metadata.is_update),
              }),
              ...(metadata?.already_saved !== undefined && {
                already_saved: String(metadata.already_saved),
              }),
            },
          });
        },
        /**
         * Track drafts saved as rules (batch)
         */
        draftsSaved: (taskId: string, metadata: { draft_count: number; saved_count: number }) => {
          trackEvent(EventNames.RULE_BUILD_DRAFTS_SAVED, {
            metadata: {
              task_id: taskId,
              draft_count: String(metadata.draft_count),
              saved_count: String(metadata.saved_count),
            },
          });
        },
        /**
         * Track chat message sent
         */
        chatMessageSent: (
          taskId: string,
          metadata?: { message_length?: number; has_focused_draft?: boolean },
        ) => {
          trackEvent(EventNames.RULE_BUILD_CHAT_MESSAGE_SENT, {
            metadata: {
              task_id: taskId,
              ...(metadata?.message_length !== undefined && {
                message_length: String(metadata.message_length),
              }),
              ...(metadata?.has_focused_draft !== undefined && {
                has_focused_draft: String(metadata.has_focused_draft),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track chat response received (stream completed)
         */
        chatResponseReceived: (
          taskId: string,
          metadata?: { response_length?: number; duration_ms?: number },
        ) => {
          trackEvent(EventNames.RULE_BUILD_CHAT_RESPONSE_RECEIVED, {
            metadata: {
              task_id: taskId,
              ...(metadata?.response_length !== undefined && {
                response_length: String(metadata.response_length),
              }),
              ...(metadata?.duration_ms !== undefined && {
                duration_ms: String(Math.round(metadata.duration_ms)),
              }),
            },
          });
        },
        /**
         * Track interrupt shown to user
         */
        chatInterruptShown: (
          taskId: string,
          metadata?: { interrupt_type?: string; message?: string },
        ) => {
          trackEvent(EventNames.RULE_BUILD_CHAT_INTERRUPT_SHOWN, {
            metadata: { task_id: taskId, ...metadata },
          });
        },
        /**
         * Track interrupt response
         */
        chatInterruptResponded: (
          taskId: string,
          metadata?: { interrupt_type?: string; response?: string },
        ) => {
          trackEvent(EventNames.RULE_BUILD_CHAT_INTERRUPT_RESPONDED, {
            metadata: { task_id: taskId, ...metadata },
          });
        },
        /**
         * Track chat error
         */
        chatError: (taskId: string, metadata?: { error_type?: string; error_message?: string }) => {
          trackEvent(EventNames.RULE_BUILD_CHAT_ERROR, {
            metadata: { task_id: taskId, ...metadata },
          });
        },
        /**
         * Track generation started (polling begins)
         */
        generationStarted: (taskId: string, metadata?: { source?: string }) => {
          trackEvent(EventNames.RULE_BUILD_GENERATION_STARTED, {
            metadata: { task_id: taskId, ...metadata },
          });
        },
        /**
         * Track generation completed
         */
        generationCompleted: (
          taskId: string,
          metadata?: { draft_count?: number; duration_ms?: number },
        ) => {
          trackEvent(EventNames.RULE_BUILD_GENERATION_COMPLETED, {
            metadata: {
              task_id: taskId,
              ...(metadata?.draft_count !== undefined && {
                draft_count: String(metadata.draft_count),
              }),
              ...(metadata?.duration_ms !== undefined && {
                duration_ms: String(Math.round(metadata.duration_ms)),
              }),
              ...metadata,
            },
          });
        },
        /**
         * Track generation timeout
         */
        generationTimeout: (taskId: string) => {
          trackEvent(EventNames.RULE_BUILD_GENERATION_TIMEOUT, {
            metadata: { task_id: taskId },
          });
        },
        /**
         * Track generation failed
         */
        generationFailed: (taskId: string, metadata?: { error_type?: string }) => {
          trackEvent(EventNames.RULE_BUILD_GENERATION_FAILED, {
            metadata: { task_id: taskId, ...metadata },
          });
        },
        /**
         * Track error
         */
        error: (taskId: string, metadata?: { error_type?: string; error_message?: string }) => {
          trackEvent(EventNames.RULE_BUILD_ERROR, {
            metadata: { task_id: taskId, ...metadata },
          });
        },
      },

      // Template metrics
      template: {
        earlierNotesOpened: (templateId: string, selectedMode: string) => {
          trackEvent(EventNames.TEMPLATE_EARLIER_NOTES_OPENED, {
            metadata: {
              template_id: templateId,
              selected_mode: selectedMode,
            },
          });
        },
        earlierNotesSelected: (templateId: string, selectedMode: string) => {
          trackEvent(EventNames.TEMPLATE_EARLIER_NOTES_SELECTED, {
            metadata: {
              template_id: templateId,
              selected_mode: selectedMode,
            },
          });
        },
        // Template ↔ appointment-type linking from the template side (GDN-2149)
        appointmentTypeLinkOpened: (metadata: {
          template_id: string;
          source: 'template_editor' | 'ai_builder_completion';
          /** Types already linked to the template when the modal opened. */
          linked_count: number;
        }) => {
          trackEvent(EventNames.TEMPLATE_APPOINTMENT_TYPE_LINK_OPENED, {
            metadata: {
              ...metadata,
              linked_count: String(metadata.linked_count),
            },
          });
        },
        appointmentTypesChanged: (metadata: {
          template_id: string;
          /** 'link' on commit; 'undo' when the snackbar action reverts it. */
          action: 'link' | 'undo';
          /** Types newly linked (no previous template). */
          linked_count: number;
          /** Types moved off a different template. */
          reassigned_count: number;
          source: 'template_editor' | 'ai_builder_completion';
        }) => {
          trackEvent(EventNames.TEMPLATE_APPOINTMENT_TYPES_CHANGED, {
            metadata: {
              ...metadata,
              linked_count: String(metadata.linked_count),
              reassigned_count: String(metadata.reassigned_count),
            },
          });
        },
        createAppointmentTypeClicked: (metadata: {
          template_id: string;
          source: 'template_editor' | 'ai_builder_completion';
          /** Whether the user had typed a search query when choosing Create. */
          has_query: boolean;
        }) => {
          trackEvent(EventNames.TEMPLATE_CREATE_APPOINTMENT_TYPE_CLICKED, {
            metadata: {
              ...metadata,
              has_query: String(metadata.has_query),
            },
          });
        },
        select: (templateId: string, metadata?: { specialty?: string; is_custom?: boolean }) => {
          trackEvent(EventNames.TEMPLATE_SELECTED, {
            metadata: {
              template_id: templateId,
              ...(metadata?.is_custom !== undefined && {
                is_custom: String(metadata.is_custom),
              }),
              ...metadata,
            },
          });
        },
        customize: (
          templateId: string,
          metadata?: { sections_added?: number; sections_removed?: number },
        ) => {
          trackEvent(EventNames.TEMPLATE_CUSTOMIZED, {
            metadata: {
              template_id: templateId,
              ...(metadata?.sections_added && {
                sections_added: String(metadata.sections_added),
              }),
              ...(metadata?.sections_removed && {
                sections_removed: String(metadata.sections_removed),
              }),
            },
          });
        },
        save: (
          templateId: string,
          metadata?: {
            template_type?: string;
            is_custom?: boolean;
            specialty?: string;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_SAVED, {
            metadata: {
              template_id: templateId,
              ...(metadata?.is_custom !== undefined && {
                is_custom: String(metadata.is_custom),
              }),
              ...metadata,
            },
          });
        },
        delete: (
          templateId: string,
          metadata?: {
            template_type?: string;
            is_custom?: boolean;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_DELETED, {
            metadata: {
              template_id: templateId,
              ...(metadata?.is_custom !== undefined && {
                is_custom: String(metadata.is_custom),
              }),
              ...metadata,
            },
          });
        },
        share: (
          templateId: string,
          metadata?: {
            recipients_count?: number;
            share_method?: string;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_SHARED, {
            metadata: {
              template_id: templateId,
              ...(metadata?.recipients_count && {
                recipients_count: String(metadata.recipients_count),
              }),
              ...metadata,
            },
          });
        },
        addSection: (
          templateId: string,
          metadata?: {
            section_type?: string;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_SECTION_ADDED, {
            metadata: {
              template_id: templateId,
              ...metadata,
            },
          });
        },
        removeSection: (
          templateId: string,
          metadata?: {
            section_type?: string;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_SECTION_REMOVED, {
            metadata: {
              template_id: templateId,
              ...metadata,
            },
          });
        },

        // Template customization flow
        customizationStarted: (
          templateId: string,
          metadata?: {
            source?: 'new' | 'existing' | 'public_copy' | 'ai_builder';
            base_template_id?: string;
            user_specialty?: string;
            initial_sections_count?: number;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_CUSTOMIZATION_STARTED, {
            metadata: {
              template_id: templateId,
              ...(metadata?.initial_sections_count && {
                initial_sections_count: String(metadata.initial_sections_count),
              }),
              ...metadata,
            },
          });
        },

        sectionModified: (
          templateId: string,
          metadata?: {
            action?: 'edit_title' | 'edit_content' | 'reorder' | 'toggle_summary';
            section_type?: string;
            section_position?: number;
            has_ai_content?: boolean;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_SECTION_MODIFIED, {
            metadata: {
              template_id: templateId,
              ...(metadata?.section_position !== undefined && {
                section_position: String(metadata.section_position),
              }),
              ...(metadata?.has_ai_content !== undefined && {
                has_ai_content: String(metadata.has_ai_content),
              }),
              ...metadata,
            },
          });
        },

        previewGenerated: (
          templateId: string,
          metadata?: {
            preview_count_in_session?: number;
            sections_modified_since_last?: number;
            trigger?: 'manual' | 'auto';
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_PREVIEW_GENERATED, {
            metadata: {
              template_id: templateId,
              ...(metadata?.preview_count_in_session && {
                preview_count_in_session: String(metadata.preview_count_in_session),
              }),
              ...(metadata?.sections_modified_since_last && {
                sections_modified_since_last: String(metadata.sections_modified_since_last),
              }),
              ...metadata,
            },
          });
        },

        saveAttempted: (
          templateId: string,
          metadata?: {
            success?: boolean;
            validation_errors?: string[];
            retry_count?: number;
            sections_count?: number;
            has_custom_sections?: boolean;
            created_via?: 'manual' | 'ai_builder';
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_SAVE_ATTEMPTED, {
            metadata: {
              template_id: templateId,
              ...(metadata?.success !== undefined && {
                success: String(metadata.success),
              }),
              ...(metadata?.retry_count && {
                retry_count: String(metadata.retry_count),
              }),
              ...(metadata?.sections_count && {
                sections_count: String(metadata.sections_count),
              }),
              ...(metadata?.has_custom_sections !== undefined && {
                has_custom_sections: String(metadata.has_custom_sections),
              }),
              ...(metadata?.validation_errors && {
                validation_errors: metadata.validation_errors.join(','),
              }),
              ...metadata,
            },
          });
        },

        // AI builder tracking
        aiChatStarted: (
          sessionId: string,
          metadata?: {
            initial_transcript_id?: string;
            upload_type?: 'file' | 'transcript';
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_AI_CHAT_STARTED, {
            metadata: {
              session_id: sessionId,
              ...metadata,
            },
          });
        },

        aiMessageSent: (
          sessionId: string,
          metadata?: {
            message_type?: 'prompt' | 'refinement' | 'undo_request';
            selected_section_id?: string;
            prompt_source?: 'user_typed' | 'prompt_library';
            processing_stage?: string;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_AI_MESSAGE_SENT, {
            metadata: {
              session_id: sessionId,
              ...metadata,
            },
          });
        },

        aiRegenerationCompleted: (
          sessionId: string,
          metadata?: {
            sections_updated?: number;
            success?: boolean;
            error_type?: string;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_AI_REGENERATION_COMPLETED, {
            metadata: {
              session_id: sessionId,
              ...(metadata?.sections_updated && {
                sections_updated: String(metadata.sections_updated),
              }),
              ...(metadata?.success !== undefined && {
                success: String(metadata.success),
              }),
              ...metadata,
            },
          });
        },

        // Module selector tracking
        moduleSelector: {
          opened: (metadata: {
            source: string;
            excluded_count?: number;
            template_id?: string;
            template_name?: string;
          }) => {
            trackEvent(EventNames.TEMPLATE_MODULE_SELECTOR_OPENED, {
              metadata: {
                source: metadata.source,
                ...(metadata.excluded_count !== undefined && {
                  excluded_count: String(metadata.excluded_count),
                }),
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.template_name && { template_name: metadata.template_name }),
              },
            });
          },

          closed: (metadata: {
            source: string;
            duration_ms?: number;
            had_selection?: boolean;
            template_id?: string;
            template_name?: string;
          }) => {
            trackEvent(EventNames.TEMPLATE_MODULE_SELECTOR_CLOSED, {
              metadata: {
                source: metadata.source,
                ...(metadata.duration_ms !== undefined && {
                  duration_ms: String(metadata.duration_ms),
                }),
                ...(metadata.had_selection !== undefined && {
                  had_selection: String(metadata.had_selection),
                }),
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.template_name && { template_name: metadata.template_name }),
              },
            });
          },

          searched: (metadata: {
            source: string;
            query_length: number;
            results_count: number;
            template_id?: string;
            template_name?: string;
          }) => {
            trackEvent(EventNames.TEMPLATE_MODULE_SELECTOR_SEARCH_PERFORMED, {
              metadata: {
                source: metadata.source,
                query_length: String(metadata.query_length),
                results_count: String(metadata.results_count),
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.template_name && { template_name: metadata.template_name }),
              },
            });
          },

          filterChanged: (metadata: {
            source: string;
            from_filter: string;
            to_filter: string;
            template_id?: string;
            template_name?: string;
          }) => {
            trackEvent(EventNames.TEMPLATE_MODULE_SELECTOR_FILTER_CHANGED, {
              metadata: {
                source: metadata.source,
                from_filter: metadata.from_filter,
                to_filter: metadata.to_filter,
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.template_name && { template_name: metadata.template_name }),
              },
            });
          },

          sectionSelected: (metadata: {
            source: string;
            module_id: string;
            selection_count: number;
            is_custom?: boolean;
            template_id?: string;
            template_name?: string;
          }) => {
            trackEvent(EventNames.TEMPLATE_MODULE_SELECTOR_SECTION_SELECTED, {
              metadata: {
                source: metadata.source,
                module_id: metadata.module_id,
                selection_count: String(metadata.selection_count),
                ...(metadata.is_custom !== undefined && {
                  is_custom: String(metadata.is_custom),
                }),
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.template_name && { template_name: metadata.template_name }),
              },
            });
          },

          sectionDeselected: (metadata: {
            source: string;
            module_id: string;
            selection_count: number;
            template_id?: string;
            template_name?: string;
          }) => {
            trackEvent(EventNames.TEMPLATE_MODULE_SELECTOR_SECTION_DESELECTED, {
              metadata: {
                source: metadata.source,
                module_id: metadata.module_id,
                selection_count: String(metadata.selection_count),
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.template_name && { template_name: metadata.template_name }),
              },
            });
          },

          previewToggled: (metadata: {
            source: string;
            action: 'show' | 'hide';
            module_id: string;
            template_id?: string;
            template_name?: string;
          }) => {
            trackEvent(EventNames.TEMPLATE_MODULE_SELECTOR_PREVIEW_TOGGLED, {
              metadata: {
                source: metadata.source,
                action: metadata.action,
                module_id: metadata.module_id,
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.template_name && { template_name: metadata.template_name }),
              },
            });
          },

          customSectionInitiated: (metadata: {
            source: string;
            template_id?: string;
            template_name?: string;
          }) => {
            trackEvent(EventNames.TEMPLATE_MODULE_SELECTOR_CUSTOM_SECTION_INITIATED, {
              metadata: {
                source: metadata.source,
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.template_name && { template_name: metadata.template_name }),
              },
            });
          },

          customSectionCancelled: (metadata: {
            source: string;
            had_progress?: boolean;
            template_id?: string;
            template_name?: string;
          }) => {
            trackEvent(EventNames.TEMPLATE_MODULE_SELECTOR_CUSTOM_SECTION_CANCELLED, {
              metadata: {
                source: metadata.source,
                ...(metadata.had_progress !== undefined && {
                  had_progress: String(metadata.had_progress),
                }),
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.template_name && { template_name: metadata.template_name }),
              },
            });
          },

          selectionCleared: (metadata: {
            source: string;
            cleared_count: number;
            template_id?: string;
            template_name?: string;
          }) => {
            trackEvent(EventNames.TEMPLATE_MODULE_SELECTOR_SELECTION_CLEARED, {
              metadata: {
                source: metadata.source,
                cleared_count: String(metadata.cleared_count),
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.template_name && { template_name: metadata.template_name }),
              },
            });
          },

          sectionsAdded: (metadata: {
            source: string;
            section_count: number;
            custom_count?: number;
            duration_ms?: number;
            template_id?: string;
            template_name?: string;
          }) => {
            trackEvent(EventNames.TEMPLATE_MODULE_SELECTOR_SECTIONS_ADDED, {
              metadata: {
                source: metadata.source,
                section_count: String(metadata.section_count),
                ...(metadata.custom_count !== undefined && {
                  custom_count: String(metadata.custom_count),
                }),
                ...(metadata.duration_ms !== undefined && {
                  duration_ms: String(metadata.duration_ms),
                }),
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.template_name && { template_name: metadata.template_name }),
              },
            });
          },

          abandoned: (metadata: {
            source: string;
            had_selection?: boolean;
            selection_count?: number;
            duration_ms?: number;
            template_id?: string;
            template_name?: string;
          }) => {
            trackEvent(EventNames.TEMPLATE_MODULE_SELECTOR_ABANDONED, {
              metadata: {
                source: metadata.source,
                ...(metadata.had_selection !== undefined && {
                  had_selection: String(metadata.had_selection),
                }),
                ...(metadata.selection_count !== undefined && {
                  selection_count: String(metadata.selection_count),
                }),
                ...(metadata.duration_ms !== undefined && {
                  duration_ms: String(metadata.duration_ms),
                }),
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.template_name && { template_name: metadata.template_name }),
              },
            });
          },
        },

        // Template pinning and reordering
        reorder: (
          templateId: string,
          metadata?: {
            from_position?: number;
            to_position?: number;
            pinned_count?: number;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_REORDERED, {
            metadata: {
              template_id: templateId,
              ...(metadata?.from_position !== undefined && {
                from_position: String(metadata.from_position),
              }),
              ...(metadata?.to_position !== undefined && {
                to_position: String(metadata.to_position),
              }),
              ...(metadata?.pinned_count !== undefined && {
                pinned_count: String(metadata.pinned_count),
              }),
            },
          });
        },

        pin: (
          templateId: string,
          metadata?: {
            position?: number;
            pinned_count?: number;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_PINNED, {
            metadata: {
              template_id: templateId,
              ...(metadata?.position !== undefined && {
                position: String(metadata.position),
              }),
              ...(metadata?.pinned_count !== undefined && {
                pinned_count: String(metadata.pinned_count),
              }),
            },
          });
        },

        unpin: (
          templateId: string,
          metadata?: {
            pinned_count?: number;
          },
        ) => {
          trackEvent(EventNames.TEMPLATE_UNPINNED, {
            metadata: {
              template_id: templateId,
              ...(metadata?.pinned_count !== undefined && {
                pinned_count: String(metadata.pinned_count),
              }),
            },
          });
        },

        /**
         * Track when "add template to list" banner is shown (NoteChatView)
         */
        addToListBannerShown: (metadata?: {
          note_id?: string;
          template_id?: string;
          template_name?: string;
          template_scope?: 'public' | 'clinic' | 'personal';
        }) => {
          trackEvent(EventNames.TEMPLATE_ADD_TO_LIST_BANNER_SHOWN, { metadata });
        },

        /**
         * Track when user dismisses "add template to list" banner
         */
        addToListBannerDismissed: (metadata?: {
          note_id?: string;
          template_id?: string;
          template_name?: string;
          time_visible_ms?: number;
        }) => {
          trackEvent(EventNames.TEMPLATE_ADD_TO_LIST_BANNER_DISMISSED, {
            metadata: {
              ...(metadata?.time_visible_ms !== undefined && {
                time_visible_ms: String(metadata.time_visible_ms),
              }),
              ...metadata,
            },
          });
        },

        /**
         * Track when user clicks "Add to My Templates" button
         */
        addToListClicked: (metadata?: {
          note_id?: string;
          template_id?: string;
          template_name?: string;
          source?: 'banner';
        }) => {
          trackEvent(EventNames.TEMPLATE_ADD_TO_LIST_CLICKED, { metadata });
        },
      },

      // Module conversation metrics (AI-powered section customization)
      module: {
        conversation: {
          opened: (metadata: {
            source: 'note-editor' | 'template-builder' | 'module-selector';
            has_initial_feedback?: boolean;
            has_initial_module?: boolean;
            template_id?: string;
            note_id?: string;
          }) => {
            trackEvent(EventNames.MODULE_CONVERSATION_OPENED, {
              metadata: {
                source: metadata.source,
                ...(metadata.has_initial_feedback !== undefined && {
                  has_initial_feedback: String(metadata.has_initial_feedback),
                }),
                ...(metadata.has_initial_module !== undefined && {
                  has_initial_module: String(metadata.has_initial_module),
                }),
                ...(metadata.template_id && { template_id: metadata.template_id }),
                ...(metadata.note_id && { note_id: metadata.note_id }),
              },
            });
          },

          noteSelected: (metadata: {
            source: 'note-editor' | 'template-builder' | 'module-selector';
            note_id: string;
            has_previous_selection?: boolean;
            session_id?: string;
          }) => {
            trackEvent(EventNames.MODULE_CONVERSATION_NOTE_SELECTED, {
              metadata: {
                source: metadata.source,
                note_id: metadata.note_id,
                ...(metadata.has_previous_selection !== undefined && {
                  has_previous_selection: String(metadata.has_previous_selection),
                }),
                ...(metadata.session_id && { session_id: metadata.session_id }),
              },
            });
          },

          feedbackSubmitted: (metadata: {
            source: 'note-editor' | 'template-builder' | 'module-selector';
            feedback_length: number;
            iteration_number: number;
            submission_method: 'button' | 'keyboard';
            session_id?: string;
          }) => {
            trackEvent(EventNames.MODULE_CONVERSATION_FEEDBACK_SUBMITTED, {
              metadata: {
                source: metadata.source,
                feedback_length: String(metadata.feedback_length),
                iteration_number: String(metadata.iteration_number),
                submission_method: metadata.submission_method,
                ...(metadata.session_id && { session_id: metadata.session_id }),
              },
            });
          },

          acceptClicked: (metadata: {
            source: 'note-editor' | 'template-builder' | 'module-selector';
            total_duration_ms: number;
            iteration_count: number;
            session_id?: string;
          }) => {
            trackEvent(EventNames.MODULE_CONVERSATION_ACCEPT_CLICKED, {
              metadata: {
                source: metadata.source,
                total_duration_ms: String(metadata.total_duration_ms),
                iteration_count: String(metadata.iteration_count),
                ...(metadata.session_id && { session_id: metadata.session_id }),
              },
            });
          },

          exitClicked: (metadata: {
            source: 'note-editor' | 'template-builder' | 'module-selector';
            duration_ms: number;
            iteration_count: number;
            had_results: boolean;
            session_id?: string;
          }) => {
            trackEvent(EventNames.MODULE_CONVERSATION_EXIT_CLICKED, {
              metadata: {
                source: metadata.source,
                duration_ms: String(metadata.duration_ms),
                iteration_count: String(metadata.iteration_count),
                had_results: String(metadata.had_results),
                ...(metadata.session_id && { session_id: metadata.session_id }),
              },
            });
          },

          closed: (metadata: {
            source: 'note-editor' | 'template-builder' | 'module-selector';
            had_active_session: boolean;
            session_id?: string;
          }) => {
            trackEvent(EventNames.MODULE_CONVERSATION_CLOSED, {
              metadata: {
                source: metadata.source,
                had_active_session: String(metadata.had_active_session),
                ...(metadata.session_id && { session_id: metadata.session_id }),
              },
            });
          },

          started: (metadata: {
            source: 'note-editor' | 'template-builder' | 'module-selector';
            session_id: string;
            has_initial_module?: boolean;
          }) => {
            trackEvent(EventNames.MODULE_CONVERSATION_STARTED, {
              metadata: {
                source: metadata.source,
                session_id: metadata.session_id,
                ...(metadata.has_initial_module !== undefined && {
                  has_initial_module: String(metadata.has_initial_module),
                }),
              },
            });
          },

          iterationCompleted: (metadata: {
            source: 'note-editor' | 'template-builder' | 'module-selector';
            iteration_number: number;
            generation_duration_ms: number;
            session_id?: string;
          }) => {
            trackEvent(EventNames.MODULE_CONVERSATION_ITERATION_COMPLETED, {
              metadata: {
                source: metadata.source,
                iteration_number: String(metadata.iteration_number),
                generation_duration_ms: String(metadata.generation_duration_ms),
                ...(metadata.session_id && { session_id: metadata.session_id }),
              },
            });
          },

          errorOccurred: (metadata: {
            source: 'note-editor' | 'template-builder' | 'module-selector';
            error_type: string;
            iteration_number?: number;
            session_id?: string;
          }) => {
            trackEvent(EventNames.MODULE_CONVERSATION_ERROR_OCCURRED, {
              metadata: {
                source: metadata.source,
                error_type: metadata.error_type,
                ...(metadata.iteration_number !== undefined && {
                  iteration_number: String(metadata.iteration_number),
                }),
                ...(metadata.session_id && { session_id: metadata.session_id }),
              },
            });
          },
        },
      },

      // Medical records export metrics (patient record packets — IDs/counts only, no PHI)
      medicalRecordsExport: {
        dialogOpened: (metadata: {
          patient_id: string;
          source: 'patient_page_v1' | 'patient_page_v2' | 'patient_page_v3';
        }) => {
          trackEvent(EventNames.MEDICAL_RECORDS_EXPORT_OPENED, { metadata });
        },
        reviewOpened: (metadata: { patient_id: string }) => {
          trackEvent(EventNames.MEDICAL_RECORDS_EXPORT_REVIEW_OPENED, { metadata });
        },
        outputModeChanged: (metadata: {
          patient_id: string;
          output_mode: 'combined_pdf' | 'separate_pdfs_zip';
        }) => {
          trackEvent(EventNames.MEDICAL_RECORDS_EXPORT_OUTPUT_MODE_CHANGED, { metadata });
        },
        generationStarted: (metadata: {
          patient_id: string;
          output_mode: 'combined_pdf' | 'separate_pdfs_zip';
          encounter_count: number;
          note_count: number;
          document_count: number;
          attempt: number;
        }) => {
          trackEvent(EventNames.MEDICAL_RECORDS_EXPORT_GENERATION_STARTED, { metadata });
        },
        downloadRequested: (metadata: {
          patient_id: string;
          export_id: string;
          output_mode: 'combined_pdf' | 'separate_pdfs_zip';
        }) => {
          trackEvent(EventNames.MEDICAL_RECORDS_EXPORT_DOWNLOAD_REQUESTED, { metadata });
        },
      },

      // Patient metrics
      patient: {
        creationStarted: (source: string, metadata?: { simplified_view?: boolean }) => {
          trackEvent(EventNames.PATIENT_CREATION_STARTED, {
            metadata: { source, ...metadata },
          });
        },
        creationAbandoned: (source: string, metadata?: { had_data?: boolean }) => {
          trackEvent(EventNames.PATIENT_CREATION_ABANDONED, {
            metadata: { source, ...metadata },
          });
        },
        created: (source: string, metadata?: { has_optional_fields?: boolean }) => {
          trackEvent(EventNames.PATIENT_CREATED, {
            metadata: { source, ...metadata },
          });
        },
        selected: (
          patientId: string,
          metadata?: {
            source: 'picker' | 'list' | 'search' | 'assignment';
            context?: 'recording' | 'note_editing' | 'viewing' | 'form';
          },
        ) => {
          trackEvent(EventNames.PATIENT_SELECTED, {
            metadata: { patient_id: patientId, ...metadata },
          });
        },
        updated: (
          patientId: string,
          metadata?: {
            source: string;
            fields_updated?: string[];
          },
        ) => {
          trackEvent(EventNames.PATIENT_UPDATED, {
            metadata: { patient_id: patientId, ...metadata },
          });
        },
        listViewed: (metadata?: {
          total_count?: number;
          filtered?: boolean;
          page?: number;
          page_size?: number;
        }) => {
          trackEvent(EventNames.PATIENT_LIST_VIEWED, {
            metadata: {
              ...(metadata?.total_count !== undefined && {
                total_count: String(metadata.total_count),
              }),
              ...(metadata?.filtered !== undefined && {
                filtered: String(metadata.filtered),
              }),
              ...(metadata?.page !== undefined && {
                page: String(metadata.page),
              }),
              ...(metadata?.page_size !== undefined && {
                page_size: String(metadata.page_size),
              }),
              ...metadata,
            },
          });
        },
        detailAccessed: (
          patientId: string,
          metadata?: {
            source?: 'list' | 'search' | 'direct_link' | 'navigation';
            context?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DETAIL_ACCESSED, {
            metadata: {
              patient_id: patientId,
              ...metadata,
            },
          });
        },
        deleted: (
          patientId: string,
          metadata?: {
            source?: string;
            had_encounters?: boolean;
            had_documents?: boolean;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DELETED, {
            metadata: {
              patient_id: patientId,
              ...(metadata?.had_encounters !== undefined && {
                had_encounters: String(metadata.had_encounters),
              }),
              ...(metadata?.had_documents !== undefined && {
                had_documents: String(metadata.had_documents),
              }),
              ...metadata,
            },
          });
        },

        // Patient assistant (AI chat) events
        assistantOpened: (metadata: { patient_id: string; source: string }) => {
          trackEvent(EventNames.PATIENT_ASSISTANT_OPENED, { metadata });
        },
        assistantClosed: (metadata: { patient_id: string; source: string }) => {
          trackEvent(EventNames.PATIENT_ASSISTANT_CLOSED, { metadata });
        },

        // Patient profile events
        infoFieldSaved: (metadata: {
          patient_id: string;
          source: string;
          section: string;
          field: string;
        }) => {
          trackEvent(EventNames.PATIENT_INFO_FIELD_SAVED, { metadata });
        },
        infoClinicalItemAdded: (metadata: {
          patient_id: string;
          source: string;
          item_type: 'pharmacy';
        }) => {
          trackEvent(EventNames.PATIENT_INFO_CLINICAL_ITEM_ADDED, { metadata });
        },
        infoClinicalItemUpdated: (metadata: {
          patient_id: string;
          source: string;
          item_type: 'pharmacy';
        }) => {
          trackEvent(EventNames.PATIENT_INFO_CLINICAL_ITEM_UPDATED, { metadata });
        },
        infoClinicalItemRemoved: (metadata: {
          patient_id: string;
          source: string;
          item_type: 'pharmacy';
        }) => {
          trackEvent(EventNames.PATIENT_INFO_CLINICAL_ITEM_REMOVED, { metadata });
        },
      },

      // Medication history (SureScripts) on the patient page. The backend
      // emits SURESCRIPTS_CONSENT_RECORDED / SURESCRIPTS_HISTORY_PULLED for
      // outcomes via EventPublishingService — these front-end events cover
      // user interactions only (impression + consent click + submit).
      medicationHistory: {
        opened: (metadata: {
          patient_id: string;
          consent_state: 'unavailable' | 'needed' | 'pulling' | 'granted';
          /** DoseSpot rejected the last pull — the stale-refresh warning is showing (GDN-3298). */
          consent_stale?: boolean;
        }) => {
          trackEvent(EventNames.MEDICATION_HISTORY_OPENED, { metadata });
        },
        consentClicked: (metadata: {
          patient_id: string;
          /** True when the click came from the stale-refresh re-confirm affordance, not the first-time CTA. */
          reconfirm?: boolean;
        }) => {
          trackEvent(EventNames.MEDICATION_HISTORY_CONSENT_CLICKED, { metadata });
        },
        consentSubmitted: (metadata: { patient_id: string }) => {
          trackEvent(EventNames.MEDICATION_HISTORY_CONSENT_SUBMITTED, { metadata });
        },
        sectionToggled: (metadata: {
          patient_id: string;
          expanded: boolean;
          fill_count: number;
        }) => {
          trackEvent(EventNames.MEDICATION_HISTORY_SECTION_TOGGLED, { metadata });
        },
      },

      // Unified medications card (GDN-2351) on Patient Page V3. Interaction
      // events only — the consent flow reuses the medicationHistory family and
      // outcome events (e.g. discontinue succeeded) are emitted backend-side.
      unifiedMedications: {
        cardOpened: (metadata: {
          patient_id: string;
          medication_count: number;
          history_fill_count: number;
          consent_state: 'unavailable' | 'needed' | 'pulling' | 'granted';
          /** DoseSpot rejected the last pull — the stale-refresh warning is showing (GDN-3298). */
          consent_stale?: boolean;
          surface?: string;
        }) => {
          trackEvent(EventNames.UNIFIED_MEDS_CARD_OPENED, { metadata });
        },
        filterChanged: (metadata: {
          patient_id: string;
          filter: 'all' | 'active' | 'inactive';
          surface?: string;
        }) => {
          trackEvent(EventNames.UNIFIED_MEDS_FILTER_CHANGED, { metadata });
        },
        historyToggled: (metadata: {
          patient_id: string;
          medication_id: string;
          expanded: boolean;
          surface?: string;
        }) => {
          trackEvent(EventNames.UNIFIED_MEDS_HISTORY_TOGGLED, { metadata });
        },
        suggestionsSectionToggled: (metadata: {
          patient_id: string;
          expanded: boolean;
          suggested_count: number;
          surface?: string;
        }) => {
          trackEvent(EventNames.UNIFIED_MEDS_SUGGESTIONS_TOGGLED, { metadata });
        },
        addClicked: (metadata: { patient_id: string; surface?: string }) => {
          trackEvent(EventNames.UNIFIED_MEDS_ADD_CLICKED, { metadata });
        },
        editClicked: (metadata: {
          patient_id: string;
          medication_id: string;
          surface?: string;
        }) => {
          trackEvent(EventNames.UNIFIED_MEDS_EDIT_CLICKED, { metadata });
        },
        discontinueClicked: (metadata: {
          patient_id: string;
          medication_id: string;
          surface?: string;
        }) => {
          trackEvent(EventNames.UNIFIED_MEDS_DISCONTINUE_CLICKED, { metadata });
        },
        removeClicked: (metadata: {
          patient_id: string;
          medication_id: string;
          surface?: string;
        }) => {
          trackEvent(EventNames.UNIFIED_MEDS_REMOVE_CLICKED, { metadata });
        },
        manageRxClicked: (metadata: {
          patient_id: string;
          medication_id: string;
          surface?: string;
        }) => {
          trackEvent(EventNames.UNIFIED_MEDS_MANAGE_RX_CLICKED, { metadata });
        },
      },

      // Redesigned diagnoses/allergies cards (GDN-2351) on the V1 patient
      // page. One family, item_type-dimensioned; interaction events only.
      clinicalCards: {
        cardOpened: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'allergy';
          item_count: number;
          surface?: string;
        }) => {
          trackEvent(EventNames.CLINICAL_CARD_OPENED, { metadata });
        },
        filterChanged: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'allergy';
          filter: 'all' | 'active' | 'inactive';
          surface?: string;
        }) => {
          trackEvent(EventNames.CLINICAL_CARD_FILTER_CHANGED, { metadata });
        },
        addClicked: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'allergy';
          surface?: string;
        }) => {
          trackEvent(EventNames.CLINICAL_CARD_ADD_CLICKED, { metadata });
        },
        editClicked: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'allergy';
          item_id: string;
          surface?: string;
        }) => {
          trackEvent(EventNames.CLINICAL_CARD_EDIT_CLICKED, { metadata });
        },
        statusToggled: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'allergy';
          item_id: string;
          to_active: boolean;
          surface?: string;
        }) => {
          trackEvent(EventNames.CLINICAL_CARD_STATUS_TOGGLED, { metadata });
        },
        suggestionSelected: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'allergy';
          item_id: string;
          action: 'accept' | 'dismiss';
          surface?: string;
        }) => {
          trackEvent(EventNames.CLINICAL_CARD_SUGGESTION_SELECTED, { metadata });
        },
        suggestionsSectionToggled: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'allergy';
          expanded: boolean;
          suggested_count: number;
          surface?: string;
        }) => {
          trackEvent(EventNames.CLINICAL_CARD_SUGGESTIONS_TOGGLED, { metadata });
        },
      },

      // Patient context bar metrics
      patientContextBar: {
        popoverViewed: (metadata: {
          patient_id: string;
          indicator_type:
            | 'medications'
            | 'diagnoses'
            | 'allergies'
            | 'last_encounter'
            | 'treatment_plan'
            | 'prior_authorization';
          item_count?: number;
          source: string;
        }) => {
          trackEvent(EventNames.PATIENT_CONTEXT_BAR_POPOVER_VIEWED, {
            metadata: {
              ...metadata,
              ...(metadata.item_count !== undefined && {
                item_count: String(metadata.item_count),
              }),
            },
          });
        },
        // Treatment plan emits its own `patient_treatment_plan_opened` /
        // `_closed` events (see patientTreatmentPlan below); don't include
        // 'treatment_plan' in this union.
        editModalOpened: (metadata: {
          patient_id: string;
          data_type: 'medications' | 'diagnoses' | 'allergies';
          item_count: number;
          source: string;
        }) => {
          trackEvent(EventNames.PATIENT_CONTEXT_BAR_EDIT_MODAL_OPENED, {
            metadata: {
              ...metadata,
              item_count: String(metadata.item_count),
            },
          });
        },
        editModalClosed: (metadata: {
          patient_id: string;
          data_type: 'medications' | 'diagnoses' | 'allergies';
          had_changes: boolean;
          source: string;
        }) => {
          trackEvent(EventNames.PATIENT_CONTEXT_BAR_EDIT_MODAL_CLOSED, {
            metadata: {
              ...metadata,
              had_changes: String(metadata.had_changes),
            },
          });
        },
        editFromPopoverClicked: (metadata: {
          patient_id: string;
          data_type: 'medications' | 'diagnoses' | 'allergies' | 'treatment_plan';
          source: string;
        }) => {
          trackEvent(EventNames.PATIENT_CONTEXT_BAR_EDIT_FROM_POPOVER_CLICKED, {
            metadata,
          });
        },
        viewNoteClicked: (metadata: { patient_id: string; note_id: string; source: string }) => {
          trackEvent(EventNames.PATIENT_CONTEXT_BAR_VIEW_NOTE_CLICKED, {
            metadata,
          });
        },
        patientNameClicked: (metadata: { patient_id: string; source: string }) => {
          trackEvent(EventNames.PATIENT_CONTEXT_BAR_PATIENT_NAME_CLICKED, {
            metadata,
          });
        },
        patientDeselected: (metadata: { patient_id: string; source: string }) => {
          trackEvent(EventNames.PATIENT_CONTEXT_BAR_PATIENT_DESELECTED, {
            metadata,
          });
        },
        // Treatment plan emits its own patient_treatment_plan_save_failed;
        // don't include 'treatment_plan' in this union.
        saveFailed: (metadata: {
          patient_id: string;
          data_type: 'medications' | 'diagnoses' | 'allergies';
          error_message: string;
          source: string;
        }) => {
          trackEvent(EventNames.PATIENT_CONTEXT_BAR_SAVE_FAILED, {
            metadata,
          });
        },
        dismissed: (metadata: { encounter_id?: string; source: string }) => {
          trackEvent(EventNames.PATIENT_CONTEXT_BAR_DISMISSED, {
            metadata,
          });
        },
        restored: (metadata: {
          encounter_id?: string;
          source: string;
          had_note_title?: boolean;
        }) => {
          trackEvent(EventNames.PATIENT_CONTEXT_BAR_RESTORED, {
            metadata: {
              ...metadata,
              ...(metadata.had_note_title !== undefined && {
                had_note_title: String(metadata.had_note_title),
              }),
            },
          });
        },
      },

      // Treatment plan modal events. Fires from inside PatientTreatmentPlanEditModal
      // so every open path (context bar, patient-page server-UI dispatch, etc.)
      // produces a matching opened/closed pair attributable by `source`.
      patientTreatmentPlan: {
        opened: (metadata: { patient_id: string; item_count: number; source: string }) => {
          trackEvent(EventNames.PATIENT_TREATMENT_PLAN_OPENED, {
            metadata: {
              ...metadata,
              item_count: String(metadata.item_count),
            },
          });
        },
        closed: (metadata: { patient_id: string; had_changes: boolean; source: string }) => {
          trackEvent(EventNames.PATIENT_TREATMENT_PLAN_CLOSED, {
            metadata: {
              ...metadata,
              had_changes: String(metadata.had_changes),
            },
          });
        },
        saveFailed: (metadata: { patient_id: string; error_message: string; source: string }) => {
          trackEvent(EventNames.PATIENT_TREATMENT_PLAN_SAVE_FAILED, {
            metadata,
          });
        },
        importClicked: (metadata: { patient_id: string; source: string }) => {
          trackEvent(EventNames.PATIENT_TREATMENT_PLAN_IMPORT_CLICKED, {
            metadata,
          });
        },
        importCompleted: (metadata: { patient_id: string; source: string }) => {
          trackEvent(EventNames.PATIENT_TREATMENT_PLAN_IMPORT_COMPLETED, {
            metadata,
          });
        },
        importFailed: (metadata: { patient_id: string; error_message: string; source: string }) => {
          trackEvent(EventNames.PATIENT_TREATMENT_PLAN_IMPORT_FAILED, {
            metadata,
          });
        },
      },

      // Document metrics - for patient documents and generated documents
      document: {
        viewed: (
          documentId: string,
          metadata?: {
            document_type?: string;
            file_size?: number;
            patient_id?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DOCUMENT_VIEWED, {
            metadata: {
              document_id: documentId,
              ...(metadata?.file_size && { file_size: String(metadata.file_size) }),
              ...metadata,
            },
          });
        },
        uploaded: (metadata: {
          document_type: string;
          file_size_kb?: number;
          file_format?: string;
          source: 'encounter' | 'patient_page' | 'toolbar' | 'other';
        }) => {
          trackEvent(EventNames.PATIENT_DOCUMENT_UPLOADED, {
            metadata: {
              ...metadata,
              ...(metadata.file_size_kb && { file_size_kb: String(metadata.file_size_kb) }),
            },
          });
        },
        created: (metadata: {
          document_type:
            | 'lab_order'
            | 'claim'
            | 'form'
            | 'generated'
            | 'encounter_note'
            | 'follow_up'
            | 'other';
          source:
            | 'encounter'
            | 'lab_order_modal'
            | 'claim_view'
            | 'form_builder'
            | 'toolbar'
            | 'documents_page'
            | 'other';
          generation_method?: 'ai_generated' | 'template' | 'manual' | 'imported';
        }) => {
          trackEvent(EventNames.PATIENT_DOCUMENT_CREATED, { metadata });
        },
        deleted: (
          documentId: string,
          metadata?: {
            document_type?: string;
            document_age_days?: number;
            source?: 'patient_page' | 'encounter' | 'bulk_action';
          },
        ) => {
          trackEvent(EventNames.PATIENT_DOCUMENT_DELETED, {
            metadata: {
              document_id: documentId,
              ...metadata,
              ...(metadata?.document_age_days && {
                document_age_days: String(metadata.document_age_days),
              }),
            },
          });
        },
        filtered: (metadata: {
          filter_type: string;
          documents_shown: number;
          total_documents?: number;
        }) => {
          trackEvent(EventNames.PATIENT_DOCUMENT_FILTERED, {
            metadata: {
              ...metadata,
              documents_shown: String(metadata.documents_shown),
              ...(metadata.total_documents && {
                total_documents: String(metadata.total_documents),
              }),
            },
          });
        },
        bulkAction: (metadata: {
          action_type: 'download_all' | 'delete_selected' | 'share_selected' | 'export';
          documents_count: number;
          document_types?: string[];
        }) => {
          trackEvent(EventNames.PATIENT_DOCUMENT_BULK_ACTION, {
            metadata: {
              ...metadata,
              documents_count: String(metadata.documents_count),
              ...(metadata.document_types && { document_types: metadata.document_types.join(',') }),
            },
          });
        },

        // Document list operations
        listLoaded: (metadata: { total_documents: number; document_types?: string[] }) => {
          trackEvent(EventNames.DOCUMENT_LIST_LOADED, {
            metadata: {
              total_documents: String(metadata.total_documents),
              ...(metadata.document_types && { document_types: metadata.document_types.join(',') }),
            },
          });
        },

        paginationChanged: (metadata: {
          new_page: number;
          rows_per_page: number;
          total_pages?: number;
          source?: string;
        }) => {
          trackEvent(EventNames.DOCUMENT_PAGINATION_CHANGED, {
            metadata: {
              new_page: String(metadata.new_page),
              rows_per_page: String(metadata.rows_per_page),
              ...(metadata.total_pages && { total_pages: String(metadata.total_pages) }),
              ...(metadata.source && { source: metadata.source }),
            },
          });
        },

        // Document Pages - new document viewer pages
        pageViewed: (metadata: {
          page_type: 'documents' | 'labs' | 'forms' | 'sent_documents';
          document_count?: number;
          has_filters?: boolean;
        }) => {
          const eventName =
            metadata.page_type === 'labs'
              ? EventNames.LABS_PAGE_VIEWED
              : metadata.page_type === 'forms'
                ? EventNames.FORMS_PAGE_VIEWED
                : metadata.page_type === 'sent_documents'
                  ? EventNames.SENT_DOCUMENTS_PAGE_VIEWED
                  : EventNames.DOCUMENTS_PAGE_VIEWED;
          trackEvent(eventName, {
            metadata: {
              page_type: metadata.page_type,
              ...(metadata.document_count !== undefined && {
                document_count: String(metadata.document_count),
              }),
              ...(metadata.has_filters !== undefined && {
                has_filters: String(metadata.has_filters),
              }),
            },
          });
        },

        rowClicked: (metadata: {
          document_id?: string;
          document_type?: string;
          page_type: 'documents' | 'labs' | 'forms' | 'sent_documents';
          has_patient?: boolean;
        }) => {
          trackEvent(EventNames.DOCUMENTS_ROW_CLICKED, {
            metadata: {
              ...(metadata.document_id && { document_id: metadata.document_id }),
              page_type: metadata.page_type,
              ...(metadata.document_type && { document_type: metadata.document_type }),
              ...(metadata.has_patient !== undefined && {
                has_patient: String(metadata.has_patient),
              }),
            },
          });
        },

        searchPerformed: (metadata: {
          query_length: number;
          results_count: number;
          page_type: 'documents' | 'labs' | 'forms';
        }) => {
          trackEvent(EventNames.DOCUMENTS_SEARCH_PERFORMED, {
            metadata: {
              query_length: String(metadata.query_length),
              results_count: String(metadata.results_count),
              page_type: metadata.page_type,
            },
          });
        },

        filterApplied: (metadata: {
          filter_type: 'document_type' | 'date_range' | 'search' | 'clear';
          page_type: 'documents' | 'labs' | 'forms';
          filter_value?: string;
          results_count?: number;
        }) => {
          trackEvent(EventNames.DOCUMENTS_FILTER_APPLIED, {
            metadata: {
              filter_type: metadata.filter_type,
              page_type: metadata.page_type,
              ...(metadata.filter_value && { filter_value: metadata.filter_value }),
              ...(metadata.results_count !== undefined && {
                results_count: String(metadata.results_count),
              }),
            },
          });
        },

        sentDocumentsFilterChanged: (metadata: {
          filter_type:
            | 'patient'
            | 'channel'
            | 'status'
            | 'communication_type'
            | 'date_range'
            | 'clear';
          filter_value?: string;
          active_filter_count?: number;
        }) => {
          trackEvent(EventNames.SENT_DOCUMENTS_FILTER_CHANGED, {
            metadata: {
              filter_type: metadata.filter_type,
              ...(metadata.filter_value && { filter_value: metadata.filter_value }),
              ...(metadata.active_filter_count !== undefined && {
                active_filter_count: String(metadata.active_filter_count),
              }),
            },
          });
        },

        sortChanged: (metadata: {
          sort_field: string;
          sort_order: 'asc' | 'desc';
          page_type: 'documents' | 'labs' | 'forms' | 'sent_documents';
        }) => {
          trackEvent(EventNames.DOCUMENTS_SORT_CHANGED, {
            metadata: {
              sort_field: metadata.sort_field,
              sort_order: metadata.sort_order,
              page_type: metadata.page_type,
            },
          });
        },

        mobileViewOpened: (metadata: { page_type: 'labs' | 'forms' }) => {
          trackEvent(EventNames.DOCUMENTS_MOBILE_VIEW_OPENED, {
            metadata: {
              page_type: metadata.page_type,
            },
          });
        },

        mobileViewClosed: (metadata: {
          page_type: 'labs' | 'forms';
          had_patient_selected?: boolean;
          action_taken?: boolean;
        }) => {
          trackEvent(EventNames.DOCUMENTS_MOBILE_VIEW_CLOSED, {
            metadata: {
              page_type: metadata.page_type,
              ...(metadata.had_patient_selected !== undefined && {
                had_patient_selected: String(metadata.had_patient_selected),
              }),
              ...(metadata.action_taken !== undefined && {
                action_taken: String(metadata.action_taken),
              }),
            },
          });
        },

        downloaded: (metadata: {
          document_id: string;
          document_type?: string;
          page_type: 'documents' | 'labs' | 'forms' | 'sent_documents';
          file_size?: number;
        }) => {
          trackEvent(EventNames.DOCUMENT_DOWNLOADED, {
            metadata: {
              document_id: metadata.document_id,
              page_type: metadata.page_type,
              ...(metadata.document_type && { document_type: metadata.document_type }),
              ...(metadata.file_size && { file_size: String(metadata.file_size) }),
            },
          });
        },

        templateLibraryViewed: (metadata: { template_count: number }) => {
          trackEvent(EventNames.DOCUMENT_TEMPLATE_LIBRARY_VIEWED, {
            metadata: {
              template_count: String(metadata.template_count),
            },
          });
        },

        templateSelected: (metadata: {
          template_id: string;
          template_name: string;
          page_type: 'documents';
        }) => {
          trackEvent(EventNames.DOCUMENT_TEMPLATE_SELECTED, {
            metadata: {
              template_id: metadata.template_id,
              template_name: metadata.template_name,
              page_type: metadata.page_type,
            },
          });
        },

        templateDeleted: (metadata: { template_id: string; template_name: string }) => {
          trackEvent(EventNames.DOCUMENT_TEMPLATE_DELETED, {
            metadata: {
              template_id: metadata.template_id,
              template_name: metadata.template_name,
            },
          });
        },

        generateInitiated: (metadata: {
          method: 'prompt' | 'template' | 'suggested';
          has_patient: boolean;
          prompt_length?: number;
          template_name?: string;
        }) => {
          trackEvent(EventNames.DOCUMENT_GENERATE_INITIATED, {
            metadata: {
              method: metadata.method,
              has_patient: String(metadata.has_patient),
              ...(metadata.prompt_length && { prompt_length: String(metadata.prompt_length) }),
              ...(metadata.template_name && { template_name: metadata.template_name }),
            },
          });
        },
      },

      // PDF operations
      pdf: {
        loadPerformance: (metadata: {
          file_size_kb?: number;
          load_time_ms: number;
          viewer_type: 'inline' | 'modal' | 'new_tab';
          page_count?: number;
          document_type?: string;
        }) => {
          trackEvent(EventNames.PDF_LOAD_PERFORMANCE, {
            metadata: {
              load_time_ms: String(metadata.load_time_ms),
              viewer_type: metadata.viewer_type,
              ...(metadata.file_size_kb && { file_size_kb: String(metadata.file_size_kb) }),
              ...(metadata.page_count && { page_count: String(metadata.page_count) }),
              ...(metadata.document_type && { document_type: metadata.document_type }),
            },
          });
        },

        interaction: (metadata: {
          interaction_type: 'view' | 'download' | 'print' | 'send';
          document_type?: string;
          pages_viewed?: number;
          actions_taken?: string[];
        }) => {
          trackEvent(EventNames.DOCUMENT_INTERACTION, {
            metadata: {
              interaction_type: metadata.interaction_type,
              ...(metadata.document_type && { document_type: metadata.document_type }),
              ...(metadata.pages_viewed && { pages_viewed: String(metadata.pages_viewed) }),
              ...(metadata.actions_taken && { actions_taken: metadata.actions_taken.join(',') }),
            },
          });
        },

        printExport: (metadata: {
          format: 'pdf';
          page_count?: number;
          destination: 'printer' | 'file';
          document_type?: string;
        }) => {
          trackEvent(EventNames.DOCUMENT_PRINT_EXPORT, {
            metadata: {
              format: metadata.format,
              destination: metadata.destination,
              ...(metadata.page_count && { page_count: String(metadata.page_count) }),
              ...(metadata.document_type && { document_type: metadata.document_type }),
            },
          });
        },

        error: (metadata: {
          error_type: 'corrupt' | 'too_large' | 'unsupported' | 'network' | 'load_failed';
          file_size_kb?: number;
          document_type?: string;
          error_message?: string;
        }) => {
          trackEvent(EventNames.DOCUMENT_ERROR, {
            metadata: {
              error_type: metadata.error_type,
              ...(metadata.file_size_kb && { file_size_kb: String(metadata.file_size_kb) }),
              ...(metadata.document_type && { document_type: metadata.document_type }),
              ...(metadata.error_message && { error_message: metadata.error_message }),
            },
          });
        },
      },

      // Forms metrics
      forms: {
        create: (metadata?: { form_scope?: string }) => {
          trackEvent(EventNames.FORM_CREATED, { metadata });
        },
        clone: (
          formId: string,
          metadata?: {
            source_scope?: string;
            target_scope?: string;
            source_form_id?: string;
          },
        ) => {
          trackEvent(EventNames.FORM_CLONED, {
            metadata: { form_id: formId, ...metadata },
          });
        },
        delete: (
          formId: string,
          metadata?: {
            form_scope?: string;
            is_published?: boolean;
          },
        ) => {
          trackEvent(EventNames.FORM_DELETED, {
            metadata: {
              form_id: formId,
              ...(metadata?.is_published !== undefined && {
                is_published: String(metadata.is_published),
              }),
              ...metadata,
            },
          });
        },
        deleteDraft: (
          formId: string,
          metadata?: {
            form_version?: number;
          },
        ) => {
          trackEvent(EventNames.FORM_DRAFT_DELETED, {
            metadata: {
              form_id: formId,
              ...(metadata?.form_version && {
                form_version: String(metadata.form_version),
              }),
              ...metadata,
            },
          });
        },
        publish: (
          formId: string,
          metadata?: {
            form_scope?: string;
            is_update?: boolean;
            field_count?: number;
            version?: number;
          },
        ) => {
          trackEvent(EventNames.FORM_PUBLISHED, {
            metadata: {
              form_id: formId,
              ...(metadata?.is_update !== undefined && {
                is_update: String(metadata.is_update),
              }),
              ...(metadata?.field_count && {
                field_count: String(metadata.field_count),
              }),
              ...(metadata?.version && {
                version: String(metadata.version),
              }),
              ...metadata,
            },
          });
        },
        send: (
          formId: string,
          metadata?: {
            patient_id?: string;
            encounter_id?: string;
            delivery_channels?: string[];
            forms_count?: number;
            // Where the send flow was opened from. Used to measure conversion
            // funnels (e.g. ghost CTA -> form_sent).
            source?: string;
          },
        ) => {
          trackEvent(EventNames.FORM_SENT, {
            metadata: {
              form_id: formId,
              ...(metadata?.forms_count && {
                forms_count: String(metadata.forms_count),
              }),
              ...metadata,
            },
          });
        },
        view: (
          formId: string,
          metadata?: {
            context?: 'preview' | 'builder' | 'list';
            is_draft?: boolean;
          },
        ) => {
          trackEvent(EventNames.FORM_VIEWED, {
            metadata: {
              form_id: formId,
              ...(metadata?.is_draft !== undefined && {
                is_draft: String(metadata.is_draft),
              }),
              ...metadata,
            },
          });
        },
      },

      // Billing metrics
      billing: {
        /**
         * Provider changes the independent claim-problem facet on the JotBill
         * encounter worklist. Values are a fixed vocabulary and contain no
         * claim or patient identifiers.
         */
        worklistClaimProblemFilterChanged: (metadata: {
          claim_problem:
            | 'none'
            | 'any_problem'
            | 'rejected_or_denied'
            | 'rejected'
            | 'denied'
            | 'scrub_failed'
            | 'reversal_or_recoupment'
            | 'unmatched_remittance';
        }) => {
          trackEvent(EventNames.JOTBILL_WORKLIST_CLAIM_PROBLEM_FILTER_CHANGED, {
            metadata,
          });
        },
        claimWorkbenchEntered: (metadata: {
          claim_id: string;
          claim_status: string;
          gate_verdict: 'no_claim' | 'ready' | 'blocked' | 'post_submission' | 'busy';
          annotation_error_count: number;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_ENTERED, {
            metadata: {
              ...metadata,
              annotation_error_count: String(metadata.annotation_error_count),
            },
          });
        },
        claimWorkbenchFamilyClaimSelected: (metadata: {
          claim_id: string;
          target_claim_id: string;
          family_size: number;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_FAMILY_CLAIM_SELECTED, {
            metadata: { ...metadata, family_size: String(metadata.family_size) },
          });
        },
        /**
         * `source` is HOW the finding was reached, not which surface shows
         * it: the rail row, the flag chip and the drawer are one object, and
         * `[` / `]` reach the same selection from the keyboard (GDN-3345).
         */
        claimWorkbenchFindingSelected: (metadata: {
          claim_id: string;
          finding_key: string;
          source: 'click' | 'keyboard';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_FINDING_SELECTED, { metadata });
        },
        claimWorkbenchFieldEditSubmitted: (metadata: { claim_id: string; field_kind: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_FIELD_EDIT_SUBMITTED, { metadata });
        },
        /**
         * The biller moved the claim to a different place of service, and the
         * edit reached the server (GDN-3344). Fires alongside the field-level
         * event, not instead of it: this one carries WHICH code was chosen,
         * which is what the grouped picker exists to make answerable.
         *
         * `code` is the bare CMS code (`11`, `02`), never the label and never
         * anything else off the claim.
         */
        claimWorkbenchPlaceOfServiceChanged: (metadata: { claim_id: string; code: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_PLACE_OF_SERVICE_CHANGED, { metadata });
        },
        claimWorkbenchEligibilityRecheckRequested: (metadata: { claim_id: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_ELIGIBILITY_RECHECK_REQUESTED, {
            metadata,
          });
        },
        /**
         * The biller opened the note beside the claim.
         *
         * `source` carries BOTH which entry point was used and, for the
         * encounter card, which surface answered: below the pinned column's
         * width the card opens the expanded overlay pane instead
         * ('encounter_card_narrow'). One event, because it is one thing
         * happening.
         *
         * There was a fourth source, 'expand' — the pinned column grown into
         * that same pane. GDN-3343 removed the affordance (Jackson: the
         * fullscreen view was not adding much), so the value is gone with it.
         */
        claimWorkbenchNotePanelOpened: (metadata: {
          claim_id: string;
          source: 'excerpt' | 'encounter_card' | 'encounter_card_narrow';
          /**
           * Click or the `n` key (GDN-3345). Named `via` and not `source`
           * because `source` here already means the ENTRY POINT, and has
           * since this event shipped; renaming it would break every existing
           * query for the sake of one new dimension. Two questions, two
           * fields.
           */
          via: 'click' | 'keyboard';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_NOTE_PANEL_OPENED, { metadata });
        },
        /** The biller followed the note panel's open-in-new-tab link. */
        claimWorkbenchNoteOpenInNewTabClicked: (metadata: {
          claim_id: string;
          note_id: string;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_NOTE_OPEN_IN_NEW_TAB_CLICKED, { metadata });
        },
        /**
         * Biller flipped the note panel between the signed note and the
         * encounter transcript (GDN-3478). `view` is the one moved TO, so
         * both directions report and neither has to be inferred from the
         * absence of the other.
         */
        claimWorkbenchNoteTranscriptClicked: (metadata: {
          claim_id: string;
          note_id: string;
          view: 'transcript' | 'note';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_NOTE_TRANSCRIPT_CLICKED, { metadata });
        },
        /** Biller SELECTED the Form tab — fired on selection, never on render. */
        claimWorkbenchFormTabOpened: (metadata: {
          claim_id?: string;
          has_claim: boolean;
          source: 'click' | 'keyboard';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_FORM_TAB_OPENED, { metadata });
        },
        claimWorkbenchShortcutsToggled: (metadata: { enabled: boolean }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_SHORTCUTS_TOGGLED, { metadata });
        },
        /**
         * The biller opened the "Keyboard" legend (GDN-3345), from the
         * pipeline strip's button or from `?`.
         *
         * The adoption meter for the shortcuts themselves: nothing else on
         * this page can report that a keystroke happened without also
         * reporting the action it ran, and a biller who opens this list is
         * the one looking for keys to learn. `source` says which of the two
         * doors they came through, which is the question of whether the
         * button is discoverable or only the key is.
         */
        claimWorkbenchShortcutsOpened: (metadata: {
          claim_id?: string;
          source: 'click' | 'keyboard';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_SHORTCUTS_OPENED, { metadata });
        },
        claimWorkbenchFindingReopenRequested: (metadata: {
          claim_id: string;
          finding_key: string;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_FINDING_REOPEN_REQUESTED, { metadata });
        },
        /**
         * CLAIM QUEUES (GDN-3322). `position`/`total` are the run's own
         * numbers at the moment of the interaction — stringified, like every
         * other numeric dimension here. No queue label and no view name: the
         * label is the practice's own words for a saved view and can be free
         * text, which is not something telemetry should carry.
         *
         * Skipping is what these three measure against each other: Next/Prev
         * are the biller stepping past a claim without resolving it, and the
         * advance is one that resolved. The ratio is whether the run is
         * working.
         */
        claimWorkbenchQueueNextClicked: (metadata: {
          claim_id: string;
          position: number;
          /** The rail's control, or `j` (GDN-3345). */
          source: 'click' | 'keyboard';
          total: number;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_QUEUE_NEXT_CLICKED, {
            metadata: {
              claim_id: metadata.claim_id,
              position: String(metadata.position),
              source: metadata.source,
              total: String(metadata.total),
            },
          });
        },
        claimWorkbenchQueuePreviousClicked: (metadata: {
          claim_id: string;
          position: number;
          /** The rail's control, or `k` (GDN-3345). */
          source: 'click' | 'keyboard';
          total: number;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_QUEUE_PREVIOUS_CLICKED, {
            metadata: {
              claim_id: metadata.claim_id,
              position: String(metadata.position),
              source: metadata.source,
              total: String(metadata.total),
            },
          });
        },
        /**
         * An action resolved this claim's place in the run and the page is
         * moving on. `trigger` is the fixed vocabulary of the advance rule —
         * the four outcomes that resolve a queue place — so "which action is
         * actually clearing queues" is a group-by rather than a join across
         * four event names.
         */
        claimWorkbenchQueueAdvanceInitiated: (metadata: {
          claim_id: string;
          position: number;
          total: number;
          /* GDN-3380 renamed the disposition: 'dismiss' is now 'archive'. The
             event name and every other field are unchanged, so the funnel
             keeps its history and only the value moves. */
          trigger: 'submit' | 'archive' | 'snooze' | 'delete' | 'restore';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_QUEUE_ADVANCE_INITIATED, {
            metadata: {
              claim_id: metadata.claim_id,
              position: String(metadata.position),
              total: String(metadata.total),
              trigger: metadata.trigger,
            },
          });
        },
        /**
         * The rail's first crumb — the way out of a run. `handled` is what the
         * biller resolved before leaving (0 when they left mid-run), which is
         * the abandonment signal: returns with a low `handled` against a high
         * `position` are runs being walked rather than worked.
         */
        claimWorkbenchQueueReturnClicked: (metadata: {
          claim_id: string;
          handled: number;
          position: number;
          /** The rail's first crumb, or Esc with nothing left open (GDN-3345). */
          source: 'click' | 'keyboard';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_QUEUE_RETURN_CLICKED, {
            metadata: {
              claim_id: metadata.claim_id,
              handled: String(metadata.handled),
              position: String(metadata.position),
              source: metadata.source,
            },
          });
        },
        /**
         * The Patient card's Edit affordance — the biller leaving the claim
         * for the record behind it. Counts as its own workflow: it is the
         * route taken when a field cannot be fixed on the claim, so its
         * volume against inline edits says whether the card's own editors
         * are covering what billers actually need.
         */
        claimWorkbenchPatientRecordOpened: (metadata: {
          claim_id: string;
          highlighted_field_count: number;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_PATIENT_RECORD_OPENED, { metadata });
        },
        /**
         * The Coverage card's Edit affordance — the source peek onto the
         * coverage record. Tracked separately from the patient record's:
         * the two cards answer to different sources, and the point of
         * landing the peek on Coverage first is finding out whether a
         * biller reaches for the record or stays on the card.
         */
        claimWorkbenchCoverageRecordOpened: (metadata: {
          claim_id: string;
          highlighted_field_count: number;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_COVERAGE_RECORD_OPENED, { metadata });
        },
        /** Opens the source authorization record without sending its value. */
        claimWorkbenchAuthorizationRecordOpened: (metadata: {
          claim_id: string;
          lookup_state: string;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_AUTHORIZATION_RECORD_OPENED, {
            metadata,
          });
        },
        /** Records rebuild intent; the server owns the durable outcome. */
        claimWorkbenchAuthorizationRebuildRequested: (metadata: {
          claim_id: string;
          snapshot_state: string;
          source: 'after_save' | 'manual_retry';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_AUTHORIZATION_REBUILD_REQUESTED, {
            metadata,
          });
        },
        /** Retries the shared authorization context read without sending source values. */
        claimWorkbenchAuthorizationLookupRetryClicked: (metadata: { claim_id: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_AUTHORIZATION_LOOKUP_RETRY_CLICKED, {
            metadata,
          });
        },
        /**
         * The Billing & money card's own `Edit` (GDN-3162) — the third and
         * last card-level source peek, and the one that did not exist:
         * until now the practice's billing identity was reachable only from
         * a finding drawer, so only a biller who already had a billing
         * blocker could get to it.
         *
         * Tracked separately from `..._source_record_opened`, which starts
         * from a specific finding. The pair answers the question the card
         * button was added for: does a biller correct a billing identity
         * BEFORE it fails a rule, or only once something is already flagged.
         *
         * No `highlighted_field_count` sibling here — unlike the patient and
         * coverage peeks, this modal highlights nothing, and a field the
         * card cannot point at is not a count worth inventing.
         */
        claimWorkbenchBillingRecordOpened: (metadata: { claim_id: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_BILLING_RECORD_OPENED, { metadata });
        },
        /**
         * A biller copied one of the claim's reference numbers off the
         * Billing & money card (GDN-3181).
         *
         * These are the identifiers that get retyped into a payer portal or
         * read down a phone line, and a transposed character sends the
         * lookup nowhere. Volume here says whether the affordance is load-
         * bearing, and `reference` says WHICH number carries the work —
         * which is the input to whether the claim number needs a friendlier
         * form than the raw id it falls back to.
         *
         * `reference` is a field name, never the value: a claim control
         * number is an identifier, and identifiers do not belong in
         * telemetry.
         */
        claimWorkbenchReferenceCopied: (metadata: {
          claim_id: string;
          reference: 'claim' | 'payer_claim';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_REFERENCE_COPIED, { metadata });
        },
        /**
         * A coverage edit saved, but rebuilding the claim against it did
         * not. The biller keeps a verdict judged on the OLD coverage while
         * the record already shows the new one, so the two disagree until
         * something else rebuilds. Silent staleness is exactly what needs
         * to be countable.
         */
        claimWorkbenchCoverageRebuildFailed: (metadata: {
          claim_id: string;
          error_message: string;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_COVERAGE_REBUILD_FAILED, { metadata });
        },
        claimWorkbenchRecheckRequested: (metadata: {
          claim_id: string;
          /** The verdict card's button, or `r` (GDN-3345). */
          source: 'click' | 'keyboard';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_RECHECK_REQUESTED, { metadata });
        },
        claimWorkbenchRouteToOwnerClicked: (metadata: {
          claim_id: string;
          finding_key: string;
          policy_id: string;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_ROUTE_TO_OWNER_CLICKED, { metadata });
        },
        /**
         * The finding drawer's "Open source record" — the biller opening the
         * record that OWNS a flagged field (patient, coverage, the
         * practice's billing identity, or the rendering clinician's own
         * member billing profile) over the claim. Distinct from the
         * Patient/Coverage cards' own `Edit`: those are card-level, this
         * starts from one specific finding, so `destination` says which
         * record a finding actually sent them to, and its mix says which
         * source records the workbench most needs to reach.
         */
        claimWorkbenchSourceRecordOpened: (metadata: {
          claim_id: string;
          finding_key: string;
          destination: 'patient' | 'coverage' | 'billing' | 'member';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_SOURCE_RECORD_OPENED, { metadata });
        },
        /**
         * The other half of that funnel: the biller actually SUBMITTING the
         * clinician's NPI from the member billing modal a Box 24J blocker
         * opened. Paired with `..._source_record_opened` on
         * claim_id/finding_key, it answers the question opening alone
         * cannot — whether reaching the source record resolves the finding
         * or billers arrive and bounce.
         *
         * Interaction only, fired once per submission that actually landed
         * — after the PATCH resolves, never when it is merely sent. A value
         * the field rejected never reached the server, and one the server
         * refused changed nothing on the record; counting either would
         * inflate the side of the funnel this exists to measure. Neither the
         * validation rejection nor an API failure is an event: per the
         * repo's suffix allowlist, `_failed`/`_error` are silently dropped
         * by the Metabase pipeline, and failures are not interactions. The
         * inline field error and the rebuild-failure banner are the failure
         * surface; the outcome belongs to a BE product event.
         *
         * `destination` is constant here by construction; it is carried so
         * this joins the source-record family on one shared key rather than
         * needing a special case in every query.
         */
        claimWorkbenchMemberBillingInfoSubmitted: (metadata: {
          claim_id: string;
          finding_key: string;
          destination: 'member';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_MEMBER_BILLING_INFO_SUBMITTED, {
            metadata,
          });
        },
        /**
         * A biller clicked "Create secondary claim" and got the COB balance
         * preview instead of an immediate, irreversible create (GDN-3163).
         * Fired at CLICK time, before the preview GET resolves — the click
         * is the intent, and gating it on the response would under-count
         * exactly the slow/failed fetches worth knowing about.
         */
        claimWorkbenchSecondaryPreviewOpened: (metadata: { claim_id: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_SECONDARY_PREVIEW_OPENED, { metadata });
        },
        /**
         * The other half: the biller confirming the create from inside the
         * dialog, which the preview's three gates (secondary coverage on
         * file, remittance posted, claim balances) have to have cleared for
         * the button to be live at all.
         *
         * Paired with `..._secondary_preview_opened` on `claim_id`, the gap
         * between the two IS the safety net's yield — how many secondary
         * claims the preview stopped that would previously have gone to the
         * payer on one click. That is why no `_blocked` event exists: the
         * block is not an interaction, and per the repo's suffix allowlist
         * it would be dropped by the pipeline anyway. Subtraction answers it.
         */
        claimWorkbenchSecondaryClaimSubmitted: (metadata: { claim_id: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_SECONDARY_CLAIM_SUBMITTED, { metadata });
        },
        /**
         * The claim is being rebuilt from the records it was built from,
         * because the server reported those records had moved (GDN-3277).
         *
         * Fired on INTENT, before the POST resolves — the same convention as
         * `..._secondary_preview_opened`, and for the same reason: gating on
         * the response would under-count exactly the slow and failed rebuilds
         * worth knowing about.
         *
         * `trigger` is what this whole ticket turns on: `auto` is the page
         * handling it before the biller ever saw a phantom blocker, `manual`
         * is a biller who had to be told. The ratio between them is the
         * feature's own success measure. `sources` is the closed set of
         * record KINDS the server named (patient/coverage/encounter/
         * billing_entity), comma-joined — record kinds, never record values,
         * so nothing here is PHI.
         */
        claimWorkbenchSourceRebuildInitiated: (metadata: {
          claim_id: string;
          trigger: 'auto' | 'manual';
          sources: string;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_SOURCE_REBUILD_INITIATED, { metadata });
        },
        /**
         * The biller closed the "Updated from its records" receipt (GDN-3341).
         *
         * The receipt is deliberately untimed — the rebuild fires on arrival,
         * often before anyone looks, and "figures and codes below may have
         * moved" must not vanish unread — so this is how we learn whether it
         * outstays its welcome. `claim_id` is a UUID; no PHI.
         */
        claimWorkbenchSourceNoticeClosed: (metadata: { claim_id: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_SOURCE_NOTICE_CLOSED, { metadata });
        },
        /* JotBill Home (GDN-3379). Home is the arrival surface, so these two
           are the adoption pair: how often billers land here, and how often
           the landing turns into work. `queue` is one of four closed enum
           keys; neither event carries an id, let alone PHI. */
        homeOpened: () => {
          trackEvent(EventNames.BILLING_HOME_OPENED);
        },
        homeQueueSelected: (metadata: {
          queue: string;
          /* GDN-3201's hit-rate meter, on Home: whether the claim's workbench
             reads were already warm when the click landed — by row hover, by
             the "Work this queue" button, or by the arrival warm of each
             queue's first step. Boolean, not an id; no PHI. */
          prefetched: boolean;
        }) => {
          trackEvent(EventNames.BILLING_HOME_QUEUE_SELECTED, {
            metadata: { ...metadata, prefetched: String(metadata.prefetched) },
          });
        },
        /* The hover detail on an Issues/Findings or Reason cell opened (GDN-3379):
           once per open, from Home or the Claims tab. No identifiers. */
        issueDetailOpened: (metadata: {
          kind: 'issues' | 'reason';
          surface: 'home' | 'claims';
        }) => {
          trackEvent(EventNames.BILLING_ISSUE_DETAIL_OPENED, { metadata });
        },
        /* Canonical claim worklist (feature_jotbill_claim_workbench).
           Queue/view keys, sort keys, and filter field ids are closed enums;
           saved-view ids are UUIDs — no PHI in any of these. */
        claimWorklistWorkViewSelected: (metadata: { work_view: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKLIST_WORK_VIEW_SELECTED, { metadata });
        },
        claimWorklistSavedViewSelected: (metadata: { saved_view_id: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKLIST_SAVED_VIEW_SELECTED, { metadata });
        },
        claimWorklistSavedViewSubmitted: (metadata: {
          saved_view_id: string;
          action: 'create' | 'update';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKLIST_SAVED_VIEW_SUBMITTED, { metadata });
        },
        claimWorklistSavedViewDeleteRequested: (metadata: { saved_view_id: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKLIST_SAVED_VIEW_DELETE_REQUESTED, { metadata });
        },
        claimWorklistFilterChanged: (metadata: { field_id: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKLIST_FILTER_CHANGED, { metadata });
        },
        claimWorklistSortChanged: (metadata: { sort: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKLIST_SORT_CHANGED, { metadata });
        },
        /* "Show archived and snoozed" (GDN-3381). `on` is the state the
           biller moved TO, and `work_view` is a closed enum of queue keys.
           How often a queue is worked with hidden rows on screen is how we
           learn whether archiving is trusted or merely tolerated. */
        hiddenRowsToggled: (metadata: { on: boolean; work_view: string }) => {
          trackEvent(EventNames.BILLING_HIDDEN_ROWS_TOGGLED, { metadata });
        },
        /* "Group:" on Claims to fix (GDN-3383). `group_by` is a closed enum
           (none | reason | payer) and `work_view` a closed set of view keys;
           no claim, patient or payer identity travels. Whether billers reach
           for grouping at all is what says if the denial queue is worked in
           runs or one row at a time. */
        claimWorklistGroupingChanged: (metadata: { group_by: string; work_view: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKLIST_GROUPING_CHANGED, { metadata });
        },
        /* `route` measures the GDN-3199 navigation optimization: 'direct'
           when the row's encounter_id skips the gate shim, 'gate_fallback'
           when it can't. Paired with billing_claim_workbench_entered on
           claim_id, the gap is clicks that never arrived. */
        claimWorklistRowClicked: (metadata: {
          claim_id: string;
          /* GDN-3201: whether hover prefetch warmed this row before the
             click — the optimization's hit-rate meter. */
          prefetched: boolean;
          route: 'direct' | 'gate_fallback';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKLIST_ROW_CLICKED, {
            metadata: { ...metadata, prefetched: String(metadata.prefetched) },
          });
        },
        /* `destination` says what the truthful back button read at click
           time: provenance ('claims'/'encounters') vs the no-provenance
           fallback — the round trip's adoption meter (GDN-3197). */
        claimWorkbenchBackClicked: (metadata: {
          claim_id: string;
          /* 'encounter' (singular) is a single encounter's own page, whose
             claim tab is a same-route switch — it had no provenance at all
             before GDN-3322 and counted as 'fallback'. 'home' is JotBill
             Home's queue cards (GDN-3379). */
          destination: 'home' | 'claims' | 'encounters' | 'encounter' | 'fallback';
          /** The strip's Back, or Esc with nothing left open (GDN-3345). */
          source: 'click' | 'keyboard';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKBENCH_BACK_CLICKED, { metadata });
        },
        claimBatchSubmitInitiated: (metadata: { claim_count: number }) => {
          trackEvent(EventNames.BILLING_CLAIM_BATCH_SUBMIT_INITIATED, {
            metadata: { claim_count: String(metadata.claim_count) },
          });
        },
        claimWorklistClearSelectionClicked: (metadata: { selected_count: number }) => {
          trackEvent(EventNames.BILLING_CLAIM_WORKLIST_CLEAR_SELECTION_CLICKED, {
            metadata: { selected_count: String(metadata.selected_count) },
          });
        },
        /* Fired from the contextual header's Review action (GDN-3216) —
           adoption meter for the exception-first review path out of a batch. */
        claimBatchReviewClicked: (metadata: { claim_id: string; attention_count: number }) => {
          trackEvent(EventNames.BILLING_CLAIM_BATCH_REVIEW_CLICKED, {
            metadata: {
              claim_id: metadata.claim_id,
              attention_count: String(metadata.attention_count),
            },
          });
        },
        claimBatchDismissClicked: (metadata: { batch_status: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_BATCH_DISMISS_CLICKED, { metadata });
        },
        /* Fired when the biller confirms the delete dialog — the intent,
           not the outcome, matching claimBatchSubmitInitiated. Whether the
           claim actually went is the server's to report (it emits a
           claim-deleted event); per CLAUDE.md failures are not
           interactions, so no `_failed` counterpart belongs here. */
        claimDeleteInitiated: (metadata: { claim_id: string }) => {
          trackEvent(EventNames.BILLING_CLAIM_DELETE_INITIATED, { metadata });
        },
        claimCertificationEntered: (metadata: {
          claim_id: string;
          certification_status: string;
          certification_freshness: string;
          blocker_count: number;
          suggestion_count: number;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_CERTIFICATION_ENTERED, {
            metadata: {
              ...metadata,
              blocker_count: String(metadata.blocker_count),
              suggestion_count: String(metadata.suggestion_count),
            },
          });
        },
        claimCertificationFindingToggled: (metadata: {
          claim_id: string;
          finding_kind: 'blocker' | 'suggestion';
          target_kind: string;
          source_capability: 'available' | 'redacted' | 'not_applicable';
          expanded: boolean;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_CERTIFICATION_FINDING_TOGGLED, {
            metadata: { ...metadata, expanded: String(metadata.expanded) },
          });
        },
        claimCertificationShowMoreClicked: (metadata: {
          claim_id: string;
          hidden_suggestion_count: number;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_CERTIFICATION_SHOW_MORE_CLICKED, {
            metadata: {
              ...metadata,
              hidden_suggestion_count: String(metadata.hidden_suggestion_count),
            },
          });
        },
        claimCertificationTargetClicked: (metadata: {
          claim_id: string;
          finding_kind: 'blocker' | 'suggestion';
          target_kind: string;
          source_capability: 'available' | 'redacted' | 'not_applicable';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_CERTIFICATION_TARGET_CLICKED, { metadata });
        },
        claimCertificationDispositionSubmitted: (metadata: {
          claim_id: string;
          finding_kind: 'blocker' | 'suggestion';
          action: 'dismiss' | 'reopen';
          expected_version: number;
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_CERTIFICATION_DISPOSITION_SUBMITTED, {
            metadata: {
              ...metadata,
              expected_version: String(metadata.expected_version),
            },
          });
        },
        claimCertificationRetryClicked: (metadata: {
          claim_id: string;
          reason: 'load_error' | 'refresh_error' | 'disposition_conflict';
        }) => {
          trackEvent(EventNames.BILLING_CLAIM_CERTIFICATION_RETRY_CLICKED, { metadata });
        },
        /**
         * Provider showed/hid the pay-to address section on a billing identity
         * (GDN-2822). `source` separates the deliberate checkbox from the PO
         * Box warning's one-click move.
         */
        payToAddressToggled: (metadata: {
          enabled: boolean;
          source: 'checkbox' | 'po_box_warning';
          context?: 'wizard' | 'edit_modal';
        }) => {
          trackEvent(EventNames.BILLING_PAY_TO_ADDRESS_TOGGLED, {
            metadata: {
              ...metadata,
              enabled: String(metadata.enabled),
            },
          });
        },
        /**
         * Provider took the "Move this to the pay-to address" action from the
         * PO Box warning — the signal that the advisory actually deflects a
         * would-be Stedi rejection.
         */
        poBoxWarningActionClicked: (metadata?: { context?: 'wizard' | 'edit_modal' }) => {
          trackEvent(EventNames.BILLING_PO_BOX_WARNING_ACTION_CLICKED, {
            metadata: { ...metadata },
          });
        },
        /**
         * Gated patient balance table (GDN-2943): which filter chip the
         * biller works from — the scale mechanism under test.
         */
        patientBalanceTableFilterSelected: (metadata: { filter: string }) => {
          trackEvent(EventNames.PATIENT_BALANCE_TABLE_FILTER_SELECTED, {
            metadata,
          });
        },
        /** Row click-through to the encounter view from the balance table. */
        patientBalanceTableRowClicked: (metadata: { encounter_id: string; bucket: string }) => {
          trackEvent(EventNames.PATIENT_BALANCE_TABLE_ROW_CLICKED, {
            metadata,
          });
        },
        /** A balance table row verb — the inline primary button or an
         * item in the row's "…" overflow menu (gdn-2979). */
        patientBalanceTableActionClicked: (metadata: {
          encounter_id: string;
          action: string;
          via?: 'button' | 'overflow';
        }) => {
          trackEvent(EventNames.PATIENT_BALANCE_TABLE_ACTION_CLICKED, {
            metadata,
          });
        },
        /** "Download account history" — the raw ledger leaving as a PDF. */
        patientBalanceTableDownloadHistoryClicked: (metadata: { patient_id: string }) => {
          trackEvent(EventNames.PATIENT_BALANCE_TABLE_DOWNLOAD_HISTORY_CLICKED, {
            metadata,
          });
        },
        /** Click-through to an invoice's hosted payment page. */
        patientInvoiceLinkClicked: (metadata: {
          patient_id: string;
          invoice_id: string;
          source: 'activity_row' | 'open_invoices';
        }) => {
          trackEvent(EventNames.PATIENT_INVOICE_LINK_CLICKED, { metadata });
        },
        /**
         * Declined-invoice recovery (GDN-3064). "Update card & retry"
         * opened from a row. `retries_exhausted` splits the two decline
         * states — pending retries usually self-recover, so a click there
         * is a biller overriding the wait.
         */
        patientInvoiceRetryPaymentClicked: (metadata: {
          patient_id: string;
          invoice_id: string;
          retries_exhausted: boolean;
          via?: 'button' | 'overflow';
        }) => {
          trackEvent(EventNames.PATIENT_INVOICE_RETRY_PAYMENT_CLICKED, {
            metadata,
          });
        },
        /** The retry dialog confirmed — the interaction, not the outcome
         * (the charge result is emitted BE-side). */
        patientInvoiceRetryPaymentSubmitted: (metadata: {
          patient_id: string;
          invoice_id: string;
          payment_method_changed: boolean;
        }) => {
          trackEvent(EventNames.PATIENT_INVOICE_RETRY_PAYMENT_SUBMITTED, {
            metadata,
          });
        },
        /** "Stop retrying this card" — the deliberate opt-out of Stripe's
         * automatic collection, leaving the debt in place. */
        patientInvoiceStopRetriesClicked: (metadata: {
          patient_id: string;
          invoice_id: string;
        }) => {
          trackEvent(EventNames.PATIENT_INVOICE_STOP_RETRIES_CLICKED, {
            metadata,
          });
        },
        /** "Void invoice" chosen from a menu — opens the confirm. With
         * the submitted event below this measures who balks at the
         * destructive step. */
        patientInvoiceVoidClicked: (metadata: {
          patient_id: string;
          invoice_id: string;
          source: 'balance_table' | 'open_invoices';
        }) => {
          trackEvent(EventNames.PATIENT_INVOICE_VOID_CLICKED, { metadata });
        },
        /** The void confirm dialog's destructive confirm. */
        patientInvoiceVoidSubmitted: (metadata: {
          patient_id: string;
          invoice_id: string;
          source: 'balance_table' | 'open_invoices';
        }) => {
          trackEvent(EventNames.PATIENT_INVOICE_VOID_SUBMITTED, { metadata });
        },
        /** "Assign to encounter" opened from a stranded account charge
         * (GDN-3106). This is the discovery signal — 205 charges sit
         * unrepaired, and if this never fires the cure is as unreachable
         * as it was before the endpoint existed. */
        patientChargeAssignmentClicked: (metadata: {
          patient_id: string;
          amount_cents: number;
        }) => {
          trackEvent(EventNames.PATIENT_CHARGE_ASSIGNMENT_CLICKED, {
            metadata: {
              ...metadata,
              amount_cents: String(metadata.amount_cents),
            },
          });
        },
        /** The repair confirmed against a chosen encounter. Fired at
         * confirm-click; the ledger movement is the backend's event. */
        patientChargeAssignmentSubmitted: (metadata: {
          patient_id: string;
          target_encounter_id: string;
          amount_cents: number;
          /** How the stand-in splits: what lands on the encounter, and
           * what banks as account credit because the stand-in was larger
           * than the adjudicated responsibility (GDN-3125). Over-collection
           * is what estimated copays normally do, so the ratio of these two
           * is the shape of the repair, not an edge case. */
          assigned_cents: number;
          credited_cents: number;
          has_note: boolean;
        }) => {
          trackEvent(EventNames.PATIENT_CHARGE_ASSIGNMENT_SUBMITTED, {
            metadata: {
              ...metadata,
              amount_cents: String(metadata.amount_cents),
              assigned_cents: String(metadata.assigned_cents),
              credited_cents: String(metadata.credited_cents),
              has_note: String(metadata.has_note),
            },
          });
        },
        /**
         * Allocation verb (GDN-2950): the user confirmed applying a credit
         * to an encounter. Fired at confirm-click (the interaction), not on
         * server success — the BE emits the durable money event.
         */
        patientCreditAllocationSubmitted: (metadata: {
          patient_id: string;
          amount_cents: number;
          target_encounter_id: string;
          source: 'encounter_credit' | 'account_credit';
        }) => {
          trackEvent(EventNames.PATIENT_CREDIT_ALLOCATION_SUBMITTED, {
            metadata,
          });
        },
        /** Biller confirmed recording a refund from the balance table
         * (gdn-2978). Outcome (success/failure) is the BE's product
         * event; this is the interaction. */
        patientRefundSubmitted: (metadata: {
          patient_id: string;
          amount_cents: number;
          payment_ledger_entry_id: string;
          method: string;
          source: 'encounter_credit' | 'account_credit';
        }) => {
          trackEvent(EventNames.PATIENT_REFUND_SUBMITTED, {
            metadata,
          });
        },
        createClaim: (
          claimId: string,
          metadata?: {
            patient_id?: string;
            encounter_ids?: string[];
            source?: 'unbilled_sessions' | 'manual' | 'bulk';
            initial_validation_status?: 'valid' | 'has_errors' | 'incomplete';
          },
        ) => {
          trackEvent(EventNames.BILLING_CLAIM_CREATED, {
            metadata: {
              claim_id: claimId,
              ...(metadata?.encounter_ids && {
                encounter_count: String(metadata.encounter_ids.length),
              }),
              ...metadata,
            },
          });
        },
        viewClaim: (
          claimId: string,
          metadata?: {
            claim_status?: string;
            is_submitted?: boolean;
          },
        ) => {
          trackEvent(EventNames.BILLING_CLAIM_VIEWED, {
            metadata: {
              claim_id: claimId,
              ...(metadata?.is_submitted !== undefined && {
                is_submitted: String(metadata.is_submitted),
              }),
              ...metadata,
            },
          });
        },
        editClaim: (
          claimId: string,
          metadata?: {
            field_edited?: string;
            edit_type?: string;
          },
        ) => {
          trackEvent(EventNames.BILLING_CLAIM_EDITED, {
            metadata: { claim_id: claimId, ...metadata },
          });
        },
        deleteClaim: (
          claimId: string,
          metadata?: {
            claim_status?: string;
            was_submitted?: boolean;
          },
        ) => {
          trackEvent(EventNames.BILLING_CLAIM_DELETED, {
            metadata: {
              claim_id: claimId,
              ...(metadata?.was_submitted !== undefined && {
                was_submitted: String(metadata.was_submitted),
              }),
              ...metadata,
            },
          });
        },
        rebuildClaim: (
          claimId: string,
          metadata?: {
            claim_status?: string;
          },
        ) => {
          trackEvent(EventNames.BILLING_CLAIM_REBUILT, {
            metadata: {
              claim_id: claimId,
              ...metadata,
            },
          });
        },
        autoRebuildStarted: (
          claimId: string,
          metadata?: {
            claim_status?: string;
            reasons?: string;
            reason_count?: number;
          },
        ) => {
          const { reason_count, ...restMetadata } = metadata ?? {};
          trackEvent(EventNames.BILLING_CLAIM_AUTO_REBUILD_STARTED, {
            metadata: {
              claim_id: claimId,
              ...restMetadata,
              ...(reason_count !== undefined && {
                reason_count: String(reason_count),
              }),
            },
          });
        },
        learnMoreClicked: (metadata?: { destination_url?: string }) => {
          trackEvent(EventNames.BILLING_INSURANCE_LEARN_MORE_CLICKED, {
            metadata,
          });
        },
        rulesClicked: (metadata?: { destination_path?: string }) => {
          trackEvent(EventNames.BILLING_INSURANCE_RULES_CLICKED, {
            metadata,
          });
        },
        morningWorklistFilterChanged: (metadata: {
          filter_name: 'urgency' | 'owner';
          filter_value: string;
          visible_org_count: number;
        }) => {
          trackEvent(EventNames.BILLING_MORNING_WORKLIST_FILTER_CHANGED, {
            metadata: {
              ...metadata,
              visible_org_count: String(metadata.visible_org_count),
            },
          });
        },
        morningWorklistAccountClicked: (metadata: {
          organization_id: string;
          urgency: string;
          rank: number;
          work_item_count: number;
        }) => {
          trackEvent(EventNames.BILLING_MORNING_WORKLIST_ACCOUNT_CLICKED, {
            metadata: {
              ...metadata,
              rank: String(metadata.rank),
              work_item_count: String(metadata.work_item_count),
            },
          });
        },
        morningWorklistClassicDashboardClicked: (metadata: { visible_org_count: number }) => {
          trackEvent(EventNames.BILLING_MORNING_WORKLIST_CLASSIC_DASHBOARD_CLICKED, {
            metadata: {
              visible_org_count: String(metadata.visible_org_count),
            },
          });
        },
        submitClaim: (
          claimId: string,
          metadata?: {
            /**
             * WHERE the submission was made from. 'workbench' is the rebuilt
             * claim page (GDN-3157) — named rather than folded into 'manual'
             * because the whole point of measuring it is telling the two
             * surfaces apart while both are live.
             */
            submission_method?: 'manual' | 'batch' | 'workbench';
            /**
             * 'unknown' is the GDN-3455 outcome: the request did not come back
             * and the re-read of the stored claim could not settle it either.
             * It is a value on this existing event, not a new event name, so
             * the Metabase suffix allowlist is untouched — and it matters
             * because those attempts used to be counted as 'failed', which
             * inflated the submission failure rate with submissions that had
             * in fact succeeded.
             */
            claim_status?: 'submitted' | 'blocked' | 'failed' | 'unknown';
            days_since_creation?: number;
            edit_count?: number;
            validation_attempts?: number;
          },
        ) => {
          trackEvent(EventNames.BILLING_CLAIM_SUBMITTED, {
            metadata: {
              claim_id: claimId,
              ...(metadata?.days_since_creation !== undefined && {
                days_since_creation: String(metadata.days_since_creation),
              }),
              ...(metadata?.edit_count !== undefined && {
                edit_count: String(metadata.edit_count),
              }),
              ...(metadata?.validation_attempts !== undefined && {
                validation_attempts: String(metadata.validation_attempts),
              }),
              ...metadata,
            },
          });
        },
        // ERA (835) downloads
        eraDownloaded: (
          remittanceId: string,
          metadata?: { source?: 'detail_dialog' | 'list_bulk' },
        ) => {
          trackEvent(EventNames.BILLING_ERA_DOWNLOADED, {
            metadata: { remittance_id: remittanceId, ...metadata },
          });
        },
        eraDownloadFailed: (
          remittanceId: string,
          metadata?: { source?: 'detail_dialog' | 'list_bulk' },
        ) => {
          trackEvent(EventNames.BILLING_ERA_DOWNLOAD_FAILED, {
            metadata: { remittance_id: remittanceId, ...metadata },
          });
        },
        eraBulkDownloaded: (metadata?: { count?: number }) => {
          trackEvent(EventNames.BILLING_ERA_BULK_DOWNLOADED, {
            metadata: {
              ...(metadata?.count !== undefined && { count: String(metadata.count) }),
            },
          });
        },
        eraBulkDownloadFailed: (metadata?: { count?: number }) => {
          trackEvent(EventNames.BILLING_ERA_BULK_DOWNLOAD_FAILED, {
            metadata: {
              ...(metadata?.count !== undefined && { count: String(metadata.count) }),
            },
          });
        },
        // Claim X12 (837) downloads
        claimX12Downloaded: (claimId: string) => {
          trackEvent(EventNames.BILLING_CLAIM_X12_DOWNLOADED, {
            metadata: { claim_id: claimId },
          });
        },
        claimX12DownloadFailed: (claimId: string) => {
          trackEvent(EventNames.BILLING_CLAIM_X12_DOWNLOAD_FAILED, {
            metadata: { claim_id: claimId },
          });
        },
        claimX12BulkDownloaded: (metadata?: { count?: number }) => {
          trackEvent(EventNames.BILLING_CLAIM_X12_BULK_DOWNLOADED, {
            metadata: {
              ...(metadata?.count !== undefined && { count: String(metadata.count) }),
            },
          });
        },
        claimX12BulkDownloadFailed: (metadata?: { count?: number }) => {
          trackEvent(EventNames.BILLING_CLAIM_X12_BULK_DOWNLOAD_FAILED, {
            metadata: {
              ...(metadata?.count !== undefined && { count: String(metadata.count) }),
            },
          });
        },
        // ERA triage attention actions
        claimAttentionDismissed: (
          claimId: string,
          metadata: {
            source:
              | 'claims_list'
              | 'work_queue'
              | 'overview'
              | 'claims_review'
              | 'encounter_worklist'
              | 'encounter_claim';
            attention_lane?: string;
            signals?: string[];
          },
        ) => {
          trackEvent(EventNames.BILLING_CLAIM_ATTENTION_DISMISSED, {
            metadata: {
              claim_id: claimId,
              source: metadata.source,
              ...(metadata.attention_lane && { attention_lane: metadata.attention_lane }),
              ...(metadata.signals?.length && { signals: metadata.signals.join(',') }),
            },
          });
        },
        claimAttentionSnoozed: (
          claimId: string,
          metadata: {
            source:
              | 'claims_list'
              | 'work_queue'
              | 'overview'
              | 'claims_review'
              | 'encounter_worklist'
              | 'encounter_claim';
            attention_lane?: string;
            signals?: string[];
            snooze_until?: string;
          },
        ) => {
          trackEvent(EventNames.BILLING_CLAIM_ATTENTION_SNOOZED, {
            metadata: {
              claim_id: claimId,
              source: metadata.source,
              ...(metadata.attention_lane && { attention_lane: metadata.attention_lane }),
              ...(metadata.signals?.length && { signals: metadata.signals.join(',') }),
              ...(metadata.snooze_until && { snooze_until: metadata.snooze_until }),
            },
          });
        },
        claimAttentionFailed: (
          claimId: string,
          metadata: {
            action: 'dismiss' | 'snooze';
            source:
              | 'claims_list'
              | 'work_queue'
              | 'overview'
              | 'claims_review'
              | 'encounter_worklist'
              | 'encounter_claim';
            error_code?: string;
          },
        ) => {
          trackEvent(EventNames.BILLING_CLAIM_ATTENTION_FAILED, {
            metadata: {
              claim_id: claimId,
              action: metadata.action,
              source: metadata.source,
              ...(metadata.error_code && { error_code: metadata.error_code }),
            },
          });
        },

        // Encounter work verbs (GDN-3380). Metadata is exactly what contract
        // section 9 pins: no ids beyond them, and never a note body.
        workDispositionSubmitted: (metadata: {
          action: 'archive' | 'snooze' | 'restore';
          source: 'home' | 'claims_worklist' | 'claim_workbench';
        }) => {
          trackEvent(EventNames.BILLING_WORK_DISPOSITION_SUBMITTED, {
            metadata: { action: metadata.action, source: metadata.source },
          });
        },
        archiveQuickNoteClicked: (metadata: { preset: 'resolved' | 'no_further_action' }) => {
          trackEvent(EventNames.BILLING_ARCHIVE_QUICK_NOTE_CLICKED, {
            metadata: { preset: metadata.preset },
          });
        },
        workNoteSubmitted: (metadata: {
          source: 'home' | 'claims_worklist' | 'claim_workbench';
        }) => {
          trackEvent(EventNames.BILLING_WORK_NOTE_SUBMITTED, {
            metadata: { source: metadata.source },
          });
        },
        workNoteEdited: (metadata: { source: 'home' | 'claims_worklist' | 'claim_workbench' }) => {
          trackEvent(EventNames.BILLING_WORK_NOTE_EDIT_SUBMITTED, {
            metadata: { source: metadata.source },
          });
        },
        workNoteDeleted: (metadata: { source: 'home' | 'claims_worklist' | 'claim_workbench' }) => {
          trackEvent(EventNames.BILLING_WORK_NOTE_DELETE_SUBMITTED, {
            metadata: { source: metadata.source },
          });
        },
      },

      // Patient billing from ERA
      patientBilling: {
        billClicked: (
          remittanceClaimId: string,
          metadata?: {
            amount_cents?: number;
          },
        ) => {
          trackEvent(EventNames.BILLING_PATIENT_BILL_CLICKED, {
            metadata: {
              remittance_claim_id: remittanceClaimId,
              ...metadata,
            },
          });
        },
        billed: (
          remittanceClaimId: string,
          metadata?: {
            amount_cents?: number;
            invoice_id?: string;
          },
        ) => {
          trackEvent(EventNames.BILLING_PATIENT_BILLED_FROM_ERA, {
            metadata: {
              remittance_claim_id: remittanceClaimId,
              ...metadata,
            },
          });
        },
        billFailed: (
          remittanceClaimId: string,
          metadata?: {
            error_code?: string;
            error_message?: string;
          },
        ) => {
          trackEvent(EventNames.BILLING_PATIENT_BILL_FAILED, {
            metadata: {
              remittance_claim_id: remittanceClaimId,
              ...metadata,
            },
          });
        },
        paymentUrlCopied: (
          remittanceClaimId: string,
          metadata?: {
            invoice_id?: string;
          },
        ) => {
          trackEvent(EventNames.BILLING_PATIENT_BILL_PAYMENT_URL_COPIED, {
            metadata: {
              remittance_claim_id: remittanceClaimId,
              ...metadata,
            },
          });
        },
      },

      // Analytics metrics
      analytics: {
        interestedClicked: () => {
          trackEvent(EventNames.REPORTS_ANALYTICS_INTERESTED_CLICKED, {
            metadata: {},
          });
        },
      },

      // Reports engine (practice reports: payroll, attendance, ...)
      reports: {
        /**
         * Track when a report page is opened
         */
        reportOpened: (metadata: { report_key: string }) => {
          trackEvent(EventNames.REPORT_OPENED, { metadata });
        },
        /**
         * Track when a report run is submitted (params chosen, run clicked)
         */
        runSubmitted: (metadata: {
          report_key: string;
          basis?: string;
          group_by?: string;
          window_days?: number;
          // GDN-3293 code review: deliberately no `scope` field here — at
          // submit time only the PRIOR run's scope is known (this event
          // fires before the response for the run it describes), and a
          // field that reports a different run's value would be worse
          // than not having it. `exportClicked` below carries `scope`
          // instead, since it always fires after a run's response.
        }) => {
          trackEvent(EventNames.REPORT_RUN_SUBMITTED, { metadata });
        },
        /**
         * Track when an export button is clicked (format: csv or xlsx)
         */
        exportClicked: (metadata: {
          report_key: string;
          basis?: string;
          grouped?: boolean;
          format?: string;
          columns_scope?: 'view' | 'all';
          sheet_by?: string;
          // GDN-3293: row scope of the data being exported.
          scope?: 'all' | 'own';
        }) => {
          trackEvent(EventNames.REPORT_EXPORT_CLICKED, { metadata });
        },
        /**
         * Track when a save-view dialog is submitted
         */
        viewSaveSubmitted: (metadata: { report_key: string }) => {
          trackEvent(EventNames.REPORT_VIEW_SAVE_SUBMITTED, { metadata });
        },
        /**
         * Track when a saved view is opened/run
         */
        savedViewOpened: (metadata: { report_key: string }) => {
          trackEvent(EventNames.REPORT_SAVED_VIEW_OPENED, { metadata });
        },
        /**
         * Track when a command-bar token (period/basis/group/columns/filter) is changed
         */
        tokenChanged: (metadata: {
          report_key: string;
          token: 'period' | 'basis' | 'group' | 'columns' | 'sort' | 'filter';
          via: 'pointer' | 'keyboard';
        }) => {
          trackEvent(EventNames.REPORT_TOKEN_CHANGED, { metadata });
        },
        /**
         * Track when a group band is collapsed/expanded in the console table
         */
        groupToggled: (metadata: { report_key: string; collapsed: boolean }) => {
          trackEvent(EventNames.REPORT_GROUP_TOGGLED, { metadata });
        },
        /**
         * Track when the appointment-labels filter selection changes.
         * `label_count` is how many labels are selected — never the ids or
         * names (no PHI / no high-cardinality values).
         */
        labelFilterChanged: (metadata: { report_key: string; label_count: number }) => {
          trackEvent(EventNames.REPORT_LABEL_FILTER_CHANGED, { metadata });
        },
        /**
         * Track when the schedule-report download button (calendar toolbar) is
         * clicked. Counts only — no PHI. `provider_filter_count` is how many
         * providers were carried from the calendar filter (never ids/names).
         */
        downloadClicked: (metadata: {
          report_key: string;
          view_mode?: string;
          window_days?: number;
          provider_filter_count?: number;
        }) => {
          trackEvent(EventNames.REPORT_DOWNLOAD_CLICKED, { metadata });
        },
      },

      // Global command palette (⌘K)
      commandPalette: {
        /**
         * Track when the palette is opened (surface = pathname area)
         */
        opened: (metadata: { surface: string; via: 'keyboard' | 'pointer' }) => {
          trackEvent(EventNames.COMMAND_PALETTE_OPENED, { metadata });
        },
        /**
         * Track when a palette action is executed
         */
        actionSelected: (metadata: { action_type: string }) => {
          trackEvent(EventNames.COMMAND_PALETTE_ACTION_SELECTED, { metadata });
        },
      },

      // Communications metrics (patient communications interest tracking)
      communications: {
        interestedClicked: () => {
          trackEvent(EventNames.PATIENT_COMMUNICATIONS_INTERESTED_CLICKED, {
            metadata: {},
          });
        },
      },

      // Audit metrics
      audit: {
        learnMoreClicked: (metadata?: { destination_url?: string }) => {
          trackEvent(EventNames.AUDIT_LEARN_MORE_CLICKED, {
            metadata,
          });
        },
        sendDocumentsClicked: () => {
          trackEvent(EventNames.AUDIT_SEND_DOCUMENTS_CLICKED, {
            metadata: {},
          });
        },
        rulesClicked: (metadata?: { destination_path?: string }) => {
          trackEvent(EventNames.AUDIT_RULES_CLICKED, {
            metadata,
          });
        },
      },

      // Eligibility Checks metrics
      // Interaction counts only: never accept identifiers or clinical/financial metadata.
      eligibilityRecord: {
        manualEntryOpened: () => trackEvent(EventNames.ELIGIBILITY_RECORD_MANUAL_OPENED),
        manualEntryClosed: () => trackEvent(EventNames.ELIGIBILITY_RECORD_MANUAL_CLOSED),
        manualEntrySubmitted: () => trackEvent(EventNames.ELIGIBILITY_RECORD_MANUAL_SUBMITTED),
        automaticCheckClicked: () => trackEvent(EventNames.ELIGIBILITY_RECORD_AUTOMATIC_CLICKED),
        coverageSelected: () => trackEvent(EventNames.ELIGIBILITY_RECORD_COVERAGE_SELECTED),
        sourceOpened: () => trackEvent(EventNames.ELIGIBILITY_RECORD_SOURCE_OPENED),
        sourceClosed: () => trackEvent(EventNames.ELIGIBILITY_RECORD_SOURCE_CLOSED),
      },
      eligibilityPayerHistory: {
        historyOpened: () => trackEvent(EventNames.ELIGIBILITY_PAYER_HISTORY_OPENED),
        requestCompleted: (outcome: 'success' | 'failure', durationMs: number) => {
          if (outcome !== 'success' && outcome !== 'failure') return;
          trackEvent(EventNames.ELIGIBILITY_PAYER_HISTORY_REQUEST_COMPLETED, {
            metadata: {
              outcome,
              duration_ms: Number.isFinite(durationMs) ? Math.max(0, Math.round(durationMs)) : 0,
            },
          });
        },
        refreshClicked: () => trackEvent(EventNames.ELIGIBILITY_PAYER_HISTORY_REFRESH_CLICKED),
        claimOpened: () => trackEvent(EventNames.ELIGIBILITY_PAYER_HISTORY_CLAIM_OPENED),
      },
      eligibilityChecks: {
        learnMoreClicked: (metadata?: { destination_url?: string }) => {
          trackEvent(EventNames.ELIGIBILITY_CHECKS_LEARN_MORE_CLICKED, {
            metadata,
          });
        },
        warningViewed: (metadata?: { patient_id?: string; source?: string }) => {
          trackEvent(EventNames.ELIGIBILITY_WARNING_VIEWED, {
            metadata,
          });
        },
        warningPopoverOpened: (metadata?: { patient_id?: string; source?: string }) => {
          trackEvent(EventNames.ELIGIBILITY_WARNING_POPOVER_OPENED, {
            metadata,
          });
        },
        warningLearnMoreClicked: (metadata?: { patient_id?: string; source?: string }) => {
          trackEvent(EventNames.ELIGIBILITY_WARNING_LEARN_MORE_CLICKED, {
            metadata,
          });
        },
        warningDismissed: (metadata?: { patient_id?: string; source?: string }) => {
          trackEvent(EventNames.ELIGIBILITY_WARNING_DISMISSED, {
            metadata,
          });
        },
        badgeViewed: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
          mode?: string;
          status?: string;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_BADGE_VIEWED, { metadata });
        },
        badgePopoverOpened: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
          mode?: string;
          status?: string;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_BADGE_POPOVER_OPENED, { metadata });
        },
        runCheckClicked: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_BADGE_RUN_CHECK_CLICKED, { metadata });
        },
        viewDetailsClicked: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
          status?: string;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_BADGE_VIEW_DETAILS_CLICKED, { metadata });
        },
        verificationFormOpened: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
          trigger?: 'verify_button' | 're_verify_link';
          mode?: string;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_VERIFICATION_FORM_OPENED, { metadata });
        },
        verificationFormClosed: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
          had_dirty_fields?: boolean;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_VERIFICATION_FORM_CLOSED, {
            metadata: {
              ...metadata,
              ...(metadata?.had_dirty_fields !== undefined && {
                had_dirty_fields: String(metadata.had_dirty_fields),
              }),
            },
          });
        },
        verificationSubmitted: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
          method?: string;
          has_rep_name?: boolean;
          has_reference_number?: boolean;
          fields_adjusted_count?: number;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_VERIFICATION_SUBMITTED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_rep_name !== undefined && {
                has_rep_name: String(metadata.has_rep_name),
              }),
              ...(metadata?.has_reference_number !== undefined && {
                has_reference_number: String(metadata.has_reference_number),
              }),
              ...(metadata?.fields_adjusted_count !== undefined && {
                fields_adjusted_count: String(metadata.fields_adjusted_count),
              }),
            },
          });
        },
        verificationHistoryOpened: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
          count?: number;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_VERIFICATION_HISTORY_OPENED, {
            metadata: {
              ...metadata,
              ...(metadata?.count !== undefined && {
                count: String(metadata.count),
              }),
            },
          });
        },
        alertsSnoozedClicked: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
          duration_days?: number;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_ALERTS_SNOOZED_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.duration_days !== undefined && {
                duration_days: String(metadata.duration_days),
              }),
            },
          });
        },
        alertsDismissedClicked: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_ALERTS_DISMISSED_CLICKED, {
            metadata,
          });
        },
        alertsResumedClicked: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
          was?: 'snoozed' | 'dismissed';
        }) => {
          trackEvent(EventNames.ELIGIBILITY_ALERTS_RESUMED_CLICKED, {
            metadata,
          });
        },
        collectionFormOpened: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
          readiness?: string;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_COLLECTION_FORM_OPENED, { metadata });
        },
        collectionFormClosed: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
          had_amount?: boolean;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_COLLECTION_FORM_CLOSED, {
            metadata: {
              ...metadata,
              ...(metadata?.had_amount !== undefined && {
                had_amount: String(metadata.had_amount),
              }),
            },
          });
        },
        collectionSubmitted: (metadata?: {
          patient_id?: string;
          encounter_id?: string;
          source?: string;
          amount_cents?: number;
          had_notes?: boolean;
          fully_collected?: boolean;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_COLLECTION_SUBMITTED, {
            metadata: {
              ...metadata,
              ...(metadata?.amount_cents !== undefined && {
                amount_cents: String(metadata.amount_cents),
              }),
              ...(metadata?.had_notes !== undefined && {
                had_notes: String(metadata.had_notes),
              }),
              ...(metadata?.fully_collected !== undefined && {
                fully_collected: String(metadata.fully_collected),
              }),
            },
          });
        },
        worklistOpened: (metadata?: {
          days?: number;
          total_patients?: number;
          needs_attention?: number;
          verified?: number;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_WORKLIST_OPENED, {
            metadata: {
              ...(metadata?.days !== undefined && {
                days: String(metadata.days),
              }),
              ...(metadata?.total_patients !== undefined && {
                total_patients: String(metadata.total_patients),
              }),
              ...(metadata?.needs_attention !== undefined && {
                needs_attention: String(metadata.needs_attention),
              }),
              ...(metadata?.verified !== undefined && {
                verified: String(metadata.verified),
              }),
            },
          });
        },
        worklistRowClicked: (metadata?: {
          patient_id?: string;
          task_priority?: number;
          row_state?: string;
          action_verb?: string;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_WORKLIST_ROW_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.task_priority !== undefined && {
                task_priority: String(metadata.task_priority),
              }),
            },
          });
        },
        worklistDrawerOpened: (metadata?: {
          patient_id?: string;
          row_state?: string;
          auto_expand?: 'verification' | 'collection' | null;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_WORKLIST_DRAWER_OPENED, {
            metadata: {
              ...metadata,
              ...(metadata?.auto_expand !== undefined && {
                auto_expand: metadata.auto_expand ?? 'none',
              }),
            },
          });
        },
        worklistSegmentChanged: (metadata?: {
          from_segment?: string;
          to_segment?: string;
          to_count?: number;
        }) => {
          trackEvent(EventNames.ELIGIBILITY_WORKLIST_SEGMENT_CHANGED, {
            metadata: {
              ...metadata,
              ...(metadata?.to_count !== undefined && {
                to_count: String(metadata.to_count),
              }),
            },
          });
        },
        worklistInvoiceClicked: (metadata?: { patient_id?: string; task_priority?: number }) => {
          trackEvent(EventNames.ELIGIBILITY_WORKLIST_INVOICE_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.task_priority !== undefined && {
                task_priority: String(metadata.task_priority),
              }),
            },
          });
        },
      },

      // Appointment metrics
      appointment: {
        modalOpened: (metadata?: { source?: string; is_org_schedule?: boolean }) => {
          trackEvent(EventNames.APPOINTMENT_MODAL_OPENED, {
            metadata: {
              ...(metadata?.is_org_schedule !== undefined && {
                is_org_schedule: String(metadata.is_org_schedule),
              }),
              ...metadata,
            },
          });
        },
        popoverPhoneClicked: (metadata: { appointment_id: string; patient_id: string }) => {
          trackEvent(EventNames.APPOINTMENT_POPOVER_PATIENT_PHONE_CLICKED, { metadata });
        },
        collisionModalOpened: (metadata?: {
          has_current?: boolean;
          has_upcoming?: boolean;
          has_overtime?: boolean;
          appointment_count?: number;
          confidence_level?: string;
        }) => {
          trackEvent(EventNames.APPOINTMENT_COLLISION_MODAL_OPENED, { metadata });
        },
        collisionUnscheduledSelected: (metadata?: {
          had_appointments?: boolean;
          appointment_count?: number;
          had_current?: boolean;
          had_upcoming?: boolean;
        }) => {
          trackEvent(EventNames.APPOINTMENT_COLLISION_UNSCHEDULED_SELECTED, { metadata });
        },
        typeSelected: (metadata?: {
          appointment_type_id?: string;
          color_enum?: string;
          /** Index of the picked type in the usage-sorted list (0 = most-used). */
          position_index?: number;
          total_count?: number;
          /** Pick replaced (or confirmed) an auto-applied most-used suggestion. */
          replaced_suggestion?: boolean;
          surface?: 'encounter_view' | 'add_appointment_modal' | 'edit_appointment_modal';
        }) => {
          trackEvent(EventNames.APPOINTMENT_TYPE_SELECTED, { metadata });
        },
        typePickerOpened: (metadata?: { total_count?: number; has_suggestion?: boolean }) => {
          trackEvent(EventNames.APPOINTMENT_TYPE_PICKER_OPENED, { metadata });
        },
        typeDefaultsPreviewOpened: (metadata?: {
          appointment_type_id?: string;
          has_template?: boolean;
          fields_set_count?: number;
        }) => {
          trackEvent(EventNames.APPOINTMENT_TYPE_DEFAULTS_PREVIEW_OPENED, { metadata });
        },
        typeModalOpened: (metadata?: { mode: 'create' | 'edit'; appointment_type_id?: string }) => {
          trackEvent(EventNames.APPOINTMENT_TYPE_MODAL_OPENED, { metadata });
        },
        typeCreated: (metadata: {
          appointment_type_id: string;
          color_selected: string;
          has_default_duration: boolean;
          has_default_template: boolean;
          has_default_forms: boolean;
          has_default_reminders: boolean;
          has_default_cpt_codes?: boolean;
          is_telehealth: boolean;
        }) => {
          trackEvent(EventNames.APPOINTMENT_TYPE_CREATED, {
            metadata: {
              ...metadata,
              has_default_duration: String(metadata.has_default_duration),
              has_default_template: String(metadata.has_default_template),
              has_default_forms: String(metadata.has_default_forms),
              has_default_reminders: String(metadata.has_default_reminders),
              has_default_cpt_codes: String(metadata.has_default_cpt_codes ?? false),
              is_telehealth: String(metadata.is_telehealth),
            },
          });
        },
        typeUpdated: (metadata: {
          appointment_type_id: string;
          color_changed: boolean;
          color_selected: string;
          fields_changed: string[];
        }) => {
          trackEvent(EventNames.APPOINTMENT_TYPE_UPDATED, {
            metadata: {
              ...metadata,
              color_changed: String(metadata.color_changed),
              fields_changed: metadata.fields_changed.join(','),
            },
          });
        },
        typeColorChanged: (metadata: {
          appointment_type_id?: string;
          previous_color?: string;
          new_color: string;
          context: 'create' | 'edit';
        }) => {
          trackEvent(EventNames.APPOINTMENT_TYPE_COLOR_CHANGED, { metadata });
        },
        typeDeleteClicked: (metadata: { appointment_type_id: string }) => {
          trackEvent(EventNames.APPOINTMENT_TYPE_DELETE_CLICKED, { metadata });
        },
        typeDeleted: (metadata: { appointment_type_id: string }) => {
          trackEvent(EventNames.APPOINTMENT_TYPE_DELETED, { metadata });
        },
        typesManagementModalOpened: (metadata?: {
          source?: string;
          appointment_types_count?: number;
        }) => {
          trackEvent(EventNames.APPOINTMENT_TYPES_MANAGEMENT_MODAL_OPENED, { metadata });
        },
        recurringEnabled: (metadata?: { recurrence_pattern?: string }) => {
          trackEvent(EventNames.APPOINTMENT_RECURRING_ENABLED, { metadata });
        },
        /** Timing picked for a form/reminder send in the inline rule editors. */
        ruleTimingChanged: (metadata: {
          rule_type: 'form' | 'reminder';
          surface: 'appointment' | 'appointment_type';
          offset_iso: string;
          is_custom: boolean;
        }) => {
          trackEvent(EventNames.APPOINTMENT_RULE_TIMING_CHANGED, {
            metadata: {
              ...metadata,
              is_custom: String(metadata.is_custom),
            },
          });
        },
        /** An extra send of an already-attached form/reminder was added. */
        ruleAdditionalSendClicked: (metadata: {
          rule_type: 'form' | 'reminder';
          surface: 'appointment' | 'appointment_type';
          total_sends: number;
        }) => {
          trackEvent(EventNames.APPOINTMENT_RULE_ADDITIONAL_SEND_CLICKED, {
            metadata: {
              ...metadata,
              total_sends: String(metadata.total_sends),
            },
          });
        },
        /** Unreachable-patient warning on a Forms/Reminders row was clicked to fix contact info. */
        ruleContactWarningClicked: (metadata: {
          rule_type: 'form' | 'reminder';
          surface: 'create' | 'drawer';
          missing_fields: string[];
        }) => {
          trackEvent(EventNames.APPOINTMENT_RULE_CONTACT_WARNING_CLICKED, {
            metadata: {
              ...metadata,
              missing_fields: metadata.missing_fields.join(','),
            },
          });
        },
        create: (metadata?: {
          appointment_id?: string;
          appointment_type_id?: string;
          duration_minutes?: number;
          is_recurring?: boolean;
          recurrence_pattern?: string;
          is_telehealth?: boolean;
          has_forms?: boolean;
          forms_count?: number;
          has_reminders?: boolean;
          reminders_count?: number;
          source?: string;
          is_org_schedule?: boolean;
          target_provider_id?: string;
        }) => {
          trackEvent(EventNames.APPOINTMENT_CREATED, {
            metadata: {
              ...(metadata?.is_recurring !== undefined && {
                is_recurring: String(metadata.is_recurring),
              }),
              ...(metadata?.is_telehealth !== undefined && {
                is_telehealth: String(metadata.is_telehealth),
              }),
              ...(metadata?.has_forms !== undefined && {
                has_forms: String(metadata.has_forms),
              }),
              ...(metadata?.has_reminders !== undefined && {
                has_reminders: String(metadata.has_reminders),
              }),
              ...(metadata?.duration_minutes && {
                duration_minutes: String(metadata.duration_minutes),
              }),
              ...(metadata?.forms_count && {
                forms_count: String(metadata.forms_count),
              }),
              ...(metadata?.reminders_count && {
                reminders_count: String(metadata.reminders_count),
              }),
              ...(metadata?.is_org_schedule !== undefined && {
                is_org_schedule: String(metadata.is_org_schedule),
              }),
              ...metadata,
            },
          });
        },
        creationFailed: (metadata?: {
          error_type?: string;
          error_message?: string;
          is_org_schedule?: boolean;
        }) => {
          trackEvent(EventNames.APPOINTMENT_CREATION_FAILED, { metadata });
        },
        creationAbandoned: (metadata?: {
          source?: string;
          had_patient_selected?: boolean;
          had_type_selected?: boolean;
          is_org_schedule?: boolean;
        }) => {
          trackEvent(EventNames.APPOINTMENT_CREATION_ABANDONED, {
            metadata: {
              ...(metadata?.had_patient_selected !== undefined && {
                had_patient_selected: String(metadata.had_patient_selected),
              }),
              ...(metadata?.had_type_selected !== undefined && {
                had_type_selected: String(metadata.had_type_selected),
              }),
              ...metadata,
            },
          });
        },
        update: (appointmentId: string, metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.APPOINTMENT_UPDATED, {
            metadata: { appointment_id: appointmentId, ...metadata },
          });
        },
        cancel: (appointmentId: string, metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.APPOINTMENT_CANCELLED, {
            metadata: { appointment_id: appointmentId, ...metadata },
          });
        },
        reschedule: (appointmentId: string, metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.APPOINTMENT_RESCHEDULED, {
            metadata: { appointment_id: appointmentId, ...metadata },
          });
        },
        complete: (appointmentId: string, metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.APPOINTMENT_COMPLETED, {
            metadata: { appointment_id: appointmentId, ...metadata },
          });
        },
        noteAdded: (metadata: {
          appointment_id: string;
          note_length: number;
          is_org_schedule?: boolean;
          target_provider_id?: string;
        }) => {
          trackEvent(EventNames.APPOINTMENT_NOTE_ADDED, {
            metadata: {
              ...metadata,
              note_length: String(metadata.note_length),
              has_note: 'true',
            },
          });
        },
        noteUpdated: (metadata: {
          appointment_id: string;
          note_length: number;
          is_org_schedule?: boolean;
          target_provider_id?: string;
        }) => {
          trackEvent(EventNames.APPOINTMENT_NOTE_UPDATED, {
            metadata: {
              ...metadata,
              note_length: String(metadata.note_length),
              has_note: String(metadata.note_length > 0),
            },
          });
        },

        // Card interaction methods
        cardExpanded: (metadata: {
          appointment_id: string;
          source: 'dashboard' | 'schedule' | 'appointment_collision' | 'appointment_edit';
          is_auto_expanded?: boolean;
          position?: number;
          is_org_schedule?: boolean;
        }) => {
          trackEvent(EventNames.APPOINTMENT_CARD_EXPANDED, {
            metadata: {
              ...(metadata.is_auto_expanded !== undefined && {
                is_auto_expanded: String(metadata.is_auto_expanded),
              }),
              ...(metadata.position !== undefined && {
                position: String(metadata.position),
              }),
              ...metadata,
            },
          });
        },
        cardCollapsed: (metadata: {
          appointment_id: string;
          source: 'dashboard' | 'schedule' | 'appointment_collision' | 'appointment_edit';
          is_org_schedule?: boolean;
        }) => {
          trackEvent(EventNames.APPOINTMENT_CARD_COLLAPSED, { metadata });
        },
        actionClicked: (metadata: {
          appointment_id: string;
          action:
            | 'record_session'
            | 'view_session'
            | 'view_profile'
            | 'reschedule'
            | 'cancel'
            | 'delete'
            | 'send_reminder'
            | 'mark_no_show'
            | 'suggestion_accept'
            | 'suggestion_reject';
          source: 'dashboard' | 'schedule' | 'appointment_collision' | 'appointment_edit';
          has_encounter?: boolean;
          is_org_schedule?: boolean;
        }) => {
          trackEvent(EventNames.APPOINTMENT_ACTION_CLICKED, {
            metadata: {
              ...(metadata.has_encounter !== undefined && {
                has_encounter: String(metadata.has_encounter),
              }),
              ...metadata,
            },
          });
        },
        // Delete-with-recorded-session warning (GDN-2325)
        deleteRecordingWarningOpened: (metadata: {
          appointment_id: string;
          encounter_id?: string;
          recording_count?: number;
          source: 'dashboard' | 'schedule' | 'appointment_collision' | 'appointment_edit';
        }) => {
          trackEvent(EventNames.APPOINTMENT_DELETE_RECORDING_WARNING_OPENED, {
            metadata: {
              ...(metadata.recording_count !== undefined && {
                recording_count: String(metadata.recording_count),
              }),
              ...metadata,
            },
          });
        },
        deleteRecordingWarningConfirmClicked: (metadata: {
          appointment_id: string;
          encounter_id?: string;
          recording_count?: number;
          source: 'dashboard' | 'schedule' | 'appointment_collision' | 'appointment_edit';
        }) => {
          trackEvent(EventNames.APPOINTMENT_DELETE_RECORDING_WARNING_CONFIRM_CLICKED, {
            metadata: {
              ...(metadata.recording_count !== undefined && {
                recording_count: String(metadata.recording_count),
              }),
              ...metadata,
            },
          });
        },
        deleteRecordingWarningCancelClicked: (metadata: {
          appointment_id: string;
          encounter_id?: string;
          recording_count?: number;
          source: 'dashboard' | 'schedule' | 'appointment_collision' | 'appointment_edit';
        }) => {
          trackEvent(EventNames.APPOINTMENT_DELETE_RECORDING_WARNING_CANCEL_CLICKED, {
            metadata: {
              ...(metadata.recording_count !== undefined && {
                recording_count: String(metadata.recording_count),
              }),
              ...metadata,
            },
          });
        },
        listViewed: (metadata: {
          source: 'dashboard' | 'schedule' | 'appointment_collision' | 'appointment_edit';
          appointment_count: number;
          visible_dates?: string[];
        }) => {
          trackEvent(EventNames.APPOINTMENT_LIST_VIEWED, {
            metadata: {
              ...metadata,
              appointment_count: String(metadata.appointment_count),
              ...(metadata.visible_dates && {
                visible_dates: metadata.visible_dates.join(','),
              }),
            },
          });
        },
        eventQuickActionOpened: (metadata: {
          appointment_id: string;
          actionable_event_type: 'suggested' | 'cancelled' | 'reschedule_requested';
          view_mode: 'month' | 'week' | 'day';
          is_touch_device: boolean;
        }) => {
          trackEvent(EventNames.APPOINTMENT_EVENT_QUICK_ACTION_OPENED, {
            metadata: {
              ...metadata,
              is_touch_device: String(metadata.is_touch_device),
            },
          });
        },
        eventQuickActionClicked: (metadata: {
          appointment_id: string;
          actionable_event_type: 'suggested' | 'cancelled' | 'reschedule_requested';
          action: 'approve' | 'edit' | 'remove' | 'deny' | 'contact' | 'acknowledge' | 'rebook';
          view_mode: 'month' | 'week' | 'day';
          is_touch_device: boolean;
        }) => {
          trackEvent(EventNames.APPOINTMENT_EVENT_QUICK_ACTION_CLICKED, {
            metadata: {
              ...metadata,
              is_touch_device: String(metadata.is_touch_device),
            },
          });
        },
        eventQuickActionCompleted: (metadata: {
          appointment_id: string;
          actionable_event_type: 'suggested' | 'cancelled' | 'reschedule_requested';
          action: 'approve' | 'remove' | 'deny' | 'acknowledge' | 'rebook';
          success: boolean;
          error_message?: string;
        }) => {
          trackEvent(EventNames.APPOINTMENT_EVENT_QUICK_ACTION_COMPLETED, {
            metadata: {
              ...metadata,
              success: String(metadata.success),
            },
          });
        },
      },

      blockedTime: {
        modalOpened: (metadata?: {
          mode?: 'create' | 'edit';
          source?: 'calendar' | 'event';
          is_org_schedule?: boolean;
        }) => {
          trackEvent(EventNames.BLOCKED_TIME_MODAL_OPENED, {
            metadata: {
              ...metadata,
              ...(metadata?.is_org_schedule !== undefined && {
                is_org_schedule: String(metadata.is_org_schedule),
              }),
            },
          });
        },
        modeSelected: (metadata: { mode: 'appointment' | 'blocked_time' }) => {
          trackEvent(EventNames.BLOCKED_TIME_MODE_SELECTED, { metadata });
        },
        createSubmitted: (metadata: {
          duration_minutes: number;
          is_all_day: boolean;
          is_recurring: boolean;
          scope: 'self' | 'provider' | 'organization';
          is_org_schedule?: boolean;
          has_notes?: boolean;
          label_count?: number;
        }) => {
          trackEvent(EventNames.BLOCKED_TIME_CREATE_SUBMITTED, {
            metadata: {
              ...metadata,
              duration_minutes: String(metadata.duration_minutes),
              is_all_day: String(metadata.is_all_day),
              is_recurring: String(metadata.is_recurring),
              is_org_schedule: String(metadata.is_org_schedule ?? false),
              has_notes: String(metadata.has_notes ?? false),
              label_count: String(metadata.label_count ?? 0),
            },
          });
        },
        updateSubmitted: (metadata: {
          blocked_time_id: string;
          duration_minutes: number;
          is_all_day: boolean;
          is_recurring: boolean;
          scope: 'self' | 'provider' | 'organization';
          is_org_schedule?: boolean;
          has_notes?: boolean;
          label_count?: number;
        }) => {
          trackEvent(EventNames.BLOCKED_TIME_UPDATE_SUBMITTED, {
            metadata: {
              ...metadata,
              duration_minutes: String(metadata.duration_minutes),
              is_all_day: String(metadata.is_all_day),
              is_recurring: String(metadata.is_recurring),
              is_org_schedule: String(metadata.is_org_schedule ?? false),
              has_notes: String(metadata.has_notes ?? false),
              label_count: String(metadata.label_count ?? 0),
            },
          });
        },
        deleteClicked: (metadata: { blocked_time_id: string; is_recurring: boolean }) => {
          trackEvent(EventNames.BLOCKED_TIME_DELETE_CLICKED, {
            metadata: {
              ...metadata,
              is_recurring: String(metadata.is_recurring),
            },
          });
        },
        dragSubmitted: (metadata: {
          blocked_time_id: string;
          duration_minutes: number;
          is_org_schedule?: boolean;
        }) => {
          trackEvent(EventNames.BLOCKED_TIME_DRAG_SUBMITTED, {
            metadata: {
              ...metadata,
              duration_minutes: String(metadata.duration_minutes),
              is_org_schedule: String(metadata.is_org_schedule ?? false),
            },
          });
        },
        resizeSubmitted: (metadata: {
          blocked_time_id: string;
          duration_minutes: number;
          is_org_schedule?: boolean;
        }) => {
          trackEvent(EventNames.BLOCKED_TIME_RESIZE_SUBMITTED, {
            metadata: {
              ...metadata,
              duration_minutes: String(metadata.duration_minutes),
              is_org_schedule: String(metadata.is_org_schedule ?? false),
            },
          });
        },
      },

      // Org Schedule metrics
      orgSchedule: {
        viewToggled: (metadata: { toggled_to: 'org' | 'my' }) => {
          trackEvent(EventNames.ORG_SCHEDULE_VIEW_TOGGLED, { metadata });
        },
        providerFilterChanged: (metadata: {
          selected_count: number;
          total_count: number;
          action: 'individual_toggle' | 'only' | 'show_all';
        }) => {
          trackEvent(EventNames.ORG_SCHEDULE_PROVIDER_FILTER_CHANGED, {
            metadata: {
              ...metadata,
              selected_count: String(metadata.selected_count),
              total_count: String(metadata.total_count),
            },
          });
        },
        patientFilterChanged: (metadata: {
          selected_count: number;
          action: 'added' | 'removed' | 'cleared';
        }) => {
          trackEvent(EventNames.ORG_SCHEDULE_PATIENT_FILTER_CHANGED, {
            metadata: {
              ...metadata,
              selected_count: String(metadata.selected_count),
            },
          });
        },
        providerReassigned: (metadata: {
          appointment_id: string;
          previous_provider_id: string | null;
          new_provider_id: string | null;
        }) => {
          trackEvent(EventNames.ORG_SCHEDULE_PROVIDER_REASSIGNED, { metadata });
        },
      },

      // Schedule density (capacity clusters) metrics
      scheduleDensity: {
        clusterCardClicked: (metadata: {
          member_count: number;
          provider_count: number;
          view_mode: string;
        }) => {
          trackEvent(EventNames.SCHEDULE_CLUSTER_CARD_CLICKED, {
            metadata: {
              member_count: String(metadata.member_count),
              provider_count: String(metadata.provider_count),
              view_mode: metadata.view_mode,
            },
          });
        },
        clusterAppointmentSelected: (metadata: {
          appointment_id: string;
          member_count: number;
        }) => {
          trackEvent(EventNames.SCHEDULE_CLUSTER_APPOINTMENT_SELECTED, {
            metadata: {
              appointment_id: metadata.appointment_id,
              member_count: String(metadata.member_count),
            },
          });
        },
        clusterDensityToggled: (metadata: { toggled_to: 'on' | 'off' }) => {
          trackEvent(EventNames.SCHEDULE_CLUSTER_DENSITY_TOGGLED, { metadata });
        },
      },

      // Encounter workflow metrics
      encounter: {
        // Recording lifecycle
        recordingStartClicked: (metadata?: {
          encounter_id?: string;
          has_patient?: boolean;
          has_appointment_type?: boolean;
          microphone_selected?: string;
        }) => {
          trackEvent(EventNames.ENCOUNTER_RECORDING_START_CLICKED, {
            metadata: {
              ...(metadata?.has_patient !== undefined && {
                has_patient: String(metadata.has_patient),
              }),
              ...(metadata?.has_appointment_type !== undefined && {
                has_appointment_type: String(metadata.has_appointment_type),
              }),
              ...metadata,
            },
          });
        },
        recordingPauseClicked: (metadata?: {
          encounter_id?: string;
          elapsed_time?: number;
          is_remote_recording?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_RECORDING_PAUSE_CLICKED, {
            metadata: {
              ...(metadata?.elapsed_time !== undefined && {
                elapsed_time: String(metadata.elapsed_time),
              }),
              ...(metadata?.is_remote_recording !== undefined && {
                is_remote_recording: String(metadata.is_remote_recording),
              }),
              ...metadata,
            },
          });
        },
        recordingResumeClicked: (metadata?: {
          encounter_id?: string;
          pause_duration?: number;
          total_pauses?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_RECORDING_RESUME_CLICKED, {
            metadata: {
              ...(metadata?.pause_duration !== undefined && {
                pause_duration: String(metadata.pause_duration),
              }),
              ...(metadata?.total_pauses !== undefined && {
                total_pauses: String(metadata.total_pauses),
              }),
              ...metadata,
            },
          });
        },
        recordingStopClicked: (metadata?: {
          encounter_id?: string;
          elapsed_time?: number;
          dictation_count?: number;
          is_remote_recording?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_RECORDING_STOP_CLICKED, {
            metadata: {
              ...(metadata?.elapsed_time !== undefined && {
                elapsed_time: String(metadata.elapsed_time),
              }),
              ...(metadata?.dictation_count !== undefined && {
                dictation_count: String(metadata.dictation_count),
              }),
              ...(metadata?.is_remote_recording !== undefined && {
                is_remote_recording: String(metadata.is_remote_recording),
              }),
              ...metadata,
            },
          });
        },

        // Microphone setup
        microphoneMenuOpened: (metadata?: {
          encounter_id?: string;
          current_microphone?: string;
          available_count?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_MICROPHONE_MENU_OPENED, {
            metadata: {
              ...(metadata?.available_count !== undefined && {
                available_count: String(metadata.available_count),
              }),
              ...metadata,
            },
          });
        },
        microphoneChanged: (metadata?: {
          encounter_id?: string;
          previous_mic?: string;
          new_mic?: string;
          during_recording?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_MICROPHONE_CHANGED, {
            metadata: {
              ...(metadata?.during_recording !== undefined && {
                during_recording: String(metadata.during_recording),
              }),
              ...metadata,
            },
          });
        },
        micTestStarted: (metadata?: { encounter_id?: string; microphone_id?: string }) => {
          trackEvent(EventNames.ENCOUNTER_MIC_TEST_STARTED, { metadata });
        },
        micTestCompleted: (metadata?: {
          encounter_id?: string;
          test_result?: 'success' | 'failure' | 'timeout';
          audio_detected?: boolean;
          timeout?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_MIC_TEST_COMPLETED, {
            metadata: {
              ...(metadata?.audio_detected !== undefined && {
                audio_detected: String(metadata.audio_detected),
              }),
              ...(metadata?.timeout !== undefined && {
                timeout: String(metadata.timeout),
              }),
              ...metadata,
            },
          });
        },
        headphonesModeToggled: (metadata?: {
          encounter_id?: string;
          enabled?: boolean;
          telehealth_active?: boolean;
          during_recording?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_HEADPHONES_MODE_TOGGLED, {
            metadata: {
              ...(metadata?.enabled !== undefined && {
                enabled: String(metadata.enabled),
              }),
              ...(metadata?.telehealth_active !== undefined && {
                telehealth_active: String(metadata.telehealth_active),
              }),
              ...(metadata?.during_recording !== undefined && {
                during_recording: String(metadata.during_recording),
              }),
              ...metadata,
            },
          });
        },

        // Cross-tab recording
        recordingTakeoverPrompted: (metadata?: { encounter_id?: string }) => {
          trackEvent(EventNames.ENCOUNTER_RECORDING_TAKEOVER_PROMPTED, { metadata });
        },
        recordingTakeoverAccepted: (metadata?: { encounter_id?: string }) => {
          trackEvent(EventNames.ENCOUNTER_RECORDING_TAKEOVER_ACCEPTED, { metadata });
        },
        recordingTakeoverCancelled: (metadata?: { encounter_id?: string }) => {
          trackEvent(EventNames.ENCOUNTER_RECORDING_TAKEOVER_CANCELLED, { metadata });
        },

        // Patient selection
        patientSelected: (metadata?: {
          encounter_id?: string;
          patient_id?: string;
          source?: string;
          has_previous_patient?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_PATIENT_SELECTED, {
            metadata: {
              ...(metadata?.has_previous_patient !== undefined && {
                has_previous_patient: String(metadata.has_previous_patient),
              }),
              ...metadata,
            },
          });
        },
        patientRemoved: (metadata?: { encounter_id?: string; patient_id?: string }) => {
          trackEvent(EventNames.ENCOUNTER_PATIENT_REMOVED, { metadata });
        },
        patientClicked: (metadata?: { encounter_id?: string; patient_id?: string }) => {
          trackEvent(EventNames.ENCOUNTER_PATIENT_CLICKED, { metadata });
        },

        // Session configuration
        appointmentTypeChanged: (metadata?: {
          encounter_id?: string;
          previous_type?: string;
          new_type?: string;
          has_recordings?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_APPOINTMENT_TYPE_CHANGED, {
            metadata: {
              ...(metadata?.has_recordings !== undefined && {
                has_recordings: String(metadata.has_recordings),
              }),
              ...metadata,
            },
          });
        },
        durationEdited: (metadata?: {
          encounter_id?: string;
          default_duration?: number;
          new_duration?: number;
          is_dirty?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DURATION_EDITED, {
            metadata: {
              ...(metadata?.default_duration !== undefined && {
                default_duration: String(metadata.default_duration),
              }),
              ...(metadata?.new_duration !== undefined && {
                new_duration: String(metadata.new_duration),
              }),
              ...(metadata?.is_dirty !== undefined && {
                is_dirty: String(metadata.is_dirty),
              }),
              ...metadata,
            },
          });
        },
        telehealthToggled: (metadata?: {
          encounter_id?: string;
          enabled?: boolean;
          default_value?: boolean;
          is_dirty?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_TELEHEALTH_TOGGLED, {
            metadata: {
              ...(metadata?.enabled !== undefined && {
                enabled: String(metadata.enabled),
              }),
              ...(metadata?.default_value !== undefined && {
                default_value: String(metadata.default_value),
              }),
              ...(metadata?.is_dirty !== undefined && {
                is_dirty: String(metadata.is_dirty),
              }),
              ...metadata,
            },
          });
        },
        cptCodeEdited: (metadata?: {
          encounter_id?: string;
          code_count?: number;
          default_codes?: string[];
          is_dirty?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_CPT_CODE_EDITED, {
            metadata: {
              ...(metadata?.code_count !== undefined && {
                code_count: String(metadata.code_count),
              }),
              ...(metadata?.default_codes && {
                default_codes: metadata.default_codes.join(','),
              }),
              ...(metadata?.is_dirty !== undefined && {
                is_dirty: String(metadata.is_dirty),
              }),
              ...metadata,
            },
          });
        },
        templateChanged: (metadata?: {
          encounter_id?: string;
          previous_template?: string;
          new_template?: string;
          is_dirty?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_TEMPLATE_CHANGED, {
            metadata: {
              ...(metadata?.is_dirty !== undefined && {
                is_dirty: String(metadata.is_dirty),
              }),
              ...metadata,
            },
          });
        },
        sessionChipsMenuOpened: (metadata?: { encounter_id?: string; chip_type?: string }) => {
          trackEvent(EventNames.ENCOUNTER_SESSION_CHIPS_MENU_OPENED, { metadata });
        },
        billingPopoverOpened: (metadata?: { encounter_id?: string; focus_section?: string }) => {
          trackEvent(EventNames.ENCOUNTER_BILLING_POPOVER_OPENED, { metadata });
        },
        billingPopoverSectionToggled: (metadata?: {
          encounter_id?: string;
          section?: string;
          expanded?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_BILLING_POPOVER_SECTION_TOGGLED, {
            metadata: {
              ...(metadata?.expanded !== undefined && {
                expanded: String(metadata.expanded),
              }),
              ...metadata,
            },
          });
        },
        attestationToggled: (metadata?: {
          encounter_id?: string;
          attestation_type?: string;
          checked?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_ATTESTATION_TOGGLED, {
            metadata: {
              ...(metadata?.checked !== undefined && {
                checked: String(metadata.checked),
              }),
              ...metadata,
            },
          });
        },
        billingSignNoteClicked: (metadata?: {
          encounter_id?: string;
          has_patient?: boolean;
          has_note_signing?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_BILLING_SIGN_NOTE_CLICKED, {
            metadata: {
              ...(metadata?.has_patient !== undefined && {
                has_patient: String(metadata.has_patient),
              }),
              ...(metadata?.has_note_signing !== undefined && {
                has_note_signing: String(metadata.has_note_signing),
              }),
              ...metadata,
            },
          });
        },
        // Workflow completion
        saveForLaterClicked: (
          encounterId: string,
          metadata?: {
            source?: 'recording_widget' | 'bottom_bar';
            has_patient?: boolean;
            has_appointment?: boolean;
            has_recordings?: boolean;
            recordings_count?: number;
            has_documents?: boolean;
            documents_count?: number;
          },
        ) => {
          trackEvent(EventNames.ENCOUNTER_SAVE_FOR_LATER_CLICKED, {
            metadata: {
              encounter_id: encounterId,
              ...(metadata?.source && { source: metadata.source }),
              ...(metadata?.has_patient !== undefined && {
                has_patient: String(metadata.has_patient),
              }),
              ...(metadata?.has_appointment !== undefined && {
                has_appointment: String(metadata.has_appointment),
              }),
              ...(metadata?.has_recordings !== undefined && {
                has_recordings: String(metadata.has_recordings),
              }),
              ...(metadata?.recordings_count !== undefined && {
                recordings_count: String(metadata.recordings_count),
              }),
              ...(metadata?.has_documents !== undefined && {
                has_documents: String(metadata.has_documents),
              }),
              ...(metadata?.documents_count !== undefined && {
                documents_count: String(metadata.documents_count),
              }),
              ...metadata,
            },
          });
        },
        generateNoteClicked: (metadata?: {
          encounter_id?: string;
          has_patient?: boolean;
          recordings_count?: number;
          has_notepad?: boolean;
          template_id?: string;
          has_cpt_codes?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_GENERATE_NOTE_CLICKED, {
            metadata: {
              ...(metadata?.has_patient !== undefined && {
                has_patient: String(metadata.has_patient),
              }),
              ...(metadata?.recordings_count !== undefined && {
                recordings_count: String(metadata.recordings_count),
              }),
              ...(metadata?.has_notepad !== undefined && {
                has_notepad: String(metadata.has_notepad),
              }),
              ...(metadata?.has_cpt_codes !== undefined && {
                has_cpt_codes: String(metadata.has_cpt_codes),
              }),
              ...metadata,
            },
          });
        },
        generateNoteCompleted: (metadata?: {
          encounter_id?: string;
          note_id?: string;
          time_to_submit_ms?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_GENERATE_NOTE_COMPLETED, {
            metadata: {
              ...(metadata?.time_to_submit_ms !== undefined && {
                time_to_submit_ms: String(metadata.time_to_submit_ms),
              }),
              ...metadata,
            },
          });
        },
        generateNoteFailed: (metadata?: {
          encounter_id?: string;
          error_code?: string;
          error_message?: string;
        }) => {
          trackEvent(EventNames.ENCOUNTER_GENERATE_NOTE_FAILED, { metadata });
        },

        // Dictation management
        dictationClicked: (metadata?: {
          encounter_id?: string;
          dictation_id?: string;
          duration?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DICTATION_CLICKED, {
            metadata: {
              ...(metadata?.duration !== undefined && {
                duration: String(metadata.duration),
              }),
              ...metadata,
            },
          });
        },
        addDictationClicked: (metadata?: {
          encounter_id?: string;
          existing_dictations_count?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_ADD_DICTATION_CLICKED, {
            metadata: {
              ...(metadata?.existing_dictations_count !== undefined && {
                existing_dictations_count: String(metadata.existing_dictations_count),
              }),
              ...metadata,
            },
          });
        },
        dictationDeleteClicked: (metadata?: {
          encounter_id?: string;
          dictation_id?: string;
          duration?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DICTATION_DELETE_CLICKED, {
            metadata: {
              ...(metadata?.duration !== undefined && {
                duration: String(metadata.duration),
              }),
              ...metadata,
            },
          });
        },
        dictationDeleted: (metadata?: {
          encounter_id?: string;
          dictation_id?: string;
          dictations_remaining?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DICTATION_DELETED, {
            metadata: {
              ...(metadata?.dictations_remaining !== undefined && {
                dictations_remaining: String(metadata.dictations_remaining),
              }),
              ...metadata,
            },
          });
        },
        dictationDeleteCancelled: (metadata?: { encounter_id?: string; dictation_id?: string }) => {
          trackEvent(EventNames.ENCOUNTER_DICTATION_DELETE_CANCELLED, { metadata });
        },

        // Delete all transcripts - permanently removes all transcription data
        deleteAllTranscriptsClicked: (metadata?: {
          encounter_id?: string;
          dictation_count?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DELETE_ALL_TRANSCRIPTS_CLICKED, {
            metadata: {
              ...(metadata?.dictation_count !== undefined && {
                dictation_count: String(metadata.dictation_count),
              }),
              ...metadata,
            },
          });
        },
        deleteAllTranscriptsConfirmed: (metadata?: {
          encounter_id?: string;
          dictation_count?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DELETE_ALL_TRANSCRIPTS_CONFIRMED, {
            metadata: {
              ...(metadata?.dictation_count !== undefined && {
                dictation_count: String(metadata.dictation_count),
              }),
              ...metadata,
            },
          });
        },
        deleteAllTranscriptsCancelled: (metadata?: { encounter_id?: string }) => {
          trackEvent(EventNames.ENCOUNTER_DELETE_ALL_TRANSCRIPTS_CANCELLED, { metadata });
        },
        deleteAllTranscriptsCompleted: (metadata?: {
          encounter_id?: string;
          transcription_sessions_deleted?: number;
          notes_cleared?: number;
          analysis_sessions_deleted?: number;
          encounter_documents_deleted?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DELETE_ALL_TRANSCRIPTS_COMPLETED, {
            metadata: {
              ...(metadata?.transcription_sessions_deleted !== undefined && {
                transcription_sessions_deleted: String(metadata.transcription_sessions_deleted),
              }),
              ...(metadata?.notes_cleared !== undefined && {
                notes_cleared: String(metadata.notes_cleared),
              }),
              ...(metadata?.analysis_sessions_deleted !== undefined && {
                analysis_sessions_deleted: String(metadata.analysis_sessions_deleted),
              }),
              ...(metadata?.encounter_documents_deleted !== undefined && {
                encounter_documents_deleted: String(metadata.encounter_documents_deleted),
              }),
              ...metadata,
            },
          });
        },
        deleteAllTranscriptsFailed: (metadata?: {
          encounter_id?: string;
          error_message?: string;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DELETE_ALL_TRANSCRIPTS_FAILED, { metadata });
        },

        // Delete-encounter-with-recording warning (GDN-2325)
        deleteRecordingWarningOpened: (metadata: {
          encounter_id: string;
          recording_count?: number;
          has_appointment?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DELETE_RECORDING_WARNING_OPENED, {
            metadata: {
              ...(metadata.recording_count !== undefined && {
                recording_count: String(metadata.recording_count),
              }),
              ...(metadata.has_appointment !== undefined && {
                has_appointment: String(metadata.has_appointment),
              }),
              ...metadata,
            },
          });
        },
        deleteRecordingWarningConfirmClicked: (metadata: {
          encounter_id: string;
          recording_count?: number;
          has_appointment?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DELETE_RECORDING_WARNING_CONFIRM_CLICKED, {
            metadata: {
              ...(metadata.recording_count !== undefined && {
                recording_count: String(metadata.recording_count),
              }),
              ...(metadata.has_appointment !== undefined && {
                has_appointment: String(metadata.has_appointment),
              }),
              ...metadata,
            },
          });
        },
        deleteRecordingWarningCancelClicked: (metadata: {
          encounter_id: string;
          recording_count?: number;
          has_appointment?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DELETE_RECORDING_WARNING_CANCEL_CLICKED, {
            metadata: {
              ...(metadata.recording_count !== undefined && {
                recording_count: String(metadata.recording_count),
              }),
              ...(metadata.has_appointment !== undefined && {
                has_appointment: String(metadata.has_appointment),
              }),
              ...metadata,
            },
          });
        },

        // Document editor
        documentEditorOpened: (metadata?: {
          encounter_id?: string;
          document_id?: string;
          document_type?: string;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DOCUMENT_EDITOR_OPENED, { metadata });
        },
        documentEditorClosed: (metadata?: {
          encounter_id?: string;
          document_id?: string;
          time_open_ms?: number;
          content_edited?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DOCUMENT_EDITOR_CLOSED, {
            metadata: {
              ...(metadata?.time_open_ms !== undefined && {
                time_open_ms: String(metadata.time_open_ms),
              }),
              ...(metadata?.content_edited !== undefined && {
                content_edited: String(metadata.content_edited),
              }),
              ...metadata,
            },
          });
        },
        documentTitleEdited: (metadata?: {
          encounter_id?: string;
          document_id?: string;
          title_length?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DOCUMENT_TITLE_EDITED, {
            metadata: {
              ...(metadata?.title_length !== undefined && {
                title_length: String(metadata.title_length),
              }),
              ...metadata,
            },
          });
        },
        documentContentEdited: (metadata?: {
          encounter_id?: string;
          document_id?: string;
          content_length_delta?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DOCUMENT_CONTENT_EDITED, {
            metadata: {
              ...(metadata?.content_length_delta !== undefined && {
                content_length_delta: String(metadata.content_length_delta),
              }),
              ...metadata,
            },
          });
        },
        documentDeletedFromEditor: (metadata?: {
          encounter_id?: string;
          document_id?: string;
          document_type?: string;
        }) => {
          trackEvent(EventNames.ENCOUNTER_DOCUMENT_DELETED_FROM_EDITOR, { metadata });
        },

        // Notepad
        notepadFocused: (metadata?: {
          encounter_id?: string;
          has_patient?: boolean;
          has_recordings?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_NOTEPAD_FOCUSED, {
            metadata: {
              ...(metadata?.has_patient !== undefined && {
                has_patient: String(metadata.has_patient),
              }),
              ...(metadata?.has_recordings !== undefined && {
                has_recordings: String(metadata.has_recordings),
              }),
              ...metadata,
            },
          });
        },
        notepadContentAdded: (metadata?: {
          encounter_id?: string;
          content_length?: number;
          is_first_save?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_NOTEPAD_CONTENT_ADDED, {
            metadata: {
              ...(metadata?.content_length !== undefined && {
                content_length: String(metadata.content_length),
              }),
              ...(metadata?.is_first_save !== undefined && {
                is_first_save: String(metadata.is_first_save),
              }),
              ...metadata,
            },
          });
        },
        notepadExpanded: (metadata?: { encounter_id?: string; content_length?: number }) => {
          trackEvent(EventNames.ENCOUNTER_NOTEPAD_EXPANDED, {
            metadata: {
              ...(metadata?.content_length !== undefined && {
                content_length: String(metadata.content_length),
              }),
              ...metadata,
            },
          });
        },
        notepadCollapsed: (metadata?: {
          encounter_id?: string;
          time_expanded_ms?: number;
          content_changed?: boolean;
        }) => {
          trackEvent(EventNames.ENCOUNTER_NOTEPAD_COLLAPSED, {
            metadata: {
              ...(metadata?.time_expanded_ms !== undefined && {
                time_expanded_ms: String(metadata.time_expanded_ms),
              }),
              ...(metadata?.content_changed !== undefined && {
                content_changed: String(metadata.content_changed),
              }),
              ...metadata,
            },
          });
        },

        // Patient info tabs
        patientInfoTabChanged: (metadata?: {
          encounter_id?: string;
          patient_id?: string;
          tab_from?: string;
          tab_to?: string;
          time_in_previous_tab_ms?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_PATIENT_INFO_TAB_CHANGED, {
            metadata: {
              ...(metadata?.time_in_previous_tab_ms !== undefined && {
                time_in_previous_tab_ms: String(metadata.time_in_previous_tab_ms),
              }),
              ...metadata,
            },
          });
        },
        patientDemographicsViewed: (metadata?: {
          encounter_id?: string;
          patient_id?: string;
          view_duration_ms?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_PATIENT_DEMOGRAPHICS_VIEWED, {
            metadata: {
              ...(metadata?.view_duration_ms !== undefined && {
                view_duration_ms: String(metadata.view_duration_ms),
              }),
              ...metadata,
            },
          });
        },
        lastEncounterViewed: (metadata?: {
          encounter_id?: string;
          patient_id?: string;
          has_summary?: boolean;
          last_encounter_date?: string;
        }) => {
          trackEvent(EventNames.ENCOUNTER_LAST_ENCOUNTER_VIEWED, {
            metadata: {
              ...(metadata?.has_summary !== undefined && {
                has_summary: String(metadata.has_summary),
              }),
              ...metadata,
            },
          });
        },

        // Memos tab — session-reminders empty-state ad
        memosReminderAdShown: (metadata?: { encounter_id?: string; patient_id?: string }) => {
          trackEvent(EventNames.MEMOS_REMINDER_AD_SHOWN, { metadata });
        },
        memosReminderAdDismissed: (metadata?: {
          encounter_id?: string;
          patient_id?: string;
          dismiss_reason?: 'x_click' | 'reminder_rendered';
        }) => {
          trackEvent(EventNames.MEMOS_REMINDER_AD_DISMISSED, { metadata });
        },

        // View & Navigation
        viewLoaded: (metadata?: {
          encounter_id?: string;
          is_new?: boolean;
          is_active_route?: boolean;
          load_time_ms?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_VIEW_LOADED, {
            metadata: {
              ...(metadata?.is_new !== undefined && {
                is_new: String(metadata.is_new),
              }),
              ...(metadata?.is_active_route !== undefined && {
                is_active_route: String(metadata.is_active_route),
              }),
              ...(metadata?.load_time_ms !== undefined && {
                load_time_ms: String(metadata.load_time_ms),
              }),
              ...metadata,
            },
          });
        },
        redirectedToNote: (metadata?: {
          encounter_id?: string;
          status?: string;
          note_id?: string;
        }) => {
          trackEvent(EventNames.ENCOUNTER_REDIRECTED_TO_NOTE, { metadata });
        },
        activeRouteCreated: (metadata?: { encounter_id?: string; has_default_type?: boolean }) => {
          trackEvent(EventNames.ENCOUNTER_ACTIVE_ROUTE_CREATED, {
            metadata: {
              ...(metadata?.has_default_type !== undefined && {
                has_default_type: String(metadata.has_default_type),
              }),
              ...metadata,
            },
          });
        },

        // Error states
        notFound: (metadata?: { encounter_id?: string }) => {
          trackEvent(EventNames.ENCOUNTER_NOT_FOUND, { metadata });
        },
        initializationError: (metadata?: { encounter_id?: string; error_message?: string }) => {
          trackEvent(EventNames.ENCOUNTER_INITIALIZATION_ERROR, { metadata });
        },
        audioPermissionDenied: (metadata?: { encounter_id?: string }) => {
          trackEvent(EventNames.ENCOUNTER_AUDIO_PERMISSION_DENIED, { metadata });
        },
        remoteRecordingDetected: (metadata?: { encounter_id?: string; is_owner?: boolean }) => {
          trackEvent(EventNames.ENCOUNTER_REMOTE_RECORDING_DETECTED, {
            metadata: {
              ...(metadata?.is_owner !== undefined && {
                is_owner: String(metadata.is_owner),
              }),
              ...metadata,
            },
          });
        },

        // Additional session configuration
        icd10CodesEdited: (metadata?: {
          encounter_id?: string;
          code_count?: number;
          codes?: string[];
        }) => {
          trackEvent(EventNames.ENCOUNTER_ICD10_CODES_EDITED, {
            metadata: {
              ...(metadata?.code_count !== undefined && {
                code_count: String(metadata.code_count),
              }),
              ...(metadata?.codes && {
                codes: metadata.codes.join(','),
              }),
              ...metadata,
            },
          });
        },
        placeOfServiceEdited: (metadata?: { encounter_id?: string; new_value?: string }) => {
          trackEvent(EventNames.ENCOUNTER_PLACE_OF_SERVICE_EDITED, { metadata });
        },
        noteTitleSaved: (metadata?: { encounter_id?: string; has_title?: boolean }) => {
          trackEvent(EventNames.ENCOUNTER_NOTE_TITLE_SAVED, {
            metadata: {
              ...(metadata?.has_title !== undefined && {
                has_title: String(metadata.has_title),
              }),
              ...metadata,
            },
          });
        },
        noteTitleCleared: (metadata?: { encounter_id?: string; source?: string }) => {
          trackEvent(EventNames.ENCOUNTER_NOTE_TITLE_CLEARED, { metadata });
        },
        patientPickerOpened: (metadata?: { encounter_id?: string; has_patient?: boolean }) => {
          trackEvent(EventNames.ENCOUNTER_PATIENT_PICKER_OPENED, {
            metadata: {
              ...(metadata?.has_patient !== undefined && {
                has_patient: String(metadata.has_patient),
              }),
              ...metadata,
            },
          });
        },
        lastEncounterNoteClicked: (metadata?: {
          encounter_id?: string;
          note_id?: string;
          patient_id?: string;
        }) => {
          trackEvent(EventNames.ENCOUNTER_LAST_ENCOUNTER_NOTE_CLICKED, { metadata });
        },

        // Child component interactions
        pipelineRetryClicked: (metadata?: {
          encounter_id?: string;
          error_message?: string;
          retry_count?: number;
        }) => {
          trackEvent(EventNames.ENCOUNTER_PIPELINE_RETRY_CLICKED, {
            metadata: {
              ...(metadata?.retry_count !== undefined && {
                retry_count: String(metadata.retry_count),
              }),
              ...metadata,
            },
          });
        },
        iosRecordingTipDismissed: (metadata?: {
          encounter_id?: string;
          dismiss_type?: 'manual' | 'auto';
        }) => {
          trackEvent(EventNames.ENCOUNTER_IOS_RECORDING_TIP_DISMISSED, { metadata });
        },
      },

      // Google Calendar metrics
      googleCalendar: {
        connectInitiated: (metadata?: { integration_source?: string }) => {
          trackEvent(EventNames.GOOGLE_CALENDAR_CONNECT_INITIATED, { metadata });
        },
        connected: (metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.GOOGLE_CALENDAR_CONNECTED, { metadata });
        },
        connectionFailed: (metadata?: { error_type?: string; error_message?: string }) => {
          trackEvent(EventNames.GOOGLE_CALENDAR_CONNECTION_FAILED, { metadata });
        },
        syncCompleted: (metadata?: { integration_source?: string }) => {
          trackEvent(EventNames.GOOGLE_CALENDAR_SYNC_COMPLETED, { metadata });
        },
        disconnected: (metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.GOOGLE_CALENDAR_DISCONNECTED, { metadata });
        },
      },

      // Stripe Connect metrics
      stripeConnect: {
        initiated: (metadata?: { source?: string }) => {
          trackEvent(EventNames.STRIPE_CONNECT_INITIATED, { metadata });
        },
        completed: (metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.STRIPE_CONNECT_COMPLETED, { metadata });
        },
        failed: (metadata?: { error_type?: string; error_message?: string }) => {
          trackEvent(EventNames.STRIPE_CONNECT_FAILED, { metadata });
        },
        dashboardOpened: (metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.STRIPE_DASHBOARD_OPENED, { metadata });
        },
      },

      // Payment metrics
      payment: {
        requestInitiated: (metadata?: { source?: string; amount?: number }) => {
          trackEvent(EventNames.PAYMENT_REQUEST_INITIATED, {
            metadata: {
              ...(metadata?.amount && { amount: String(metadata.amount) }),
              ...metadata,
            },
          });
        },
        modalOpened: (metadata?: { source?: string }) => {
          trackEvent(EventNames.PAYMENT_MODAL_OPENED, { metadata });
        },
        requestSent: (metadata?: {
          source?: string;
          amount?: number;
          delivery_method?: string;
          /** How the composer's NEW ad-hoc charge got its home (GDN-3086).
           * Absent when no new charge was minted. 'account_explicit' is the
           * biller deliberately keeping it at account level — distinct from
           * the historical strand, where the question was never asked. */
          new_charge_attribution?: 'context' | 'encounter_selected' | 'account_explicit';
        }) => {
          trackEvent(EventNames.PAYMENT_REQUEST_SENT, {
            metadata: {
              ...(metadata?.amount && { amount: String(metadata.amount) }),
              ...metadata,
            },
          });
        },
        requestFailed: (metadata?: {
          source?: string;
          error_type?: string;
          error_message?: string;
        }) => {
          trackEvent(EventNames.PAYMENT_REQUEST_FAILED, { metadata });
        },
        invoicesViewed: (metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.STRIPE_INVOICES_VIEWED, { metadata });
        },

        // Patient Payment Ingestion (Phase 2B)
        paymentRecorded: (metadata?: {
          encounter_id?: string;
          payment_method?: string;
          amount_cents?: number;
          allocated?: boolean;
        }) => {
          trackEvent(EventNames.BILLING_PAYMENT_RECORDED, {
            metadata: {
              ...(metadata?.amount_cents !== undefined && {
                amount_cents: String(metadata.amount_cents),
              }),
              ...metadata,
            },
          });
        },
        chargesCaptured: (metadata?: {
          encounter_id?: string;
          item_count?: number;
          total_charge_cents?: number;
        }) => {
          trackEvent(EventNames.BILLING_CHARGES_CAPTURED, {
            metadata: {
              ...(metadata?.item_count !== undefined && {
                item_count: String(metadata.item_count),
              }),
              ...(metadata?.total_charge_cents !== undefined && {
                total_charge_cents: String(metadata.total_charge_cents),
              }),
              ...metadata,
            },
          });
        },
        // Settle sheet V2 — amount-first allocation (GDN-2400)
        settleAllocationToggled: (metadata?: {
          target_kind?: string;
          included?: boolean;
          encounter_id?: string;
          source?: string;
        }) => {
          trackEvent(EventNames.SETTLE_ALLOCATION_PREVIEW_TOGGLED, {
            metadata: {
              ...metadata,
              ...(metadata?.included !== undefined && {
                included: String(metadata.included),
              }),
            },
          });
        },
        /** A choice made in the charge-attribution picker (GDN-3086) —
         * fired at selection, not submit, so attempted-then-abandoned
         * attributions keep their denominator (the submitted answer rides
         * payment_request_sent's new_charge_attribution). */
        settleChargeAttributionSelected: (metadata?: {
          kind?: 'encounter' | 'account';
          source?: string;
        }) => {
          trackEvent(EventNames.SETTLE_CHARGE_ATTRIBUTION_SELECTED, {
            metadata,
          });
        },
        settleCopayChipClicked: (metadata?: {
          amount_cents?: number;
          encounter_id?: string;
          source?: string;
        }) => {
          trackEvent(EventNames.SETTLE_COPAY_CHIP_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.amount_cents !== undefined && {
                amount_cents: String(metadata.amount_cents),
              }),
            },
          });
        },
        /**
         * Settle sheet — the unapplied-credit suggestion's verb (GDN-3175).
         * Tells us whether leading with the bookkeeping truth actually
         * routes billers to the allocation, which is the claim that
         * justified un-hiding the collect verb in the review state.
         */
        settleCreditSuggestionClicked: (metadata?: { amount_cents?: number; source?: string }) => {
          trackEvent(EventNames.SETTLE_CREDIT_SUGGESTION_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.amount_cents !== undefined && {
                amount_cents: String(metadata.amount_cents),
              }),
            },
          });
        },
        settleCreditRemainderSubmitted: (metadata?: {
          amount_cents?: number;
          credit_cents?: number;
          payment_method?: string;
          encounter_id?: string;
          source?: string;
        }) => {
          trackEvent(EventNames.SETTLE_CREDIT_REMAINDER_SUBMITTED, {
            metadata: {
              ...metadata,
              ...(metadata?.amount_cents !== undefined && {
                amount_cents: String(metadata.amount_cents),
              }),
              ...(metadata?.credit_cents !== undefined && {
                credit_cents: String(metadata.credit_cents),
              }),
            },
          });
        },
        // Settle sheet — copay declaration (GDN-2405)
        settlePaymentIntentChanged: (metadata?: {
          intent?: 'copay' | 'general';
          encounter_id?: string;
          source?: string;
        }) => {
          trackEvent(EventNames.SETTLE_PAYMENT_INTENT_CHANGED, { metadata });
        },
        settleCopayDeclaredSubmitted: (metadata?: {
          amount_cents?: number;
          encounter_id?: string;
          payment_method?: string;
          used_suggestion?: boolean;
          source?: string;
        }) => {
          trackEvent(EventNames.SETTLE_COPAY_DECLARED_SUBMITTED, {
            metadata: {
              ...metadata,
              ...(metadata?.amount_cents !== undefined && {
                amount_cents: String(metadata.amount_cents),
              }),
              ...(metadata?.used_suggestion !== undefined && {
                used_suggestion: String(metadata.used_suggestion),
              }),
            },
          });
        },
        // Statements Queue
        statementsQueue: {
          viewed: (metadata?: { total_count?: number }) => {
            trackEvent(EventNames.STATEMENTS_QUEUE_VIEWED, {
              metadata: {
                ...(metadata?.total_count !== undefined && {
                  total_count: String(metadata.total_count),
                }),
              },
            });
          },
          pageChanged: (metadata?: { page?: number; per_page?: number }) => {
            trackEvent(EventNames.STATEMENTS_QUEUE_PAGE_CHANGED, {
              metadata: {
                ...(metadata?.page !== undefined && { page: String(metadata.page) }),
                ...(metadata?.per_page !== undefined && { per_page: String(metadata.per_page) }),
              },
            });
          },
          historyViewed: (metadata?: { total_count?: number }) => {
            trackEvent(EventNames.STATEMENTS_HISTORY_VIEWED, {
              metadata: {
                ...(metadata?.total_count !== undefined && {
                  total_count: String(metadata.total_count),
                }),
              },
            });
          },
        },
      },

      // SMS Onboarding metrics
      smsOnboarding: {
        initiated: (metadata?: { source?: string }) => {
          trackEvent(EventNames.SMS_ONBOARDING_INITIATED, { metadata });
        },
        completed: (metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.SMS_ONBOARDING_COMPLETED, { metadata });
        },
      },

      // Phone Verification metrics (user signup)
      phoneVerification: {
        requested: (metadata?: { source?: string }) => {
          trackEvent(EventNames.PHONE_VERIFICATION_REQUESTED, { metadata });
        },
        modalOpened: () => {
          trackEvent(EventNames.PHONE_VERIFICATION_MODAL_OPENED, { metadata: {} });
        },
        submitted: (metadata: { is_valid: boolean; attempt_number: number }) => {
          trackEvent(EventNames.PHONE_VERIFICATION_SUBMITTED, {
            metadata: {
              is_valid: String(metadata.is_valid),
              attempt_number: String(metadata.attempt_number),
            },
          });
        },
        completed: (metadata?: { attempts: number }) => {
          trackEvent(EventNames.PHONE_VERIFICATION_COMPLETED, {
            metadata: {
              ...(metadata?.attempts !== undefined && {
                attempts: String(metadata.attempts),
              }),
            },
          });
        },
        failed: (metadata: { attempt_number: number }) => {
          trackEvent(EventNames.PHONE_VERIFICATION_FAILED, {
            metadata: {
              attempt_number: String(metadata.attempt_number),
            },
          });
        },
        resendClicked: (metadata?: { resend_count: number }) => {
          trackEvent(EventNames.PHONE_VERIFICATION_RESEND_CLICKED, {
            metadata: {
              ...(metadata?.resend_count !== undefined && {
                resend_count: String(metadata.resend_count),
              }),
            },
          });
        },
      },

      // Toolbar metrics
      toolbar: {
        shown: (metadata?: { view_type?: string; has_patient?: boolean }) => {
          trackEvent(EventNames.TOOLBAR_SHOWN, {
            metadata: {
              ...(metadata?.has_patient !== undefined && {
                has_patient: String(metadata.has_patient),
              }),
              ...metadata,
            },
          });
        },
        actionClicked: (metadata?: { action_type: string; has_patient?: boolean }) => {
          trackEvent(EventNames.TOOLBAR_ACTION_CLICKED, {
            metadata: {
              ...(metadata?.has_patient !== undefined && {
                has_patient: String(metadata.has_patient),
              }),
              ...metadata,
            },
          });
        },
        // Toolbar payment intercept — copay routing (GDN-2405)
        copayRouteSelected: (metadata?: {
          encounter_id?: string;
          has_encounter?: boolean;
          source?: string;
        }) => {
          trackEvent(EventNames.TOOLBAR_COPAY_ROUTE_SELECTED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_encounter !== undefined && {
                has_encounter: String(metadata.has_encounter),
              }),
            },
          });
        },
        chargeRouteSelected: (metadata?: { has_card_on_file?: boolean; source?: string }) => {
          trackEvent(EventNames.TOOLBAR_CHARGE_ROUTE_SELECTED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_card_on_file !== undefined && {
                has_card_on_file: String(metadata.has_card_on_file),
              }),
            },
          });
        },
        visibilityToggled: (metadata?: { new_state?: 'visible' | 'hidden'; trigger?: string }) => {
          trackEvent(EventNames.TOOLBAR_VISIBILITY_TOGGLED, { metadata });
        },
        notificationShown: (metadata?: {
          notification_type: string;
          show_count: number;
          is_personalized: boolean;
          personalized_count?: number;
          fallback_used: boolean;
          messages?: string[];
          days_since_last?: number;
          trigger?: 'initial' | 'retry';
        }) => {
          trackEvent(EventNames.TOOLBAR_NOTIFICATION_SHOWN, {
            metadata: {
              ...(metadata?.show_count !== undefined && {
                show_count: String(metadata.show_count),
              }),
              ...(metadata?.is_personalized !== undefined && {
                is_personalized: String(metadata.is_personalized),
              }),
              ...(metadata?.personalized_count !== undefined && {
                personalized_count: String(metadata.personalized_count),
              }),
              ...(metadata?.fallback_used !== undefined && {
                fallback_used: String(metadata.fallback_used),
              }),
              ...(metadata?.days_since_last !== undefined && {
                days_since_last: String(metadata.days_since_last),
              }),
              ...metadata,
            },
          });
        },
        notificationAcknowledged: (metadata?: {
          notification_type: string;
          acknowledgment_method: 'hover' | 'click' | 'keyboard';
          time_shown_seconds: number;
          show_count: number;
          is_personalized: boolean;
          onboarding_completed: boolean;
        }) => {
          trackEvent(EventNames.TOOLBAR_NOTIFICATION_ACKNOWLEDGED, {
            metadata: {
              ...(metadata?.time_shown_seconds !== undefined && {
                time_shown_seconds: String(metadata.time_shown_seconds),
              }),
              ...(metadata?.show_count !== undefined && {
                show_count: String(metadata.show_count),
              }),
              ...(metadata?.is_personalized !== undefined && {
                is_personalized: String(metadata.is_personalized),
              }),
              ...(metadata?.onboarding_completed !== undefined && {
                onboarding_completed: String(metadata.onboarding_completed),
              }),
              ...metadata,
            },
          });
        },
        notificationDismissed: (metadata?: {
          notification_type: string;
          dismissal_method: 'timeout' | 'exit_animation' | 'navigation';
          time_shown_seconds: number;
          show_count: number;
          is_personalized: boolean;
          will_retry: boolean;
          next_retry_days?: number;
        }) => {
          trackEvent(EventNames.TOOLBAR_NOTIFICATION_DISMISSED, {
            metadata: {
              ...(metadata?.time_shown_seconds !== undefined && {
                time_shown_seconds: String(metadata.time_shown_seconds),
              }),
              ...(metadata?.show_count !== undefined && {
                show_count: String(metadata.show_count),
              }),
              ...(metadata?.is_personalized !== undefined && {
                is_personalized: String(metadata.is_personalized),
              }),
              ...(metadata?.will_retry !== undefined && {
                will_retry: String(metadata.will_retry),
              }),
              ...(metadata?.next_retry_days !== undefined && {
                next_retry_days: String(metadata.next_retry_days),
              }),
              ...metadata,
            },
          });
        },
        notificationAutoCompleted: (metadata?: {
          notification_type: string;
          total_showings: number;
          never_acknowledged: boolean;
          is_personalized: boolean;
        }) => {
          trackEvent(EventNames.TOOLBAR_NOTIFICATION_AUTO_COMPLETED, {
            metadata: {
              ...(metadata?.total_showings !== undefined && {
                total_showings: String(metadata.total_showings),
              }),
              ...(metadata?.never_acknowledged !== undefined && {
                never_acknowledged: String(metadata.never_acknowledged),
              }),
              ...(metadata?.is_personalized !== undefined && {
                is_personalized: String(metadata.is_personalized),
              }),
              ...metadata,
            },
          });
        },
      },

      // Dashboard metrics
      dashboard: {
        viewed: (metadata?: {
          has_notes?: boolean;
          notes_count?: number;
          review_count?: number;
          onboarding_complete?: boolean;
          has_upcoming_encounters?: boolean;
        }) => {
          trackEvent(EventNames.DASHBOARD_VIEWED, {
            metadata: {
              ...(metadata?.has_notes !== undefined && {
                has_notes: String(metadata.has_notes),
              }),
              ...(metadata?.notes_count !== undefined && {
                notes_count: String(metadata.notes_count),
              }),
              ...(metadata?.review_count !== undefined && {
                review_count: String(metadata.review_count),
              }),
              ...(metadata?.onboarding_complete !== undefined && {
                onboarding_complete: String(metadata.onboarding_complete),
              }),
              ...(metadata?.has_upcoming_encounters !== undefined && {
                has_upcoming_encounters: String(metadata.has_upcoming_encounters),
              }),
              ...metadata,
            },
          });
        },
        actionClicked: (metadata?: { action_type: string; source?: string }) => {
          trackEvent(EventNames.DASHBOARD_ACTION_CLICKED, { metadata });
        },
        noteInteracted: (metadata?: {
          action: 'view' | 'continue' | 'review';
          note_status?: string;
          note_age_hours?: number;
        }) => {
          trackEvent(EventNames.DASHBOARD_NOTE_INTERACTED, {
            metadata: {
              ...(metadata?.note_age_hours !== undefined && {
                note_age_hours: String(metadata.note_age_hours),
              }),
              ...metadata,
            },
          });
        },
        refreshClicked: (metadata?: { time_since_load_seconds?: number }) => {
          trackEvent(EventNames.DASHBOARD_REFRESH_CLICKED, {
            metadata: {
              ...(metadata?.time_since_load_seconds !== undefined && {
                time_since_load_seconds: String(metadata.time_since_load_seconds),
              }),
              ...metadata,
            },
          });
        },
        onboardingTaskClicked: (metadata?: {
          task_type?: string;
          tasks_remaining?: number;
          onboarding_step?: string;
        }) => {
          trackEvent(EventNames.DASHBOARD_ONBOARDING_TASK_CLICKED, {
            metadata: {
              ...(metadata?.tasks_remaining !== undefined && {
                tasks_remaining: String(metadata.tasks_remaining),
              }),
              ...metadata,
            },
          });
        },
      },

      // Lab Order metrics
      labOrder: {
        modalOpened: (metadata?: {
          source?: string;
          has_patient?: boolean;
          has_note?: boolean;
        }) => {
          trackEvent(EventNames.LAB_ORDER_MODAL_OPENED, {
            metadata: {
              ...(metadata?.has_patient !== undefined && {
                has_patient: String(metadata.has_patient),
              }),
              ...(metadata?.has_note !== undefined && {
                has_note: String(metadata.has_note),
              }),
              ...metadata,
            },
          });
        },
        searchPerformed: (metadata?: { search_term?: string; results_count?: number }) => {
          trackEvent(EventNames.LAB_ORDER_SEARCH_PERFORMED, {
            metadata: {
              ...(metadata?.results_count !== undefined && {
                results_count: String(metadata.results_count),
              }),
              ...metadata,
            },
          });
        },
        testSelected: (metadata?: { is_bundle?: boolean; total_tests?: number }) => {
          trackEvent(EventNames.LAB_ORDER_TEST_SELECTED, {
            metadata: {
              ...(metadata?.is_bundle !== undefined && {
                is_bundle: String(metadata.is_bundle),
              }),
              ...(metadata?.total_tests !== undefined && {
                total_tests: String(metadata.total_tests),
              }),
              ...metadata,
            },
          });
        },
        bundleSelected: (metadata?: { bundle_name?: string; test_count?: number }) => {
          trackEvent(EventNames.LAB_ORDER_BUNDLE_SELECTED, {
            metadata: {
              ...(metadata?.test_count !== undefined && {
                test_count: String(metadata.test_count),
              }),
              ...metadata,
            },
          });
        },
        bundleSaved: (metadata?: { bundle_name?: string; test_count?: number }) => {
          trackEvent(EventNames.LAB_ORDER_BUNDLE_SAVED, {
            metadata: {
              ...(metadata?.test_count !== undefined && {
                test_count: String(metadata.test_count),
              }),
              ...metadata,
            },
          });
        },
        aiRecommendationsApplied: (metadata?: {
          test_count?: number;
          source?: 'auto' | 'manual';
        }) => {
          trackEvent(EventNames.LAB_ORDER_AI_RECOMMENDATIONS_APPLIED, {
            metadata: {
              ...(metadata?.test_count !== undefined && {
                test_count: String(metadata.test_count),
              }),
              ...metadata,
            },
          });
        },
        requisitionGenerated: (metadata?: { test_count?: number; has_icd_codes?: boolean }) => {
          trackEvent(EventNames.LAB_ORDER_REQUISITION_GENERATED, {
            metadata: {
              ...(metadata?.test_count !== undefined && {
                test_count: String(metadata.test_count),
              }),
              ...(metadata?.has_icd_codes !== undefined && {
                has_icd_codes: String(metadata.has_icd_codes),
              }),
              ...metadata,
            },
          });
        },
        sent: (metadata?: { delivery_method?: string; test_count?: number }) => {
          trackEvent(EventNames.LAB_ORDER_SENT, {
            metadata: {
              ...(metadata?.test_count !== undefined && {
                test_count: String(metadata.test_count),
              }),
              ...metadata,
            },
          });
        },
        completed: (metadata?: { time_to_complete?: number; test_count?: number }) => {
          trackEvent(EventNames.LAB_ORDER_COMPLETED, {
            metadata: {
              ...(metadata?.time_to_complete !== undefined && {
                time_to_complete: String(metadata.time_to_complete),
              }),
              ...(metadata?.test_count !== undefined && {
                test_count: String(metadata.test_count),
              }),
              ...metadata,
            },
          });
        },
        failed: (metadata?: { error_type?: string; error_message?: string; step?: string }) => {
          trackEvent(EventNames.LAB_ORDER_FAILED, { metadata });
        },
      },

      // Auth metrics
      auth: {
        signupStarted: (metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.AUTH_SIGNUP_STARTED, { metadata });
        },
        signupCompleted: (
          metadata?: Record<string, unknown> & {
            specialty?: string;
            discovery_channel?: string;
          },
        ) => {
          trackEvent(EventNames.AUTH_SIGNUP_COMPLETED, { metadata });
        },
        loginCompleted: (
          metadata?: Record<string, unknown> & {
            is_returning_user?: boolean;
            auth_method?: string;
          },
        ) => {
          trackEvent(EventNames.AUTH_LOGIN_COMPLETED, { metadata });
        },
        logout: (metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.AUTH_LOGOUT, { metadata });
        },
      },

      // Onboarding metrics
      onboarding: {
        started: (metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.ONBOARDING_STARTED, { metadata });
        },
        profileCompleted: (
          metadata?: Record<string, unknown> & {
            specialty?: string;
            discovery_channel?: string;
            has_referral_code?: boolean;
          },
        ) => {
          trackEvent(EventNames.ONBOARDING_PROFILE_COMPLETED, { metadata });
        },
        preferencesSet: (
          metadata?: Record<string, unknown> & {
            language?: string;
            note_preferences?: Record<string, unknown>;
          },
        ) => {
          trackEvent(EventNames.ONBOARDING_PREFERENCES_SET, { metadata });
        },
        pathSelected: (path: string, metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.ONBOARDING_PATH_SELECTED, {
            metadata: { path, ...metadata },
          });
        },
        completed: (
          metadata?: Record<string, unknown> & {
            completion_path?: string;
            time_to_complete_seconds?: number;
          },
        ) => {
          trackEvent(EventNames.ONBOARDING_COMPLETED, { metadata });
        },
        skipped: (
          metadata?: Record<string, unknown> & {
            skipped_at_step?: string;
          },
        ) => {
          trackEvent(EventNames.ONBOARDING_SKIPPED, { metadata });
        },
      },

      // Performance metrics
      performance: {
        timing: (actionType: string, duration: number, metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.PERFORMANCE_TIMING, {
            value: Math.round(duration),
            metadata: {
              action_type: actionType,
              duration_ms: String(Math.round(duration)),
              ...metadata,
            },
          });
        },
        // Helper to get current timestamp
        now: () => Date.now(),
      },

      // Search metrics (generic with metadata)
      search: {
        initiated: (metadata: {
          search_type: 'patient' | 'payer' | 'note' | 'form';
          search_context: 'nav_search' | 'list_page' | 'modal';
          character_count?: number;
        }) => {
          trackEvent(EventNames.SEARCH_INITIATED, {
            metadata: {
              ...metadata,
              ...(metadata.character_count !== undefined && {
                character_count: String(metadata.character_count),
              }),
            },
          });
        },
        performed: (metadata: {
          search_type: 'patient' | 'payer' | 'note' | 'form';
          search_context: 'nav_search' | 'list_page' | 'modal';
          query_length: number;
          results_count: number;
          has_results: boolean;
        }) => {
          trackEvent(EventNames.SEARCH_PERFORMED, {
            metadata: {
              search_type: metadata.search_type,
              search_context: metadata.search_context,
              query_length: String(metadata.query_length),
              results_count: String(metadata.results_count),
              has_results: metadata.has_results ? 'true' : 'false',
            },
          });
        },
        resultClicked: (metadata: {
          search_type: 'patient' | 'payer' | 'note' | 'form';
          search_context: 'nav_search' | 'list_page' | 'modal';
          result_position: number;
          result_id: string;
        }) => {
          trackEvent(EventNames.SEARCH_RESULT_CLICKED, {
            metadata: {
              ...metadata,
              result_position: String(metadata.result_position),
            },
          });
        },
        /**
         * Emits `search_clear_clicked`, not `search_cleared` (GDN-3453). The
         * method keeps its name because it reads correctly at the call site;
         * the EVENT name is what the Metabase pipeline filters on, and
         * `_cleared` is not on its allowlist.
         */
        cleared: (metadata: {
          search_type: 'patient' | 'payer' | 'note' | 'form';
          search_context: 'nav_search' | 'list_page' | 'modal';
          had_results: boolean;
          query_length?: number;
        }) => {
          trackEvent(EventNames.SEARCH_CLEAR_CLICKED, {
            metadata: {
              ...metadata,
              had_results: metadata.had_results ? 'true' : 'false',
              ...(metadata.query_length !== undefined && {
                query_length: String(metadata.query_length),
              }),
            },
          });
        },
        abandoned: (metadata: {
          search_type: 'patient' | 'payer' | 'note' | 'form';
          search_context: 'nav_search' | 'list_page' | 'modal';
          had_results: boolean;
          query_length?: number;
          results_shown_count?: number;
        }) => {
          trackEvent(EventNames.SEARCH_ABANDONED, {
            metadata: {
              ...metadata,
              had_results: metadata.had_results ? 'true' : 'false',
              ...(metadata.query_length !== undefined && {
                query_length: String(metadata.query_length),
              }),
              ...(metadata.results_shown_count !== undefined && {
                results_shown_count: String(metadata.results_shown_count),
              }),
            },
          });
        },
      },

      // Navigation metrics (generic with metadata)
      navigation: {
        clicked: (metadata: {
          menu_item: string;
          source_path: string;
          destination_path: string;
          is_dropdown?: boolean;
        }) => {
          trackEvent(EventNames.NAVIGATION_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata.is_dropdown !== undefined && {
                is_dropdown: metadata.is_dropdown ? 'true' : 'false',
              }),
            },
          });
        },
        back: (metadata: {
          source_path: string;
          destination_path?: string;
          method: 'browser_back' | 'app_back_button';
        }) => {
          trackEvent(EventNames.NAVIGATION_BACK, {
            metadata,
          });
        },
        quickAction: (metadata: {
          action_type: 'new_note' | 'new_patient' | 'schedule' | 'record' | 'billing_portal';
          source_path: string;
        }) => {
          trackEvent(EventNames.NAVIGATION_QUICK_ACTION, {
            metadata,
          });
        },
      },

      // Error metrics
      error: {
        track: (error: Error | string, context: string, metadata?: Record<string, unknown>) => {
          const errorMessage = error instanceof Error ? error.message : error;
          const errorType = error instanceof Error ? error.name : 'unknown';

          trackEvent(EventNames.ERROR_OCCURRED, {
            metadata: {
              error_message: errorMessage,
              error_type: errorType,
              error_context: context,
              ...metadata,
            },
          });
        },
        api: (
          endpoint: string,
          status: number,
          message: string,
          metadata?: Record<string, unknown>,
        ) => {
          trackEvent(EventNames.ERROR_API, {
            metadata: {
              endpoint,
              status_code: String(status),
              error_message: message,
              ...metadata,
            },
          });
        },
        network: (url: string, error: string, metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.ERROR_NETWORK, {
            metadata: {
              url,
              error_message: error,
              ...metadata,
            },
          });
        },
        pipelineInit: (metadata: {
          error_type: string;
          error_source: string;
          error_message: string;
          error_name?: string;
          retry_count: number;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.PIPELINE_INIT_FAILED, {
            metadata,
          });
        },
      },

      // Note telemetry metrics (mirrored from NoteTelemetry system)
      // These events track fine-grained user interactions with note content
      // All payloads are already flattened from the NoteTelemetry system
      noteTelemetry: {
        selectionUpdate: (metadata: Record<string, string>) => {
          trackEvent(EventNames.NOTE_SELECTION_UPDATE, { metadata });
        },
        textSelected: (metadata: Record<string, string>) => {
          trackEvent(EventNames.NOTE_TEXT_SELECTED, { metadata });
        },
        textCopied: (metadata: Record<string, string>) => {
          trackEvent(EventNames.NOTE_TEXT_COPIED, { metadata });
        },
        noteViewed: (metadata: Record<string, string>) => {
          trackEvent(EventNames.NOTE_NOTE_VIEWED, { metadata });
        },
        sectionRendered: (metadata: Record<string, string>) => {
          trackEvent(EventNames.NOTE_SECTION_RENDERED, { metadata });
        },
        sectionInteracted: (metadata: Record<string, string>) => {
          trackEvent(EventNames.NOTE_SECTION_INTERACTED, { metadata });
        },
        sectionEdited: (metadata: Record<string, string>) => {
          trackEvent(EventNames.NOTE_SECTION_EDITED, { metadata });
        },
      },

      // CSAT (Customer Satisfaction) metrics
      csat: {
        modalOpened: (
          metadata?: Record<string, unknown> & { context?: string; context_id?: string },
        ) => {
          trackEvent(EventNames.CSAT_MODAL_OPENED, { metadata });
        },
        ratingSubmitted: (
          metadata?: Record<string, unknown> & {
            rating?: string;
            context?: string;
            context_id?: string;
          },
        ) => {
          trackEvent(EventNames.CSAT_RATING_SUBMITTED, { metadata });
        },
        followUpSubmitted: (
          metadata?: Record<string, unknown> & {
            rating?: string;
            context?: string;
            context_id?: string;
            selected_options?: string[];
            comment_provided?: string;
          },
        ) => {
          trackEvent(EventNames.CSAT_FOLLOW_UP_SUBMITTED, {
            metadata: {
              ...metadata,
              ...(metadata?.selected_options && {
                selected_options: metadata.selected_options.join(','),
              }),
            },
          });
        },
        dismissed: (
          metadata?: Record<string, unknown> & {
            modal_type?: string;
            context?: string;
          },
        ) => {
          trackEvent(EventNames.CSAT_DISMISSED, { metadata });
        },
        referralInviteShown: (
          metadata?: Record<string, unknown> & {
            rating?: string;
            context?: string;
            context_id?: string;
          },
        ) => {
          trackEvent(EventNames.CSAT_REFERRAL_INVITE_SHOWN, { metadata });
        },
        referralInviteDeclined: (
          metadata?: Record<string, unknown> & {
            rating?: string;
            context?: string;
            context_id?: string;
          },
        ) => {
          trackEvent(EventNames.CSAT_REFERRAL_INVITE_DECLINED, { metadata });
        },
        reviewSurfaceClicked: (
          metadata?: Record<string, unknown> & {
            surface?: string;
            variant?: string;
            context?: string;
            context_id?: string;
          },
        ) => {
          trackEvent(EventNames.CSAT_REVIEW_SURFACE_CLICKED, { metadata });
        },
      },

      // No free text, account identity, URLs, filenames, or binary data.
      jotSupport: {
        opened: () => trackEvent(EventNames.JOT_SUPPORT_OPENED),
        closed: () => trackEvent(EventNames.JOT_SUPPORT_CLOSED),
        submitted: () => trackEvent(EventNames.JOT_SUPPORT_SUBMITTED),
        retryClicked: () => trackEvent(EventNames.JOT_SUPPORT_RETRY_CLICKED),
        statusRequested: () => trackEvent(EventNames.JOT_SUPPORT_STATUS_REQUESTED),
        attachmentSelected: () => trackEvent(EventNames.JOT_SUPPORT_ATTACHMENT_SELECTED),
      },

      // Thumbs Feedback (thumbs up/down widget)
      thumbsFeedback: {
        positive: (
          metadata?: Record<string, unknown> & {
            content_type?: string;
            response_id?: string;
            model_version?: string;
          },
        ) => {
          trackEvent(EventNames.THUMBS_FEEDBACK_POSITIVE, { metadata });
        },
        negative: (
          metadata?: Record<string, unknown> & {
            content_type?: string;
            response_id?: string;
            model_version?: string;
          },
        ) => {
          trackEvent(EventNames.THUMBS_FEEDBACK_NEGATIVE, { metadata });
        },
        categoriesSubmitted: (
          metadata?: Record<string, unknown> & {
            content_type?: string;
            response_id?: string;
            categories?: string;
            comment_length?: string;
          },
        ) => {
          trackEvent(EventNames.THUMBS_FEEDBACK_CATEGORIES_SUBMITTED, { metadata });
        },
        dismissed: (
          metadata?: Record<string, unknown> & {
            content_type?: string;
            response_id?: string;
          },
        ) => {
          trackEvent(EventNames.THUMBS_FEEDBACK_DISMISSED, { metadata });
        },
      },

      // System notification metrics
      systemNotification: {
        bannerDismissed: (metadata: {
          notification_id: string;
          notification_type: 'banner';
          severity: 'info' | 'warning' | 'error';
          dismissal_type: 'persistent' | 'session';
        }) => {
          trackEvent(EventNames.SYSTEM_NOTIFICATION_BANNER_DISMISSED, { metadata });
        },
        modalOpened: (metadata: {
          notification_id: string;
          notification_type: 'modal';
          severity: 'info' | 'warning' | 'error';
        }) => {
          trackEvent(EventNames.SYSTEM_NOTIFICATION_MODAL_OPENED, { metadata });
        },
        modalDismissed: (metadata: {
          notification_id: string;
          notification_type: 'modal';
          severity: 'info' | 'warning' | 'error';
          dismissal_type: 'persistent' | 'session';
        }) => {
          trackEvent(EventNames.SYSTEM_NOTIFICATION_MODAL_DISMISSED, { metadata });
        },
        linkClicked: (metadata: {
          notification_id: string;
          notification_type: 'banner' | 'modal';
          severity: 'info' | 'warning' | 'error';
          link_url: string;
          link_text: string;
        }) => {
          trackEvent(EventNames.SYSTEM_NOTIFICATION_LINK_CLICKED, { metadata });
        },
        fetchError: (metadata: { error_message: string; status_code?: string }) => {
          trackEvent(EventNames.SYSTEM_NOTIFICATION_FETCH_ERROR, { metadata });
        },
      },

      // Feature toggle metrics (for two-way toggles between versions)
      toggle: {
        switchedToNew: (metadata?: { feature_key: string }) => {
          trackEvent(EventNames.TOGGLE_SWITCHED_TO_NEW, { metadata });
        },
        switchedToClassic: (metadata?: { feature_key: string }) => {
          trackEvent(EventNames.TOGGLE_SWITCHED_TO_CLASSIC, { metadata });
        },
        modeViewed: (metadata?: { feature_key: string; mode: 'new' | 'classic' }) => {
          trackEvent(EventNames.TOGGLE_MODE_VIEWED, { metadata });
        },
        graduated: (metadata?: {
          feature_key: string;
          notes_before_graduation?: number;
          graduation_reason?: string;
        }) => {
          trackEvent(EventNames.TOGGLE_GRADUATED, {
            metadata: {
              ...metadata,
              ...(metadata?.notes_before_graduation !== undefined && {
                notes_before_graduation: String(metadata.notes_before_graduation),
              }),
            },
          });
        },
        nudgeApplied: (metadata: {
          feature_key: string;
          nudge_type: 'version' | 'periodic';
          nudge_version?: number;
          interval_ms?: number;
        }) => {
          trackEvent(EventNames.TOGGLE_NUDGE_APPLIED, {
            metadata: {
              ...metadata,
              ...(metadata.nudge_version !== undefined && {
                nudge_version: String(metadata.nudge_version),
              }),
              ...(metadata.interval_ms !== undefined && {
                interval_ms: String(metadata.interval_ms),
              }),
            },
          });
        },
      },

      // Entitlement metrics
      entitlement: {
        upgradeClicked: (
          metadata?: Record<string, unknown> & {
            capability_name?: string;
            current_usage?: number;
            quota?: number;
            quota_remaining?: number;
            is_depleted?: boolean;
            bundle_name?: string;
            reset_date?: string;
          },
        ) => {
          trackEvent(EventNames.ENTITLEMENT_UPGRADE_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.current_usage !== undefined && {
                current_usage: String(metadata.current_usage),
              }),
              ...(metadata?.quota !== undefined && {
                quota: String(metadata.quota),
              }),
              ...(metadata?.quota_remaining !== undefined && {
                quota_remaining: String(metadata.quota_remaining),
              }),
              ...(metadata?.is_depleted !== undefined && {
                is_depleted: String(metadata.is_depleted),
              }),
            },
          });
        },
        blockedOverlayShown: (
          metadata?: Record<string, unknown> & {
            capability_name?: string;
            source?: string;
            has_quota?: boolean;
            current_usage?: number;
            quota?: number;
          },
        ) => {
          trackEvent(EventNames.ENTITLEMENT_BLOCKED_OVERLAY_SHOWN, {
            metadata: {
              ...metadata,
              ...(metadata?.has_quota !== undefined && {
                has_quota: String(metadata.has_quota),
              }),
              ...(metadata?.current_usage !== undefined && {
                current_usage: String(metadata.current_usage),
              }),
              ...(metadata?.quota !== undefined && {
                quota: String(metadata.quota),
              }),
            },
          });
        },
        upgradeRequested: (
          metadata?: Record<string, unknown> & {
            capability_name?: string;
            source?: string;
            action_label?: string;
            has_quota?: boolean;
            current_usage?: number;
            quota?: number;
          },
        ) => {
          trackEvent(EventNames.ENTITLEMENT_UPGRADE_REQUESTED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_quota !== undefined && {
                has_quota: String(metadata.has_quota),
              }),
              ...(metadata?.current_usage !== undefined && {
                current_usage: String(metadata.current_usage),
              }),
              ...(metadata?.quota !== undefined && {
                quota: String(metadata.quota),
              }),
            },
          });
        },
        blockedDismissed: (
          metadata?: Record<string, unknown> & {
            capability_name?: string;
            source?: string;
            action_label?: string;
            has_quota?: boolean;
            time_on_overlay?: number;
          },
        ) => {
          trackEvent(EventNames.ENTITLEMENT_BLOCKED_DISMISSED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_quota !== undefined && {
                has_quota: String(metadata.has_quota),
              }),
              ...(metadata?.time_on_overlay !== undefined && {
                time_on_overlay: String(metadata.time_on_overlay),
              }),
            },
          });
        },
      },
      subscription: {
        // Flow-level events
        flowStarted: (
          metadata?: Record<string, unknown> & {
            entry_point?: string;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_FLOW_STARTED, { metadata });
        },
        stepCompleted: (
          metadata?: Record<string, unknown> & {
            step_name?: string;
            step_number?: number;
            time_on_step?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_FLOW_STEP_COMPLETED, {
            metadata: {
              ...metadata,
              ...(metadata?.step_number !== undefined && {
                step_number: String(metadata.step_number),
              }),
              ...(metadata?.time_on_step !== undefined && {
                time_on_step: String(metadata.time_on_step),
              }),
            },
          });
        },
        flowCompleted: (
          metadata?: Record<string, unknown> & {
            total_time?: number;
            tier_id?: string;
            tier_name?: string;
            addon_count?: number;
            addon_ids?: string[];
            addon_names?: string[];
            billing_interval?: string;
            total_amount?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_FLOW_COMPLETED, {
            metadata: {
              ...metadata,
              ...(metadata?.total_time !== undefined && {
                total_time: String(metadata.total_time),
              }),
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
              ...(metadata?.total_amount !== undefined && {
                total_amount: String(metadata.total_amount),
              }),
            },
          });
        },
        flowAbandoned: (
          metadata?: Record<string, unknown> & {
            step_name?: string;
            step_number?: number;
            time_in_flow?: number;
            tier_selected?: boolean;
            addon_count?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_FLOW_ABANDONED, {
            metadata: {
              ...metadata,
              ...(metadata?.step_number !== undefined && {
                step_number: String(metadata.step_number),
              }),
              ...(metadata?.time_in_flow !== undefined && {
                time_in_flow: String(metadata.time_in_flow),
              }),
              ...(metadata?.tier_selected !== undefined && {
                tier_selected: String(metadata.tier_selected),
              }),
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
            },
          });
        },
        stepNextClicked: (
          metadata?: Record<string, unknown> & {
            step_name?: string;
            step_number?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_STEP_NEXT_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.step_number !== undefined && {
                step_number: String(metadata.step_number),
              }),
            },
          });
        },
        stepBackClicked: (
          metadata?: Record<string, unknown> & {
            step_name?: string;
            step_number?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_STEP_BACK_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.step_number !== undefined && {
                step_number: String(metadata.step_number),
              }),
            },
          });
        },

        // Tier selection events
        tierCardClicked: (
          metadata?: Record<string, unknown> & {
            tier_id?: string;
            tier_name?: string;
            monthly_price?: number;
            yearly_price?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_TIER_CARD_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.monthly_price !== undefined && {
                monthly_price: String(metadata.monthly_price),
              }),
              ...(metadata?.yearly_price !== undefined && {
                yearly_price: String(metadata.yearly_price),
              }),
            },
          });
        },
        tierSelected: (
          metadata?: Record<string, unknown> & {
            tier_id?: string;
            tier_name?: string;
            billing_interval?: string;
            monthly_price?: number;
            yearly_price?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_TIER_SELECTED, {
            metadata: {
              ...metadata,
              ...(metadata?.monthly_price !== undefined && {
                monthly_price: String(metadata.monthly_price),
              }),
              ...(metadata?.yearly_price !== undefined && {
                yearly_price: String(metadata.yearly_price),
              }),
            },
          });
        },
        tierChanged: (
          metadata?: Record<string, unknown> & {
            from_tier_id?: string;
            to_tier_id?: string;
            from_tier_name?: string;
            to_tier_name?: string;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_TIER_CHANGED, { metadata });
        },
        tierContinueClicked: (
          metadata?: Record<string, unknown> & {
            tier_id?: string;
            tier_name?: string;
            billing_interval?: string;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_TIER_CONTINUE_CLICKED, { metadata });
        },

        // Add-on selection events
        addonAddClicked: (
          metadata?: Record<string, unknown> & {
            addon_id?: string;
            addon_name?: string;
            current_addon_count?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_ADDON_ADD_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.current_addon_count !== undefined && {
                current_addon_count: String(metadata.current_addon_count),
              }),
            },
          });
        },
        addonAdded: (
          metadata?: Record<string, unknown> & {
            addon_id?: string;
            addon_name?: string;
            billing_interval?: string;
            monthly_price?: number;
            yearly_price?: number;
            new_addon_count?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_ADDON_ADDED, {
            metadata: {
              ...metadata,
              ...(metadata?.monthly_price !== undefined && {
                monthly_price: String(metadata.monthly_price),
              }),
              ...(metadata?.yearly_price !== undefined && {
                yearly_price: String(metadata.yearly_price),
              }),
              ...(metadata?.new_addon_count !== undefined && {
                new_addon_count: String(metadata.new_addon_count),
              }),
            },
          });
        },
        addonRemoveClicked: (
          metadata?: Record<string, unknown> & {
            addon_id?: string;
            addon_name?: string;
            current_addon_count?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_ADDON_REMOVE_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.current_addon_count !== undefined && {
                current_addon_count: String(metadata.current_addon_count),
              }),
            },
          });
        },
        addonRemoved: (
          metadata?: Record<string, unknown> & {
            addon_id?: string;
            addon_name?: string;
            new_addon_count?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_ADDON_REMOVED, {
            metadata: {
              ...metadata,
              ...(metadata?.new_addon_count !== undefined && {
                new_addon_count: String(metadata.new_addon_count),
              }),
            },
          });
        },
        addonLearnMoreClicked: (
          metadata?: Record<string, unknown> & {
            addon_id?: string;
            addon_name?: string;
            learn_more_url?: string;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_ADDON_LEARN_MORE_CLICKED, { metadata });
        },
        changeTierClicked: (
          metadata?: Record<string, unknown> & {
            current_tier_id?: string;
            current_tier_name?: string;
            addon_count?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_CHANGE_TIER_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
            },
          });
        },
        addonsContinueClicked: (
          metadata?: Record<string, unknown> & {
            tier_id?: string;
            addon_count?: number;
            billing_interval?: string;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_ADDONS_CONTINUE_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
            },
          });
        },

        // Billing interval events
        billingIntervalToggled: (
          metadata?: Record<string, unknown> & {
            from_interval?: string;
            to_interval?: string;
            tier_id?: string;
            addon_count?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_BILLING_INTERVAL_TOGGLED, {
            metadata: {
              ...metadata,
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
            },
          });
        },

        // Payment & completion events
        paymentStepViewed: (
          metadata?: Record<string, unknown> & {
            tier_id?: string;
            addon_count?: number;
            billing_interval?: string;
            total_amount?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_PAYMENT_STEP_VIEWED, {
            metadata: {
              ...metadata,
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
              ...(metadata?.total_amount !== undefined && {
                total_amount: String(metadata.total_amount),
              }),
            },
          });
        },
        paymentDetailsEntered: (
          metadata?: Record<string, unknown> & {
            has_payment_method?: boolean;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_PAYMENT_DETAILS_ENTERED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_payment_method !== undefined && {
                has_payment_method: String(metadata.has_payment_method),
              }),
            },
          });
        },
        paymentSubmitClicked: (
          metadata?: Record<string, unknown> & {
            tier_id?: string;
            tier_name?: string;
            addon_count?: number;
            addon_ids?: string[];
            addon_names?: string[];
            billing_interval?: string;
            total_amount?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_PAYMENT_SUBMIT_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
              ...(metadata?.total_amount !== undefined && {
                total_amount: String(metadata.total_amount),
              }),
            },
          });
        },
        paymentSucceeded: (
          metadata?: Record<string, unknown> & {
            tier_id?: string;
            tier_name?: string;
            addon_count?: number;
            addon_ids?: string[];
            addon_names?: string[];
            billing_interval?: string;
            total_amount?: number;
            payment_duration?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_PAYMENT_SUCCEEDED, {
            metadata: {
              ...metadata,
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
              ...(metadata?.total_amount !== undefined && {
                total_amount: String(metadata.total_amount),
              }),
              ...(metadata?.payment_duration !== undefined && {
                payment_duration: String(metadata.payment_duration),
              }),
            },
          });
        },
        paymentFailed: (
          metadata?: Record<string, unknown> & {
            error_message?: string;
            error_code?: string;
            tier_id?: string;
            addon_count?: number;
            total_amount?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_PAYMENT_FAILED, {
            metadata: {
              ...metadata,
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
              ...(metadata?.total_amount !== undefined && {
                total_amount: String(metadata.total_amount),
              }),
            },
          });
        },
        subscriptionCreated: (
          metadata?: Record<string, unknown> & {
            subscription_id?: string;
            tier_id?: string;
            tier_name?: string;
            addon_count?: number;
            addon_ids?: string[];
            addon_names?: string[];
            billing_interval?: string;
            total_amount?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_CREATED, {
            metadata: {
              ...metadata,
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
              ...(metadata?.total_amount !== undefined && {
                total_amount: String(metadata.total_amount),
              }),
            },
          });
        },

        // Bundle learn more page events
        bundlePageViewed: (
          metadata?: Record<string, unknown> & {
            current_tab?: string;
          },
        ) => {
          trackEvent(EventNames.BUNDLE_PAGE_VIEWED, { metadata });
        },
        bundleTabChanged: (
          metadata?: Record<string, unknown> & {
            previous_tab?: string;
            new_tab?: string;
          },
        ) => {
          trackEvent(EventNames.BUNDLE_TAB_CHANGED, { metadata });
        },
        bundleTalkToSalesClicked: (
          metadata?: Record<string, unknown> & {
            tab?: string;
            destination_url?: string;
          },
        ) => {
          trackEvent(EventNames.BUNDLE_TALK_TO_SALES_CLICKED, { metadata });
        },
        bundleBackClicked: (
          metadata?: Record<string, unknown> & {
            from_tab?: string;
          },
        ) => {
          trackEvent(EventNames.BUNDLE_BACK_CLICKED, { metadata });
        },
      },

      // Referral metrics
      referral: {
        pageViewed: (
          metadata?: Record<string, unknown> & {
            has_referral_code?: boolean;
            referrals_count?: number;
            total_earnings?: string;
          },
        ) => {
          trackEvent(EventNames.REFERRAL_PAGE_VIEWED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_referral_code !== undefined && {
                has_referral_code: String(metadata.has_referral_code),
              }),
              ...(metadata?.referrals_count !== undefined && {
                referrals_count: String(metadata.referrals_count),
              }),
            },
          });
        },
        linkCopied: (
          metadata?: Record<string, unknown> & {
            referral_code?: string;
            source?: string;
          },
        ) => {
          trackEvent(EventNames.REFERRAL_LINK_COPIED, { metadata });
        },
        linkCopyFailed: (
          metadata?: Record<string, unknown> & {
            error_message?: string;
          },
        ) => {
          trackEvent(EventNames.REFERRAL_LINK_COPY_FAILED, { metadata });
        },
        faqExpanded: (
          metadata?: Record<string, unknown> & {
            question_index?: number;
            question_text?: string;
          },
        ) => {
          trackEvent(EventNames.REFERRAL_FAQ_EXPANDED, {
            metadata: {
              ...metadata,
              ...(metadata?.question_index !== undefined && {
                question_index: String(metadata.question_index),
              }),
            },
          });
        },
      },

      // Patient Deduplication metrics
      patientDeduplication: {
        findDuplicatesClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_FIND_DUPLICATES_CLICKED, { metadata });
        },
        scanCompleted: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            total_patients?: number;
            duplicate_groups_found?: number;
            total_duplicates_found?: number;
            scan_duration_ms?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_SCAN_COMPLETED, { metadata });
        },
        scanFailed: (
          error: string,
          metadata?: Record<string, unknown> & {
            organization_id?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_SCAN_FAILED, {
            metadata: { error_message: error, ...metadata },
          });
        },
        mergeInitiated: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            group_id?: string;
            selected_patient_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_MERGE_INITIATED, { metadata });
        },
        mergeCustomizationViewed: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            group_id?: string;
            patient_count?: number;
            module_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_MERGE_CUSTOMIZATION_VIEWED, { metadata });
        },
        mergeCompleted: (
          metadata?: Record<string, unknown> & {
            group_id?: string;
            primary_patient_id?: string;
            merged_patient_count?: number;
            merge_duration_ms?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_MERGE_COMPLETED, { metadata });
        },
        mergeFailed: (
          error: string,
          metadata?: Record<string, unknown> & {
            group_id?: string;
            primary_patient_id?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_MERGE_FAILED, {
            metadata: { error_message: error, ...metadata },
          });
        },
        dismissClicked: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            group_id?: string;
            patient_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_DISMISS_CLICKED, { metadata });
        },
        dismissCompleted: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            group_id?: string;
            patient_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_DISMISS_COMPLETED, { metadata });
        },
        dismissFailed: (
          error: string,
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            group_id?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_DISMISS_FAILED, {
            metadata: { error_message: error, ...metadata },
          });
        },
        autoMergeCandidatesLoaded: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            total_candidates?: number;
            skipped_groups?: number;
            scan_duration_ms?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_AUTO_MERGE_CANDIDATES_LOADED, { metadata });
        },
        batchMergeCompleted: (
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            total_groups?: number;
            approved_groups?: number;
            successful?: number;
            failed?: number;
            merge_duration_ms?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_BATCH_MERGE_COMPLETED, { metadata });
        },
        batchMergeFailed: (
          error: string,
          metadata?: Record<string, unknown> & {
            organization_id?: string;
            total_groups?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_DEDUPLICATION_BATCH_MERGE_FAILED, {
            metadata: { error_message: error, ...metadata },
          });
        },
      },
      subscriptionManagement: {
        pageViewed: (
          metadata?: Record<string, unknown> & {
            subscription_status?: string;
            billing_interval?: string;
            has_payment_method?: boolean;
            is_trialing?: boolean;
            has_discount?: boolean;
            discount_type?: string;
            discount_duration?: string;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_MANAGEMENT_PAGE_VIEWED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_payment_method !== undefined && {
                has_payment_method: String(metadata.has_payment_method),
              }),
              ...(metadata?.is_trialing !== undefined && {
                is_trialing: String(metadata.is_trialing),
              }),
              ...(metadata?.has_discount !== undefined && {
                has_discount: String(metadata.has_discount),
              }),
            },
          });
        },
        billingIntervalPreview: (
          metadata?: Record<string, unknown> & {
            preview_interval?: string;
            current_interval?: string;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_BILLING_INTERVAL_PREVIEW, { metadata });
        },
        tierComparisonViewed: (
          metadata?: Record<string, unknown> & {
            subscription_status?: string;
            billing_interval?: string;
            is_trialing?: boolean;
            tier_count?: number;
            addon_count?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_TIER_COMPARISON_VIEWED, {
            metadata: {
              ...metadata,
              ...(metadata?.is_trialing !== undefined && {
                is_trialing: String(metadata.is_trialing),
              }),
            },
          });
        },
        soloInviteCtaClicked: () => {
          trackEvent(EventNames.SUBSCRIPTION_SOLO_INVITE_CTA_CLICKED);
        },
        // Outcome event: the solo single-page view committed a tier/add-on
        // change. Solo analog of the group-side `practice.batchEditApplied`.
        soloPlanChanged: (
          metadata?: Record<string, unknown> & {
            subscription_status?: string;
            billing_interval?: string;
            is_trialing?: boolean;
            tier_count?: number;
            addon_count?: number;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_SOLO_PLAN_CHANGED, {
            metadata: {
              ...metadata,
              ...(metadata?.is_trialing !== undefined && {
                is_trialing: String(metadata.is_trialing),
              }),
            },
          });
        },
        billingIntervalConfirmed: (
          metadata?: Record<string, unknown> & {
            from_interval?: string;
            to_interval?: string;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_BILLING_INTERVAL_CONFIRMED, { metadata });
        },
        paymentMethodUpdateClicked: () => {
          trackEvent(EventNames.SUBSCRIPTION_PAYMENT_METHOD_UPDATE_CLICKED);
        },
        paymentMethodUpdated: (
          metadata?: Record<string, unknown> & {
            payment_method_type?: string;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_PAYMENT_METHOD_UPDATED, { metadata });
        },
        cancelClicked: () => {
          trackEvent(EventNames.SUBSCRIPTION_CANCEL_CLICKED);
        },
        cancelConfirmed: (
          metadata?: Record<string, unknown> & {
            cancellation_reason_provided?: boolean;
            // Mandatory cancel-survey props (GDN-2986). Categories are fixed
            // enum ids, never free text — safe to send, no PHI.
            category_count?: number;
            primary_category?: string;
            categories?: string;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_CANCEL_CONFIRMED, {
            metadata: {
              ...metadata,
              ...(metadata?.cancellation_reason_provided !== undefined && {
                cancellation_reason_provided: String(metadata.cancellation_reason_provided),
              }),
            },
          });
        },
        reactivateClicked: () => {
          trackEvent(EventNames.SUBSCRIPTION_REACTIVATE_CLICKED);
        },
        reactivateConfirmed: () => {
          trackEvent(EventNames.SUBSCRIPTION_REACTIVATE_CONFIRMED);
        },
        discountDisplayed: (
          metadata?: Record<string, unknown> & {
            discount_type?: string;
            discount_duration?: string;
            coupon_name?: string;
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_DISCOUNT_DISPLAYED, { metadata });
        },
        // Interaction: "Show all" under the sidebar's recent-invoice preview,
        // opening the full invoice-history modal (GDN-2343).
        invoiceHistoryOpened: (
          metadata?: Record<string, unknown> & {
            org_type?: 'group' | 'solo';
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_INVOICE_HISTORY_OPENED, { metadata });
        },
        invoiceHistoryClosed: (
          metadata?: Record<string, unknown> & {
            org_type?: 'group' | 'solo';
          },
        ) => {
          trackEvent(EventNames.SUBSCRIPTION_INVOICE_HISTORY_CLOSED, { metadata });
        },
      },
      // Practice redesign (GDN-1618). Cross-cutting events that don't belong
      // to either `subscriptionManagement` (org-side billing) or `clinicians`
      // (per-member ops); namespaced separately so the redesigned surface keeps
      // a clean analytics identity as more sub-issues land.
      practice: {
        personCardOpened: (
          metadata?: Record<string, unknown> & {
            member_id?: string;
            role_count?: number;
            has_tier?: boolean;
            addon_count?: number;
          },
        ) => {
          trackEvent(EventNames.PRACTICE_PERSON_CARD_OPENED, {
            metadata: {
              ...metadata,
              ...(metadata?.role_count !== undefined && {
                role_count: String(metadata.role_count),
              }),
              ...(metadata?.has_tier !== undefined && {
                has_tier: String(metadata.has_tier),
              }),
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
            },
          });
        },
        personCardSectionExpanded: (
          metadata?: Record<string, unknown> & {
            member_id?: string;
            section?: 'roles' | 'tier' | 'supervision' | 'profile' | 'billing';
          },
        ) => {
          trackEvent(EventNames.PRACTICE_PERSON_CARD_SECTION_EXPANDED, { metadata });
        },
        batchEditOpened: (
          metadata?: Record<string, unknown> & {
            member_count?: number;
            is_trialing?: boolean;
            billing_interval?: string;
          },
        ) => {
          trackEvent(EventNames.PRACTICE_BATCH_EDIT_OPENED, {
            metadata: {
              ...metadata,
              ...(metadata?.member_count !== undefined && {
                member_count: String(metadata.member_count),
              }),
              ...(metadata?.is_trialing !== undefined && {
                is_trialing: String(metadata.is_trialing),
              }),
            },
          });
        },
        batchEditApplied: (
          metadata?: Record<string, unknown> & {
            members_changed?: number;
            members_succeeded?: number;
            members_errored?: number;
            cost_delta_cents?: number;
          },
        ) => {
          trackEvent(EventNames.PRACTICE_BATCH_EDIT_APPLIED, {
            metadata: {
              ...metadata,
              ...(metadata?.members_changed !== undefined && {
                members_changed: String(metadata.members_changed),
              }),
              ...(metadata?.members_succeeded !== undefined && {
                members_succeeded: String(metadata.members_succeeded),
              }),
              ...(metadata?.members_errored !== undefined && {
                members_errored: String(metadata.members_errored),
              }),
              ...(metadata?.cost_delta_cents !== undefined && {
                cost_delta_cents: String(metadata.cost_delta_cents),
              }),
            },
          });
        },
      },
      practiceSettings: {
        organizationNameChanged: () => {
          trackEvent(EventNames.ORGANIZATION_NAME_CHANGED);
        },
      },
      clinicians: {
        pageViewed: (
          metadata?: Record<string, unknown> & {
            member_count?: number;
            active_member_count?: number;
            pending_invitation_count?: number;
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_PAGE_VIEWED, {
            metadata: {
              ...metadata,
              ...(metadata?.member_count !== undefined && {
                member_count: String(metadata.member_count),
              }),
              ...(metadata?.active_member_count !== undefined && {
                active_member_count: String(metadata.active_member_count),
              }),
              ...(metadata?.pending_invitation_count !== undefined && {
                pending_invitation_count: String(metadata.pending_invitation_count),
              }),
            },
          });
        },
        inviteDialogOpened: () => {
          trackEvent(EventNames.CLINICIANS_INVITE_DIALOG_OPENED);
        },
        inviteSent: (
          metadata?: Record<string, unknown> & {
            // `role` is the deprecated singular field; kept (set to roles[0])
            // so existing dashboards that key off the string field don't break
            // mid-migration. New charts should use `roles` + `role_count`.
            role?: string;
            roles?: string[];
            role_count?: number;
            has_tier?: boolean;
            tier_id?: string;
            addon_count?: number;
            addon_ids?: string[];
            needs_cosign?: boolean;
            can_prepare_prescriptions_on_behalf?: boolean;
            success?: boolean;
            was_resent?: boolean;
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_INVITE_SENT, {
            metadata: {
              ...metadata,
              ...(metadata?.has_tier !== undefined && {
                has_tier: String(metadata.has_tier),
              }),
              ...(metadata?.role_count !== undefined && {
                role_count: String(metadata.role_count),
              }),
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
              ...(metadata?.needs_cosign !== undefined && {
                needs_cosign: String(metadata.needs_cosign),
              }),
              ...(metadata?.can_prepare_prescriptions_on_behalf !== undefined && {
                can_prepare_prescriptions_on_behalf: String(
                  metadata.can_prepare_prescriptions_on_behalf,
                ),
              }),
              ...(metadata?.success !== undefined && {
                success: String(metadata.success),
              }),
              ...(metadata?.was_resent !== undefined && {
                was_resent: String(metadata.was_resent),
              }),
            },
          });
        },
        memberRowExpanded: (
          metadata?: Record<string, unknown> & {
            member_id?: string;
            role_count?: number;
            has_bundles?: boolean;
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_MEMBER_ROW_EXPANDED, {
            metadata: {
              ...metadata,
              ...(metadata?.role_count !== undefined && {
                role_count: String(metadata.role_count),
              }),
              ...(metadata?.has_bundles !== undefined && {
                has_bundles: String(metadata.has_bundles),
              }),
            },
          });
        },
        memberEditOpened: (
          metadata?: Record<string, unknown> & {
            member_id?: string;
            role_count?: number;
            has_tier?: boolean;
            addon_count?: number;
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_MEMBER_EDIT_OPENED, {
            metadata: {
              ...metadata,
              ...(metadata?.role_count !== undefined && {
                role_count: String(metadata.role_count),
              }),
              ...(metadata?.has_tier !== undefined && {
                has_tier: String(metadata.has_tier),
              }),
              ...(metadata?.addon_count !== undefined && {
                addon_count: String(metadata.addon_count),
              }),
            },
          });
        },
        memberRoleChanged: (
          metadata?: Record<string, unknown> & {
            member_id?: string;
            role_count?: number;
            roles_added?: number;
            roles_removed?: number;
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_MEMBER_ROLE_CHANGED, {
            metadata: {
              ...metadata,
              ...(metadata?.role_count !== undefined && {
                role_count: String(metadata.role_count),
              }),
              ...(metadata?.roles_added !== undefined && {
                roles_added: String(metadata.roles_added),
              }),
              ...(metadata?.roles_removed !== undefined && {
                roles_removed: String(metadata.roles_removed),
              }),
            },
          });
        },
        memberBillUnderChanged: (
          metadata?: Record<string, unknown> & {
            member_id?: string;
            scope?: 'all_payers' | 'payer_rule';
            has_target?: boolean;
            payer_rule_count?: number;
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_MEMBER_BILL_UNDER_CHANGED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_target !== undefined && {
                has_target: String(metadata.has_target),
              }),
              ...(metadata?.payer_rule_count !== undefined && {
                payer_rule_count: String(metadata.payer_rule_count),
              }),
            },
          });
        },
        memberSupervisorChanged: (
          metadata?: Record<string, unknown> & {
            member_id?: string;
            has_supervisor?: boolean;
            seeded_from_bill_under?: boolean;
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_MEMBER_SUPERVISOR_CHANGED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_supervisor !== undefined && {
                has_supervisor: String(metadata.has_supervisor),
              }),
              ...(metadata?.seeded_from_bill_under !== undefined && {
                seeded_from_bill_under: String(metadata.seeded_from_bill_under),
              }),
            },
          });
        },
        memberBundleChanged: (
          metadata?: Record<string, unknown> & {
            member_id?: string;
            tier_changed?: boolean;
            from_tier_id?: string;
            to_tier_id?: string;
            addons_added?: number;
            addons_removed?: number;
            addon_ids_added?: string[];
            addon_ids_removed?: string[];
            addon_names_added?: string[];
            addon_names_removed?: string[];
            final_addon_count?: number;
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_MEMBER_BUNDLE_CHANGED, {
            metadata: {
              ...metadata,
              ...(metadata?.tier_changed !== undefined && {
                tier_changed: String(metadata.tier_changed),
              }),
              ...(metadata?.addons_added !== undefined && {
                addons_added: String(metadata.addons_added),
              }),
              ...(metadata?.addons_removed !== undefined && {
                addons_removed: String(metadata.addons_removed),
              }),
              ...(metadata?.final_addon_count !== undefined && {
                final_addon_count: String(metadata.final_addon_count),
              }),
            },
          });
        },
        memberDeactivated: (
          metadata?: Record<string, unknown> & {
            member_id?: string;
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_MEMBER_DEACTIVATED, { metadata });
        },
        memberReactivated: (
          metadata?: Record<string, unknown> & {
            member_id?: string;
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_MEMBER_REACTIVATED, { metadata });
        },
        invitationResent: (
          metadata?: Record<string, unknown> & {
            invitation_id?: string;
            success?: boolean;
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_INVITATION_RESENT, {
            metadata: {
              ...metadata,
              ...(metadata?.success !== undefined && {
                success: String(metadata.success),
              }),
            },
          });
        },
        invitationDeleted: (
          metadata?: Record<string, unknown> & {
            invitation_id?: string;
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_INVITATION_DELETED, { metadata });
        },
        invitationEdited: (
          metadata?: Record<string, unknown> & {
            invitation_id?: string;
            role_changed?: boolean;
            bundle_changed?: boolean;
            from_tier_id?: string;
            to_tier_id?: string;
            addon_ids_added?: string[];
            addon_ids_removed?: string[];
            addon_names_added?: string[];
            addon_names_removed?: string[];
          },
        ) => {
          trackEvent(EventNames.CLINICIANS_INVITATION_EDITED, {
            metadata: {
              ...metadata,
              ...(metadata?.role_changed !== undefined && {
                role_changed: String(metadata.role_changed),
              }),
              ...(metadata?.bundle_changed !== undefined && {
                bundle_changed: String(metadata.bundle_changed),
              }),
            },
          });
        },
        refreshClicked: () => {
          trackEvent(EventNames.CLINICIANS_REFRESH_CLICKED);
        },
      },

      // Patient Import metrics
      patientImport: {
        fileSelected: (
          metadata?: Record<string, unknown> & {
            file_type?: string;
            file_size_kb?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_FILE_SELECTED, {
            metadata: {
              ...metadata,
              ...(metadata?.file_size_kb !== undefined && {
                file_size_kb: String(metadata.file_size_kb),
              }),
            },
          });
        },
        uploadStarted: (
          metadata?: Record<string, unknown> & {
            file_type?: string;
            file_size_kb?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_UPLOAD_STARTED, {
            metadata: {
              ...metadata,
              ...(metadata?.file_size_kb !== undefined && {
                file_size_kb: String(metadata.file_size_kb),
              }),
            },
          });
        },
        uploadCompleted: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_UPLOAD_COMPLETED, { metadata });
        },
        uploadFailed: (
          metadata?: Record<string, unknown> & {
            error_message?: string;
            status_code?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_UPLOAD_FAILED, {
            metadata: {
              ...metadata,
              ...(metadata?.status_code !== undefined && {
                status_code: String(metadata.status_code),
              }),
            },
          });
        },
        sessionsViewed: (
          metadata?: Record<string, unknown> & {
            active_count?: number;
            complete_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_SESSIONS_VIEWED, {
            metadata: {
              ...metadata,
              ...(metadata?.active_count !== undefined && {
                active_count: String(metadata.active_count),
              }),
              ...(metadata?.complete_count !== undefined && {
                complete_count: String(metadata.complete_count),
              }),
            },
          });
        },
        sessionDeleted: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_SESSION_DELETED, { metadata });
        },
        reviewViewed: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            total_patients?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_REVIEW_VIEWED, {
            metadata: {
              ...metadata,
              ...(metadata?.total_patients !== undefined && {
                total_patients: String(metadata.total_patients),
              }),
            },
          });
        },
        approved: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            total_patients?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_APPROVED, {
            metadata: {
              ...metadata,
              ...(metadata?.total_patients !== undefined && {
                total_patients: String(metadata.total_patients),
              }),
            },
          });
        },
        completed: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            imported_count?: number;
            failed_count?: number;
            new_patients?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_COMPLETED, {
            metadata: {
              ...metadata,
              ...(metadata?.imported_count !== undefined && {
                imported_count: String(metadata.imported_count),
              }),
              ...(metadata?.failed_count !== undefined && {
                failed_count: String(metadata.failed_count),
              }),
              ...(metadata?.new_patients !== undefined && {
                new_patients: String(metadata.new_patients),
              }),
            },
          });
        },
        failed: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            error_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_FAILED, {
            metadata: {
              ...metadata,
              ...(metadata?.error_count !== undefined && {
                error_count: String(metadata.error_count),
              }),
            },
          });
        },
        cancelled: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_CANCELLED, { metadata });
        },
        wizardStepViewed: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            step_id?: string;
            item_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_WIZARD_STEP_VIEWED, {
            metadata: {
              ...metadata,
              ...(metadata?.item_count !== undefined && {
                item_count: String(metadata.item_count),
              }),
            },
          });
        },
        wizardDismissed: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            ambiguous_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_WIZARD_DISMISSED, {
            metadata: {
              ...metadata,
              ...(metadata?.ambiguous_count !== undefined && {
                ambiguous_count: String(metadata.ambiguous_count),
              }),
            },
          });
        },
        skipAppointments: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            appointments_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_SKIP_APPOINTMENTS, {
            metadata: {
              ...metadata,
              ...(metadata?.appointments_count !== undefined && {
                appointments_count: String(metadata.appointments_count),
              }),
            },
          });
        },
        skipNewPatients: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            new_patients_count?: number;
            matched_patients_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_IMPORT_SKIP_NEW_PATIENTS, {
            metadata: {
              ...metadata,
              ...(metadata?.new_patients_count !== undefined && {
                new_patients_count: String(metadata.new_patients_count),
              }),
              ...(metadata?.matched_patients_count !== undefined && {
                matched_patients_count: String(metadata.matched_patients_count),
              }),
            },
          });
        },
      },

      // Insurance card upload metrics
      insuranceCard: {
        uploadStarted: (
          metadata?: Record<string, unknown> & {
            file_count?: number;
          },
        ) => {
          trackEvent(EventNames.INSURANCE_CARD_UPLOAD_STARTED, {
            metadata: {
              ...metadata,
              ...(metadata?.file_count !== undefined && {
                file_count: String(metadata.file_count),
              }),
            },
          });
        },
        uploadCompleted: (
          metadata?: Record<string, unknown> & {
            confidence?: number;
            has_front?: boolean;
            has_back?: boolean;
          },
        ) => {
          trackEvent(EventNames.INSURANCE_CARD_UPLOAD_COMPLETED, {
            metadata: {
              ...metadata,
              ...(metadata?.confidence !== undefined && {
                confidence: String(metadata.confidence),
              }),
              ...(metadata?.has_front !== undefined && {
                has_front: String(metadata.has_front),
              }),
              ...(metadata?.has_back !== undefined && {
                has_back: String(metadata.has_back),
              }),
            },
          });
        },
        uploadFailed: (
          metadata?: Record<string, unknown> & {
            error_type?: string;
          },
        ) => {
          trackEvent(EventNames.INSURANCE_CARD_UPLOAD_FAILED, { metadata });
        },
        // GDN-2730 review flow. No card values here — payer, member ID and
        // subscriber names are all PHI-adjacent. What is worth measuring is
        // whether the human actually corrected the extraction, which is the
        // question the proposal step exists to answer.
        reviewOpened: (metadata: {
          patient_id: string;
          entry_point: 'card_upload' | 'proposal_card';
        }) => {
          trackEvent(EventNames.INSURANCE_CARD_REVIEW_OPENED, { metadata });
        },
        reviewSubmitted: (metadata: {
          patient_id: string;
          relationship_changed: boolean;
          fields_edited: number;
        }) => {
          trackEvent(EventNames.INSURANCE_CARD_REVIEW_SUBMITTED, {
            metadata: {
              patient_id: metadata.patient_id,
              relationship_changed: String(metadata.relationship_changed),
              fields_edited: String(metadata.fields_edited),
            },
          });
        },
        reviewClosed: (metadata: { patient_id: string }) => {
          trackEvent(EventNames.INSURANCE_CARD_REVIEW_CLOSED, { metadata });
        },
      },

      insuranceHistory: {
        opened: (metadata: { patient_id: string; history_count: number }) => {
          trackEvent(EventNames.INSURANCE_HISTORY_OPENED, {
            metadata: {
              patient_id: metadata.patient_id,
              history_count: String(metadata.history_count),
            },
          });
        },
        addClicked: (metadata: { patient_id: string }) => {
          trackEvent(EventNames.INSURANCE_HISTORY_ADD_CLICKED, { metadata });
        },
        editClicked: (metadata: { patient_id: string; coverage_id: string }) => {
          trackEvent(EventNames.INSURANCE_HISTORY_EDIT_CLICKED, { metadata });
        },
        saveSubmitted: (metadata: {
          patient_id: string;
          coverage_id?: string;
          coverage_order: number;
          mode: 'add' | 'edit';
        }) => {
          trackEvent(EventNames.INSURANCE_HISTORY_SAVE_SUBMITTED, {
            metadata: {
              ...metadata,
              coverage_order: String(metadata.coverage_order),
            },
          });
        },
        removeClicked: (metadata: { patient_id: string; coverage_id: string }) => {
          trackEvent(EventNames.INSURANCE_HISTORY_REMOVE_CLICKED, { metadata });
        },
        removeSubmitted: (metadata: { patient_id: string; coverage_id: string }) => {
          trackEvent(EventNames.INSURANCE_HISTORY_REMOVE_SUBMITTED, { metadata });
        },
      },

      // W2 course management. Fixed enums and booleans only: no patient,
      // authorization, payer, member, or coverage identifiers.
      priorAuthorizationCourse: {
        addClicked: () => {
          trackEvent(EventNames.PRIOR_AUTHORIZATION_COURSE_ADD_CLICKED);
        },
        editClicked: (metadata: { course_kind: 'authorization' | 'benefit_limit' }) => {
          trackEvent(EventNames.PRIOR_AUTHORIZATION_COURSE_EDIT_CLICKED, { metadata });
        },
        saveSubmitted: (metadata: {
          course_kind: 'authorization' | 'benefit_limit';
          has_coverage: boolean;
          mode: 'create' | 'edit';
        }) => {
          trackEvent(EventNames.PRIOR_AUTHORIZATION_COURSE_SAVE_SUBMITTED, {
            metadata: {
              ...metadata,
              has_coverage: String(metadata.has_coverage),
            },
          });
        },
        retryClicked: () => {
          trackEvent(EventNames.PRIOR_AUTHORIZATION_COURSE_RETRY_CLICKED);
        },
      },

      // W2 plan-centered coverage workspace. Fixed workflow enums only;
      // payer, member, authorization, and patient identifiers are excluded.
      insuranceWorkspace: {
        navigationClicked: (metadata: { destination: string }) => {
          trackEvent(EventNames.INSURANCE_WORKSPACE_NAVIGATION_CLICKED, { metadata });
        },
        taskChooserOpened: (metadata: { mode: 'setup' | 'change' }) => {
          trackEvent(EventNames.INSURANCE_WORKSPACE_TASK_CHOOSER_OPENED, { metadata });
        },
        taskSelected: (metadata: {
          mode: 'setup' | 'change';
          source: 'authorization_summary' | 'chooser' | 'coverage_map';
          task: string;
        }) => {
          trackEvent(EventNames.INSURANCE_WORKSPACE_TASK_SELECTED, { metadata });
        },
        taskClosed: (metadata: { outcome: 'cancelled' | 'completed'; task: string }) => {
          trackEvent(EventNames.INSURANCE_WORKSPACE_TASK_CLOSED, { metadata });
        },
        setupSubmitted: (metadata: { path: 'insurance' | 'self_pay' }) => {
          trackEvent(EventNames.INSURANCE_WORKSPACE_SETUP_SUBMITTED, { metadata });
        },
        changeSubmitted: (metadata: { change: 'insurance' | 'self_pay' }) => {
          trackEvent(EventNames.INSURANCE_WORKSPACE_CHANGE_SUBMITTED, { metadata });
        },
      },

      // Patient Payment Methods metrics
      patientPaymentMethod: {
        /**
         * Track when payment methods panel is expanded
         */
        panelExpanded: (
          patientId: string,
          metadata?: {
            payment_methods_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_PAYMENT_METHOD_PANEL_EXPANDED, {
            metadata: {
              patient_id: patientId,
              ...(metadata?.payment_methods_count !== undefined && {
                payment_methods_count: String(metadata.payment_methods_count),
              }),
            },
          });
        },
        /**
         * Track when add payment method button is clicked
         */
        addClicked: (patientId: string) => {
          trackEvent(EventNames.PATIENT_PAYMENT_METHOD_ADD_CLICKED, {
            metadata: { patient_id: patientId },
          });
        },
        /**
         * Track when payment method is successfully added
         */
        added: (
          patientId: string,
          metadata?: {
            payment_method_type?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_PAYMENT_METHOD_ADDED, {
            metadata: { patient_id: patientId, ...metadata },
          });
        },
        /**
         * Track when adding payment method fails
         */
        addFailed: (
          patientId: string,
          metadata?: {
            error?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_PAYMENT_METHOD_ADD_FAILED, {
            metadata: { patient_id: patientId, ...metadata },
          });
        },
        /**
         * Track when payment method is deleted
         */
        deleted: (
          patientId: string,
          metadata?: {
            payment_method_id?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_PAYMENT_METHOD_DELETED, {
            metadata: { patient_id: patientId, ...metadata },
          });
        },
        /**
         * Track when deleting payment method fails
         */
        deleteFailed: (
          patientId: string,
          metadata?: {
            payment_method_id?: string;
            error?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_PAYMENT_METHOD_DELETE_FAILED, {
            metadata: { patient_id: patientId, ...metadata },
          });
        },
        /**
         * Track when payment method is set as default
         */
        setDefault: (
          patientId: string,
          metadata?: {
            payment_method_id?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_PAYMENT_METHOD_SET_DEFAULT, {
            metadata: { patient_id: patientId, ...metadata },
          });
        },
      },

      // Patient Invoices metrics
      patientInvoice: {
        /**
         * Track when invoices panel is expanded
         */
        panelExpanded: (
          patientId: string,
          metadata?: {
            invoices_count?: number;
          },
        ) => {
          trackEvent(EventNames.PATIENT_INVOICE_PANEL_EXPANDED, {
            metadata: {
              patient_id: patientId,
              ...(metadata?.invoices_count !== undefined && {
                invoices_count: String(metadata.invoices_count),
              }),
            },
          });
        },
        /**
         * Track when invoice hosted URL is viewed
         */
        invoiceViewed: (
          patientId: string,
          invoiceId: string,
          metadata?: {
            invoice_status?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_INVOICE_VIEWED, {
            metadata: {
              patient_id: patientId,
              invoice_id: invoiceId,
              ...metadata,
            },
          });
        },
        /**
         * Track when invoice PDF is downloaded
         */
        invoiceDownloaded: (
          patientId: string,
          invoiceId: string,
          metadata?: {
            invoice_status?: string;
          },
        ) => {
          trackEvent(EventNames.PATIENT_INVOICE_DOWNLOADED, {
            metadata: {
              patient_id: patientId,
              invoice_id: invoiceId,
              ...metadata,
            },
          });
        },
      },

      // JotRx onboarding metrics
      jotrx: {
        bannerShown: (
          metadata?: Record<string, unknown> & {
            setup_state?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_BANNER_SHOWN, { metadata });
        },
        bannerDismissed: (
          metadata?: Record<string, unknown> & {
            setup_state?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_BANNER_DISMISSED, { metadata });
        },
        setupStarted: (
          metadata?: Record<string, unknown> & {
            source?: string;
            setup_state?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_SETUP_STARTED, { metadata });
        },
        setupCallBooked: (
          metadata?: Record<string, unknown> & {
            source?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_SETUP_CALL_BOOKED, { metadata });
        },
        demoRequested: (
          metadata?: Record<string, unknown> & {
            source?: string;
            setup_state?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_DEMO_REQUESTED, { metadata });
        },
        calloutShown: (
          metadata?: Record<string, unknown> & {
            page?: string;
            setup_state?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_CALLOUT_SHOWN, { metadata });
        },
        calloutCtaClicked: (
          metadata?: Record<string, unknown> & {
            page?: string;
            setup_state?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_CALLOUT_CTA_CLICKED, { metadata });
        },
        dosespotSetupOpened: (
          metadata?: Record<string, unknown> & {
            source?: string;
            setup_state?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_DOSESPOT_SETUP_OPENED, { metadata });
        },
        prescribeOpened: (
          metadata?: Record<string, unknown> & {
            source?: string;
            patient_id?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_PRESCRIBE_OPENED, { metadata });
        },
        orgSetupCompleted: (metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.JOTRX_ORG_SETUP_COMPLETED, { metadata });
        },
        orgSetupFailed: (
          metadata?: Record<string, unknown> & {
            error_type?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_ORG_SETUP_FAILED, { metadata });
        },
        clinicianRegistered: (
          metadata?: Record<string, unknown> & {
            prescribes_controlled_substances?: boolean;
            has_dea_number?: boolean;
            uses_another_platform?: boolean;
          },
        ) => {
          trackEvent(EventNames.JOTRX_CLINICIAN_REGISTERED, { metadata });
        },
        clinicianRegistrationFailed: (
          metadata?: Record<string, unknown> & {
            error_type?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_CLINICIAN_REGISTRATION_FAILED, { metadata });
        },
        patientConnected: (
          metadata?: Record<string, unknown> & {
            patient_id?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_PATIENT_CONNECTED, { metadata });
        },
        patientConnectFailed: (
          metadata?: Record<string, unknown> & {
            patient_id?: string;
            error_type?: string;
          },
        ) => {
          trackEvent(EventNames.JOTRX_PATIENT_CONNECT_FAILED, { metadata });
        },
      },

      // Prescribing notifications bell (GDN-1062)
      prescribingNotifications: {
        bellOpened: (metadata: {
          total_count: number;
          transmission_count: number;
          pending_count: number;
          refill_count: number;
          change_count: number;
        }) => {
          trackEvent(EventNames.PRESCRIBING_NOTIFICATIONS_BELL_OPENED, { metadata });
        },
        rowClicked: (metadata: {
          notification_type: 'transmission' | 'pending' | 'refill' | 'change';
          count: number;
        }) => {
          trackEvent(EventNames.PRESCRIBING_NOTIFICATIONS_ROW_CLICKED, { metadata });
        },
      },

      // Audio device service metrics
      audioDevice: {
        initialized: (
          metadata?: Record<string, unknown> & {
            device_count?: number;
            permission_granted?: boolean;
            selected_transport?: string;
          },
        ) => {
          trackEvent(EventNames.AUDIO_DEVICE_SERVICE_INITIALIZED, {
            metadata: {
              ...metadata,
              ...(metadata?.device_count !== undefined && {
                device_count: String(metadata.device_count),
              }),
            },
          });
        },
        deviceChanged: (
          metadata?: Record<string, unknown> & {
            added_count?: number;
            removed_count?: number;
            changed_count?: number;
            during_recording?: boolean;
            enumerate_duration_ms?: number;
          },
        ) => {
          trackEvent(EventNames.AUDIO_DEVICE_CHANGED, {
            metadata: {
              ...metadata,
              ...(metadata?.added_count !== undefined && {
                added_count: String(metadata.added_count),
              }),
              ...(metadata?.removed_count !== undefined && {
                removed_count: String(metadata.removed_count),
              }),
              ...(metadata?.changed_count !== undefined && {
                changed_count: String(metadata.changed_count),
              }),
              ...(metadata?.enumerate_duration_ms !== undefined && {
                enumerate_duration_ms: String(metadata.enumerate_duration_ms),
              }),
            },
          });
        },
        deviceLost: (
          metadata?: Record<string, unknown> & {
            device_transport?: string;
            during_recording?: boolean;
          },
        ) => {
          trackEvent(EventNames.AUDIO_DEVICE_LOST, { metadata });
        },
        substituted: (
          metadata?: Record<string, unknown> & {
            requested_transport?: string;
            actual_transport?: string;
            /** Why the requested device was abandoned: overconstrained | not_found | not_readable | timeout */
            fallback_reason?: string;
          },
        ) => {
          trackEvent(EventNames.AUDIO_DEVICE_SUBSTITUTED, { metadata });
        },
        micAutoAcquired: (metadata?: { microphone_id?: string; device_transport?: string }) => {
          trackEvent(EventNames.ENCOUNTER_MIC_AUTO_ACQUIRED, { metadata });
        },
        autoFallback: (
          metadata?: Record<string, unknown> & {
            failed_device_label?: string;
            candidate_device_label?: string;
            devices_tried_count?: number;
          },
        ) => {
          trackEvent(EventNames.ENCOUNTER_MIC_AUTO_FALLBACK, { metadata });
        },
        healthChanged: (
          metadata?: Record<string, unknown> & {
            from_state?: string;
            to_state?: string;
            during_recording?: boolean;
            analyser_duration_ms?: number;
            audio_context_state?: string;
            polls_since_start?: number;
          },
        ) => {
          trackEvent(EventNames.AUDIO_HEALTH_STATE_CHANGED, {
            metadata: {
              ...metadata,
              ...(metadata?.analyser_duration_ms !== undefined && {
                analyser_duration_ms: String(metadata.analyser_duration_ms),
              }),
              ...(metadata?.polls_since_start !== undefined && {
                polls_since_start: String(metadata.polls_since_start),
              }),
            },
          });
        },
        permissionResult: (
          metadata?: Record<string, unknown> & {
            granted?: boolean;
            error_type?: string;
          },
        ) => {
          trackEvent(EventNames.AUDIO_PERMISSION_RESULT, { metadata });
        },
        performanceSummary: (
          metadata?: Record<string, unknown> & {
            session_duration_seconds?: number;
            total_polls?: number;
            analyser_duration_p95_ms?: number;
            analyser_duration_max_ms?: number;
            enumerate_call_count?: number;
            enumerate_duration_max_ms?: number;
            audio_context_state?: string;
            silence_transition_count?: number;
            health_states_visited?: string;
            device_count?: number;
          },
        ) => {
          trackEvent(EventNames.AUDIO_PERFORMANCE_SUMMARY, {
            metadata: {
              ...metadata,
              ...(metadata?.session_duration_seconds !== undefined && {
                session_duration_seconds: String(metadata.session_duration_seconds),
              }),
              ...(metadata?.total_polls !== undefined && {
                total_polls: String(metadata.total_polls),
              }),
              ...(metadata?.analyser_duration_p95_ms !== undefined && {
                analyser_duration_p95_ms: String(metadata.analyser_duration_p95_ms),
              }),
              ...(metadata?.analyser_duration_max_ms !== undefined && {
                analyser_duration_max_ms: String(metadata.analyser_duration_max_ms),
              }),
              ...(metadata?.enumerate_call_count !== undefined && {
                enumerate_call_count: String(metadata.enumerate_call_count),
              }),
              ...(metadata?.enumerate_duration_max_ms !== undefined && {
                enumerate_duration_max_ms: String(metadata.enumerate_duration_max_ms),
              }),
              ...(metadata?.silence_transition_count !== undefined && {
                silence_transition_count: String(metadata.silence_transition_count),
              }),
              ...(metadata?.device_count !== undefined && {
                device_count: String(metadata.device_count),
              }),
            },
          });
        },
      },
      // Session completion & recovery metrics (ADR-007)
      sessionCompletion: {
        drainCompleted: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            drain_duration_ms?: number;
            completed_count?: number;
            failed_count?: number;
            timed_out?: boolean;
          },
        ) => {
          trackEvent(EventNames.SESSION_COMPLETION_DRAIN_COMPLETED, {
            metadata: {
              ...metadata,
              ...(metadata?.drain_duration_ms !== undefined && {
                drain_duration_ms: String(metadata.drain_duration_ms),
              }),
              ...(metadata?.completed_count !== undefined && {
                completed_count: String(metadata.completed_count),
              }),
              ...(metadata?.failed_count !== undefined && {
                failed_count: String(metadata.failed_count),
              }),
              ...(metadata?.timed_out !== undefined && {
                timed_out: metadata.timed_out ? 'true' : 'false',
              }),
            },
          });
        },
        finished: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            total_chunks?: number;
            uploaded_chunks?: number;
            failed_chunks?: number;
            failed_chunks_ratio?: number;
            drain_duration_seconds?: number;
            timed_out?: boolean;
            recording_end_confirmed?: boolean;
            status?: string;
          },
        ) => {
          trackEvent(EventNames.SESSION_COMPLETION_FINISHED, {
            metadata: {
              ...metadata,
              ...(metadata?.total_chunks !== undefined && {
                total_chunks: String(metadata.total_chunks),
              }),
              ...(metadata?.uploaded_chunks !== undefined && {
                uploaded_chunks: String(metadata.uploaded_chunks),
              }),
              ...(metadata?.failed_chunks !== undefined && {
                failed_chunks: String(metadata.failed_chunks),
              }),
              ...(metadata?.failed_chunks_ratio !== undefined && {
                failed_chunks_ratio: String(metadata.failed_chunks_ratio),
              }),
              ...(metadata?.drain_duration_seconds !== undefined && {
                drain_duration_seconds: String(metadata.drain_duration_seconds),
              }),
              ...(metadata?.timed_out !== undefined && {
                timed_out: metadata.timed_out ? 'true' : 'false',
              }),
              ...(metadata?.recording_end_confirmed !== undefined && {
                recording_end_confirmed: metadata.recording_end_confirmed ? 'true' : 'false',
              }),
            },
          });
        },
        timeout: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            queued_at_timeout?: number;
            in_flight_at_timeout?: number;
          },
        ) => {
          trackEvent(EventNames.SESSION_COMPLETION_TIMEOUT, {
            metadata: {
              ...metadata,
              ...(metadata?.queued_at_timeout !== undefined && {
                queued_at_timeout: String(metadata.queued_at_timeout),
              }),
              ...(metadata?.in_flight_at_timeout !== undefined && {
                in_flight_at_timeout: String(metadata.in_flight_at_timeout),
              }),
            },
          });
        },
        networkFailure: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            pending_chunks?: number;
          },
        ) => {
          trackEvent(EventNames.SESSION_COMPLETION_NETWORK_FAILURE, {
            metadata: {
              ...metadata,
              ...(metadata?.pending_chunks !== undefined && {
                pending_chunks: String(metadata.pending_chunks),
              }),
            },
          });
        },
        fatalError: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            error_type?: string;
            error_message?: string;
            pending_chunks?: number;
            duration_seconds?: number;
          },
        ) => {
          trackEvent(EventNames.SESSION_COMPLETION_FATAL_ERROR, {
            metadata: {
              ...metadata,
              ...(metadata?.pending_chunks !== undefined && {
                pending_chunks: String(metadata.pending_chunks),
              }),
              ...(metadata?.duration_seconds !== undefined && {
                duration_seconds: String(metadata.duration_seconds),
              }),
            },
          });
        },
        abandoned: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            completion_status?: string;
            drain_elapsed_seconds?: number;
            total_chunks?: number;
            uploaded_chunks?: number;
            failed_chunks?: number;
          },
        ) => {
          trackEvent(EventNames.SESSION_ABANDONED, {
            metadata: {
              ...metadata,
              ...(metadata?.drain_elapsed_seconds !== undefined && {
                drain_elapsed_seconds: String(metadata.drain_elapsed_seconds),
              }),
              ...(metadata?.total_chunks !== undefined && {
                total_chunks: String(metadata.total_chunks),
              }),
              ...(metadata?.uploaded_chunks !== undefined && {
                uploaded_chunks: String(metadata.uploaded_chunks),
              }),
              ...(metadata?.failed_chunks !== undefined && {
                failed_chunks: String(metadata.failed_chunks),
              }),
            },
          });
        },
        pageUnload: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            completion_status?: string;
            drain_elapsed_seconds?: number;
            total_chunks?: number;
            uploaded_chunks?: number;
            failed_chunks?: number;
            pending_queue_depth?: number;
            in_flight_count?: number;
          },
        ) => {
          trackEvent(EventNames.SESSION_COMPLETION_PAGE_UNLOAD, {
            metadata: {
              ...metadata,
              ...(metadata?.drain_elapsed_seconds !== undefined && {
                drain_elapsed_seconds: String(metadata.drain_elapsed_seconds),
              }),
              ...(metadata?.total_chunks !== undefined && {
                total_chunks: String(metadata.total_chunks),
              }),
              ...(metadata?.uploaded_chunks !== undefined && {
                uploaded_chunks: String(metadata.uploaded_chunks),
              }),
              ...(metadata?.failed_chunks !== undefined && {
                failed_chunks: String(metadata.failed_chunks),
              }),
              ...(metadata?.pending_queue_depth !== undefined && {
                pending_queue_depth: String(metadata.pending_queue_depth),
              }),
              ...(metadata?.in_flight_count !== undefined && {
                in_flight_count: String(metadata.in_flight_count),
              }),
            },
          });
        },
      },

      // Session recovery metrics (ADR-007)
      sessionRecovery: {
        scanCompleted: (
          metadata?: Record<string, unknown> & {
            found?: number;
            recovered?: number;
            failed?: number;
            cleaned?: number;
          },
        ) => {
          trackEvent(EventNames.SESSION_RECOVERY_SCAN_COMPLETED, {
            metadata: {
              ...metadata,
              ...(metadata?.found !== undefined && { found: String(metadata.found) }),
              ...(metadata?.recovered !== undefined && { recovered: String(metadata.recovered) }),
              ...(metadata?.failed !== undefined && { failed: String(metadata.failed) }),
              ...(metadata?.cleaned !== undefined && { cleaned: String(metadata.cleaned) }),
            },
          });
        },
        sessionRecovered: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            total_chunks?: number;
            uploaded_chunks?: number;
            failed_chunks?: number;
          },
        ) => {
          trackEvent(EventNames.SESSION_RECOVERY_SESSION_RECOVERED, {
            metadata: {
              ...metadata,
              ...(metadata?.total_chunks !== undefined && {
                total_chunks: String(metadata.total_chunks),
              }),
              ...(metadata?.uploaded_chunks !== undefined && {
                uploaded_chunks: String(metadata.uploaded_chunks),
              }),
              ...(metadata?.failed_chunks !== undefined && {
                failed_chunks: String(metadata.failed_chunks),
              }),
            },
          });
        },
        sessionFailed: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            error?: string;
          },
        ) => {
          trackEvent(EventNames.SESSION_RECOVERY_SESSION_FAILED, {
            metadata: {
              ...metadata,
            },
          });
        },
        staleManifestCleaned: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            reason?: string;
            total_chunks?: number;
          },
        ) => {
          trackEvent(EventNames.SESSION_RECOVERY_STALE_MANIFEST_CLEANED, {
            metadata: {
              ...metadata,
              ...(metadata?.total_chunks !== undefined && {
                total_chunks: String(metadata.total_chunks),
              }),
            },
          });
        },
      },

      // Clinic document events
      clinicDocuments: {
        uploaded: (
          metadata?: Record<string, unknown> & {
            clinic_document_id?: string;
            scope_type?: string;
            file_count?: number;
            source?: string;
          },
        ) => {
          trackEvent(EventNames.CLINIC_DOC_UPLOADED, {
            metadata: {
              ...metadata,
              ...(metadata?.file_count !== undefined && {
                file_count: String(metadata.file_count),
              }),
            },
          });
        },
        uploadFailed: (
          metadata?: Record<string, unknown> & {
            scope_type?: string;
            file_count?: number;
            source?: string;
            error_message?: string;
          },
        ) => {
          trackEvent(EventNames.CLINIC_DOC_UPLOAD_FAILED, {
            metadata: {
              ...metadata,
              ...(metadata?.file_count !== undefined && {
                file_count: String(metadata.file_count),
              }),
            },
          });
        },
        deleted: (
          metadata?: Record<string, unknown> & {
            clinic_document_id?: string;
          },
        ) => {
          trackEvent(EventNames.CLINIC_DOC_DELETED, { metadata });
        },
        tabSwitched: (
          metadata?: Record<string, unknown> & {
            tab?: string;
          },
        ) => {
          trackEvent(EventNames.CLINIC_DOC_TAB_SWITCHED, { metadata });
        },
        fileViewed: (
          metadata?: Record<string, unknown> & {
            clinic_document_id?: string;
            source?: string;
            has_page_ref?: boolean;
          },
        ) => {
          trackEvent(EventNames.CLINIC_DOC_FILE_VIEWED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_page_ref !== undefined && {
                has_page_ref: metadata.has_page_ref ? 'true' : 'false',
              }),
            },
          });
        },
        chatMessageSent: (
          metadata?: Record<string, unknown> & {
            thread_id?: string;
            message_length?: number;
          },
        ) => {
          trackEvent(EventNames.CLINIC_DOC_CHAT_MESSAGE_SENT, {
            metadata: {
              ...metadata,
              ...(metadata?.message_length !== undefined && {
                message_length: String(metadata.message_length),
              }),
            },
          });
        },
        chatResponseReceived: (
          metadata?: Record<string, unknown> & {
            thread_id?: string;
            duration_ms?: number;
          },
        ) => {
          trackEvent(EventNames.CLINIC_DOC_CHAT_RESPONSE_RECEIVED, {
            metadata: {
              ...metadata,
              ...(metadata?.duration_ms !== undefined && {
                duration_ms: String(metadata.duration_ms),
              }),
            },
          });
        },
        chatError: (
          metadata?: Record<string, unknown> & {
            thread_id?: string;
            error_message?: string;
          },
        ) => {
          trackEvent(EventNames.CLINIC_DOC_CHAT_ERROR, { metadata });
        },
        chatConversationCleared: (
          metadata?: Record<string, unknown> & {
            thread_id?: string;
            message_count?: number;
          },
        ) => {
          trackEvent(EventNames.CLINIC_DOC_CHAT_CONVERSATION_CLEARED, {
            metadata: {
              ...metadata,
              ...(metadata?.message_count !== undefined && {
                message_count: String(metadata.message_count),
              }),
            },
          });
        },
        chatCitationClicked: (
          metadata?: Record<string, unknown> & {
            clinic_document_id?: string;
            has_page_ref?: boolean;
            thread_id?: string;
          },
        ) => {
          trackEvent(EventNames.CLINIC_DOC_CHAT_CITATION_CLICKED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_page_ref !== undefined && {
                has_page_ref: metadata.has_page_ref ? 'true' : 'false',
              }),
            },
          });
        },
      },

      // Recording pipeline UI events
      recordingUI: {
        healthDegraded: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            health_tier?: string;
            queue_depth?: number;
            failed_count?: number;
            is_online?: boolean;
          },
        ) => {
          trackEvent(EventNames.RECORDING_HEALTH_DEGRADED, {
            metadata: {
              ...metadata,
              ...(metadata?.queue_depth !== undefined && {
                queue_depth: String(metadata.queue_depth),
              }),
              ...(metadata?.failed_count !== undefined && {
                failed_count: String(metadata.failed_count),
              }),
              ...(metadata?.is_online !== undefined && {
                is_online: metadata.is_online ? 'true' : 'false',
              }),
            },
          });
        },
        uploadBannerShown: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            total_chunks?: number;
            drain_elapsed_seconds?: number;
          },
        ) => {
          trackEvent(EventNames.UPLOAD_BANNER_SHOWN, {
            metadata: {
              ...metadata,
              ...(metadata?.total_chunks !== undefined && {
                total_chunks: String(metadata.total_chunks),
              }),
              ...(metadata?.drain_elapsed_seconds !== undefined && {
                drain_elapsed_seconds: String(metadata.drain_elapsed_seconds),
              }),
            },
          });
        },
        uploadBannerLossAcknowledged: (
          metadata?: Record<string, unknown> & {
            session_id?: string;
            failed_chunks?: number;
            total_chunks?: number;
            loss_severity?: string;
          },
        ) => {
          trackEvent(EventNames.UPLOAD_BANNER_LOSS_ACKNOWLEDGED, {
            metadata: {
              ...metadata,
              ...(metadata?.failed_chunks !== undefined && {
                failed_chunks: String(metadata.failed_chunks),
              }),
              ...(metadata?.total_chunks !== undefined && {
                total_chunks: String(metadata.total_chunks),
              }),
            },
          });
        },
        recoveryBannerAction: (
          metadata?: Record<string, unknown> & {
            action?: string;
            session_count?: number;
          },
        ) => {
          trackEvent(EventNames.RECOVERY_BANNER_ACTION, {
            metadata: {
              ...metadata,
              ...(metadata?.session_count !== undefined && {
                session_count: String(metadata.session_count),
              }),
            },
          });
        },
      },

      // EHR extension settings metrics
      ehrExtension: {
        settingsViewed: (
          metadata?: Record<string, unknown> & {
            view_state?: string;
          },
        ) => {
          trackEvent(EventNames.EHR_EXTENSION_SETTINGS_VIEWED, { metadata });
        },
        chromeStoreClicked: (
          metadata?: Record<string, unknown> & {
            source?: string;
          },
        ) => {
          trackEvent(EventNames.EHR_EXTENSION_CHROME_STORE_CLICKED, { metadata });
        },
        addToPlanClicked: (
          metadata?: Record<string, unknown> & {
            source?: string;
          },
        ) => {
          trackEvent(EventNames.EHR_EXTENSION_ADD_TO_PLAN_CLICKED, { metadata });
        },
        manageCliniciansClicked: (metadata?: Record<string, unknown>) => {
          trackEvent(EventNames.EHR_EXTENSION_MANAGE_CLINICIANS_CLICKED, { metadata });
        },
        tutorialClicked: (
          metadata?: Record<string, unknown> & {
            source?: string;
          },
        ) => {
          trackEvent(EventNames.EHR_EXTENSION_TUTORIAL_CLICKED, { metadata });
        },
      },

      // Telehealth call events
      telehealth: {
        joinClicked: (
          metadata?: Record<string, unknown> & {
            appointment_id?: string;
            encounter_id?: string;
            source?: string;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_JOIN_CLICKED, { metadata });
        },
        joinAttempted: (
          metadata?: Record<string, unknown> & {
            appointment_id?: string;
            encounter_id?: string;
            source?: string;
            outcome?: 'pre_call_started' | 'connected' | 'failed';
            error_code?: string;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_JOIN_ATTEMPTED, { metadata });
        },
        callConnected: (metadata?: {
          appointment_id?: string;
          encounter_id?: string;
          source?: string;
        }) => {
          trackEvent(EventNames.TELEHEALTH_CALL_CONNECTED, { metadata });
        },
        callLeft: (metadata?: {
          appointment_id?: string;
          encounter_id?: string;
          stopped_recording?: boolean;
          source?: string;
        }) => {
          trackEvent(EventNames.TELEHEALTH_CALL_LEFT, {
            metadata: {
              ...metadata,
              ...(metadata?.stopped_recording !== undefined && {
                stopped_recording: String(metadata.stopped_recording),
              }),
            },
          });
        },
        callError: (metadata?: {
          appointment_id?: string;
          encounter_id?: string;
          error_message?: string;
        }) => {
          trackEvent(EventNames.TELEHEALTH_CALL_ERROR, { metadata });
        },
        micToggled: (metadata?: { appointment_id?: string; enabled?: boolean }) => {
          trackEvent(EventNames.TELEHEALTH_MIC_TOGGLED, {
            metadata: {
              ...(metadata?.enabled !== undefined && {
                enabled: String(metadata.enabled),
              }),
              ...metadata,
            },
          });
        },
        videoToggled: (metadata?: { appointment_id?: string; enabled?: boolean }) => {
          trackEvent(EventNames.TELEHEALTH_VIDEO_TOGGLED, {
            metadata: {
              ...(metadata?.enabled !== undefined && {
                enabled: String(metadata.enabled),
              }),
              ...metadata,
            },
          });
        },
        patientJoined: (metadata?: {
          appointment_id?: string;
          encounter_id?: string;
          participant_count?: number;
        }) => {
          trackEvent(EventNames.TELEHEALTH_PATIENT_JOINED, {
            metadata: {
              ...(metadata?.participant_count !== undefined && {
                participant_count: String(metadata.participant_count),
              }),
              ...metadata,
            },
          });
        },
        patientLeft: (metadata?: { appointment_id?: string; encounter_id?: string }) => {
          trackEvent(EventNames.TELEHEALTH_PATIENT_LEFT, { metadata });
        },
        participantAdmitted: (metadata?: { appointment_id?: string }) => {
          trackEvent(EventNames.TELEHEALTH_PARTICIPANT_ADMITTED, { metadata });
        },
        participantDenied: (metadata?: { appointment_id?: string }) => {
          trackEvent(EventNames.TELEHEALTH_PARTICIPANT_DENIED, { metadata });
        },
        waitingRoomRequestShown: (metadata?: {
          appointment_id?: string;
          participant_count?: number;
        }) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_REQUEST_SHOWN, { metadata });
        },
        endCallWithRecordingWarning: (metadata?: {
          appointment_id?: string;
          encounter_id?: string;
        }) => {
          trackEvent(EventNames.TELEHEALTH_END_CALL_WITH_RECORDING_WARNING, { metadata });
        },
        sessionEndInitiated: (metadata?: {
          encounter_id?: string;
          had_other_participants?: boolean;
          recording_active_at_confirm?: boolean;
        }) => {
          trackEvent(EventNames.TELEHEALTH_SESSION_END_INITIATED, {
            metadata: {
              ...metadata,
              ...(metadata?.had_other_participants !== undefined && {
                had_other_participants: String(metadata.had_other_participants),
              }),
              ...(metadata?.recording_active_at_confirm !== undefined && {
                recording_active_at_confirm: String(metadata.recording_active_at_confirm),
              }),
            },
          });
        },
        linkCopied: (metadata?: {
          appointment_id?: string;
          encounter_id?: string;
          source?: string;
        }) => {
          trackEvent(EventNames.TELEHEALTH_LINK_COPIED, { metadata });
        },
        patientNotified: (metadata?: {
          appointment_id?: string;
          encounter_id?: string;
          source?: string;
        }) => {
          trackEvent(EventNames.TELEHEALTH_PATIENT_NOTIFIED, { metadata });
        },
        deviceSwitched: (metadata?: {
          appointment_id?: string;
          device_type?: 'mic' | 'camera' | 'speaker';
          during_recording?: boolean;
        }) => {
          trackEvent(EventNames.TELEHEALTH_DEVICE_SWITCHED, {
            metadata: {
              ...(metadata?.during_recording !== undefined && {
                during_recording: String(metadata.during_recording),
              }),
              ...metadata,
            },
          });
        },
        deviceSwitchFailed: (metadata?: {
          appointment_id?: string;
          device_type?: 'mic' | 'camera' | 'speaker';
          error_code?: string;
        }) => {
          trackEvent(EventNames.TELEHEALTH_DEVICE_SWITCH_FAILED, { metadata });
        },
        linkCopyFailed: (metadata?: { appointment_id?: string; error_message?: string }) => {
          trackEvent(EventNames.TELEHEALTH_LINK_COPY_FAILED, { metadata });
        },
        sendToPatientFailed: (metadata?: {
          appointment_id?: string;
          encounter_id?: string;
          source?: string;
          error_message?: string;
        }) => {
          trackEvent(EventNames.TELEHEALTH_SEND_TO_PATIENT_FAILED, { metadata });
        },
        screenShareToggled: (metadata?: { appointment_id?: string; enabled?: boolean }) => {
          trackEvent(EventNames.TELEHEALTH_SCREEN_SHARE_TOGGLED, {
            metadata: {
              ...(metadata?.enabled !== undefined && {
                enabled: String(metadata.enabled),
              }),
              ...metadata,
            },
          });
        },
        preCallCompleted: (metadata?: {
          appointment_id?: string;
          camera_on?: boolean;
          mic_on?: boolean;
          time_in_lobby_ms?: number;
        }) => {
          trackEvent(EventNames.TELEHEALTH_PRECALL_COMPLETED, {
            metadata: {
              ...(metadata?.camera_on !== undefined && {
                camera_on: String(metadata.camera_on),
              }),
              ...(metadata?.mic_on !== undefined && {
                mic_on: String(metadata.mic_on),
              }),
              ...(metadata?.time_in_lobby_ms !== undefined && {
                time_in_lobby_ms: String(metadata.time_in_lobby_ms),
              }),
              ...metadata,
            },
          });
        },
        noiseCancellationToggled: (metadata?: { appointment_id?: string; enabled?: boolean }) => {
          trackEvent(EventNames.TELEHEALTH_NOISE_CANCELLATION_TOGGLED, {
            metadata: {
              ...(metadata?.enabled !== undefined && {
                enabled: String(metadata.enabled),
              }),
              ...metadata,
            },
          });
        },
        backgroundBlurChanged: (metadata?: {
          appointment_id?: string;
          mode?: 'none' | 'light' | 'strong';
        }) => {
          trackEvent(EventNames.TELEHEALTH_BACKGROUND_BLUR_CHANGED, {
            metadata,
          });
        },
        selfViewToggled: (metadata?: { appointment_id?: string; hidden?: boolean }) => {
          trackEvent(EventNames.TELEHEALTH_SELF_VIEW_TOGGLED, {
            metadata: {
              ...(metadata?.hidden !== undefined && {
                hidden: String(metadata.hidden),
              }),
              ...metadata,
            },
          });
        },
        layoutChanged: (metadata?: {
          appointment_id?: string;
          layout?: string;
          participant_count?: number;
        }) => {
          trackEvent(EventNames.TELEHEALTH_LAYOUT_CHANGED, {
            metadata: {
              ...(metadata?.participant_count !== undefined && {
                participant_count: String(metadata.participant_count),
              }),
              ...metadata,
            },
          });
        },
        pipOpened: (metadata?: { strategy?: string; trigger?: string }) => {
          trackEvent(EventNames.TELEHEALTH_PIP_OPENED, { metadata });
        },
        pipClosed: (metadata?: { strategy?: string; trigger?: string }) => {
          trackEvent(EventNames.TELEHEALTH_PIP_CLOSED, { metadata });
        },
        pipError: (metadata?: {
          strategy?: string;
          error_type?: string;
          error_message?: string;
        }) => {
          trackEvent(EventNames.TELEHEALTH_PIP_ERROR, { metadata });
        },
        pipAutoTriggered: (metadata?: { strategy?: string }) => {
          trackEvent(EventNames.TELEHEALTH_PIP_AUTO_TRIGGERED, { metadata });
        },
        prejoinCopyLinkClicked: (metadata?: {
          appointment_id?: string;
          encounter_id?: string;
          source?: string;
          outcome?: string;
        }) => {
          trackEvent(EventNames.TELEHEALTH_PREJOIN_COPY_LINK_CLICKED, { metadata });
        },
        prejoinSendInviteClicked: (metadata?: {
          appointment_id?: string;
          encounter_id?: string;
          source?: string;
          outcome?: string;
        }) => {
          trackEvent(EventNames.TELEHEALTH_PREJOIN_SEND_INVITE_CLICKED, { metadata });
        },
        waitingRoomLinkCopyAttempted: (
          metadata?: Record<string, unknown> & {
            appointment_id?: string;
            encounter_id?: string;
            source?: string;
            outcome?: 'success' | 'failed' | 'unavailable';
            has_link?: boolean;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_LINK_COPY_ATTEMPTED, {
            metadata: {
              ...metadata,
              ...(metadata?.has_link !== undefined && {
                has_link: String(metadata.has_link),
              }),
            },
          });
        },
        waitingRoomPanelOpened: (
          metadata?: Record<string, unknown> & {
            source?: string;
            waiting_count?: number;
            provider_availability?: string;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_PANEL_OPENED, {
            metadata: {
              ...metadata,
              ...(metadata?.waiting_count !== undefined && {
                waiting_count: String(metadata.waiting_count),
              }),
            },
          });
        },
        waitingRoomPanelClosed: (
          metadata?: Record<string, unknown> & {
            source?: string;
            waiting_count?: number;
            provider_availability?: string;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_PANEL_CLOSED, {
            metadata: {
              ...metadata,
              ...(metadata?.waiting_count !== undefined && {
                waiting_count: String(metadata.waiting_count),
              }),
            },
          });
        },
        waitingRoomAvailabilityChanged: (
          metadata?: Record<string, unknown> & {
            source?: string;
            requested_status?: string;
            previous_status?: string;
            status?: string;
            outcome?: 'success' | 'failed';
            error_code?: string;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_AVAILABILITY_CHANGED, {
            metadata,
          });
        },
        waitingRoomRemoveRequested: (
          metadata?: Record<string, unknown> & {
            entry_id?: string;
            entry_state?: string;
            source?: string;
            waiting_count?: number;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_REMOVE_REQUESTED, {
            metadata: {
              ...metadata,
              ...(metadata?.waiting_count !== undefined && {
                waiting_count: String(metadata.waiting_count),
              }),
            },
          });
        },
        waitingRoomDestinationPickerOpened: (
          metadata?: Record<string, unknown> & {
            entry_id?: string;
            entry_state?: string;
            destination_count?: number;
            appointment_count?: number;
            has_active_call?: boolean;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_DESTINATION_PICKER_OPENED, {
            metadata: {
              ...metadata,
              ...(metadata?.destination_count !== undefined && {
                destination_count: String(metadata.destination_count),
              }),
              ...(metadata?.appointment_count !== undefined && {
                appointment_count: String(metadata.appointment_count),
              }),
              ...(metadata?.has_active_call !== undefined && {
                has_active_call: String(metadata.has_active_call),
              }),
            },
          });
        },
        waitingRoomDestinationSelected: (
          metadata?: Record<string, unknown> & {
            entry_id?: string;
            entry_state?: string;
            destination_type?: 'active_call' | 'appointment' | 'walk_in';
            encounter_id?: string;
            appointment_id?: string;
            skip_pre_call_lobby?: boolean;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_DESTINATION_SELECTED, {
            metadata: {
              ...metadata,
              ...(metadata?.skip_pre_call_lobby !== undefined && {
                skip_pre_call_lobby: String(metadata.skip_pre_call_lobby),
              }),
            },
          });
        },
        waitingRoomDestinationAutoSelected: (
          metadata?: Record<string, unknown> & {
            entry_id?: string;
            entry_state?: string;
            destination_type?: 'walk_in';
            skip_pre_call_lobby?: boolean;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_DESTINATION_AUTO_SELECTED, {
            metadata: {
              ...metadata,
              ...(metadata?.skip_pre_call_lobby !== undefined && {
                skip_pre_call_lobby: String(metadata.skip_pre_call_lobby),
              }),
            },
          });
        },
        waitingRoomDestinationCancelled: (
          metadata?: Record<string, unknown> & {
            entry_id?: string;
            entry_state?: string;
            destination_count?: number;
            appointment_count?: number;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_DESTINATION_CANCELLED, {
            metadata: {
              ...metadata,
              ...(metadata?.destination_count !== undefined && {
                destination_count: String(metadata.destination_count),
              }),
              ...(metadata?.appointment_count !== undefined && {
                appointment_count: String(metadata.appointment_count),
              }),
            },
          });
        },
        waitingRoomDestinationLaunchFailed: (
          metadata?: Record<string, unknown> & {
            entry_id?: string;
            entry_state?: string;
            destination_type?: 'active_call' | 'appointment' | 'walk_in';
            appointment_id?: string;
            encounter_id?: string;
            error_code?: string;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_DESTINATION_LAUNCH_FAILED, {
            metadata,
          });
        },
        waitingRoomAutoAdmitStarted: (
          metadata?: Record<string, unknown> & {
            entry_id?: string;
            entry_state?: string;
            encounter_id?: string;
            elapsed_ms?: number;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_AUTO_ADMIT_STARTED, {
            metadata: {
              ...metadata,
              ...(metadata?.elapsed_ms !== undefined && {
                elapsed_ms: String(metadata.elapsed_ms),
              }),
            },
          });
        },
        waitingRoomAutoAdmitSucceeded: (
          metadata?: Record<string, unknown> & {
            entry_id?: string;
            entry_state?: string;
            encounter_id?: string;
            elapsed_ms?: number;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_AUTO_ADMIT_SUCCEEDED, {
            metadata: {
              ...metadata,
              ...(metadata?.elapsed_ms !== undefined && {
                elapsed_ms: String(metadata.elapsed_ms),
              }),
            },
          });
        },
        waitingRoomAutoAdmitFailed: (
          metadata?: Record<string, unknown> & {
            entry_id?: string;
            entry_state?: string;
            encounter_id?: string;
            elapsed_ms?: number;
            error_code?: string;
          },
        ) => {
          trackEvent(EventNames.TELEHEALTH_WAITING_ROOM_AUTO_ADMIT_FAILED, {
            metadata: {
              ...metadata,
              ...(metadata?.elapsed_ms !== undefined && {
                elapsed_ms: String(metadata.elapsed_ms),
              }),
            },
          });
        },
      },

      // Membership metrics
      membership: {
        deactivatedScreenViewed: (metadata?: { is_admin?: boolean }) => {
          trackEvent(EventNames.MEMBERSHIP_DEACTIVATED_SCREEN_VIEWED, {
            metadata: {
              ...(metadata?.is_admin !== undefined && {
                is_admin: String(metadata.is_admin),
              }),
            },
          });
        },
      },

      // Tour metrics
      tour: {
        promptShown: (metadata?: {
          tour_id?: string;
          tour_version?: number;
          step_count?: number;
        }) => {
          trackEvent(EventNames.TOUR_PROMPT_SHOWN, {
            metadata: {
              ...(metadata?.tour_version !== undefined && {
                tour_version: String(metadata.tour_version),
              }),
              ...(metadata?.step_count !== undefined && {
                step_count: String(metadata.step_count),
              }),
              ...metadata,
            },
          });
        },
        started: (metadata?: { tour_id?: string; tour_version?: number; step_count?: number }) => {
          trackEvent(EventNames.TOUR_STARTED, {
            metadata: {
              ...(metadata?.tour_version !== undefined && {
                tour_version: String(metadata.tour_version),
              }),
              ...(metadata?.step_count !== undefined && {
                step_count: String(metadata.step_count),
              }),
              ...metadata,
            },
          });
        },
        stepViewed: (metadata?: {
          tour_id?: string;
          step_id?: string;
          step_index?: number;
          total_steps?: number;
        }) => {
          trackEvent(EventNames.TOUR_STEP_VIEWED, {
            metadata: {
              ...(metadata?.step_index !== undefined && {
                step_index: String(metadata.step_index),
              }),
              ...(metadata?.total_steps !== undefined && {
                total_steps: String(metadata.total_steps),
              }),
              ...metadata,
            },
          });
        },
        dismissed: (metadata?: {
          tour_id?: string;
          last_step_id?: string;
          last_step_index?: number;
        }) => {
          trackEvent(EventNames.TOUR_DISMISSED, {
            metadata: {
              ...(metadata?.last_step_index !== undefined && {
                last_step_index: String(metadata.last_step_index),
              }),
              ...metadata,
            },
          });
        },
        completed: (metadata?: {
          tour_id?: string;
          tour_version?: number;
          total_steps?: number;
        }) => {
          trackEvent(EventNames.TOUR_COMPLETED, {
            metadata: {
              ...(metadata?.tour_version !== undefined && {
                tour_version: String(metadata.tour_version),
              }),
              ...(metadata?.total_steps !== undefined && {
                total_steps: String(metadata.total_steps),
              }),
              ...metadata,
            },
          });
        },
      },

      // Note viewer modal events
      noteViewer: {
        opened: (metadata: {
          note_id: string;
          completion_status?: string;
          section_count?: number;
          has_patient?: boolean;
          has_template?: boolean;
          source?: string;
        }) => {
          trackEvent(EventNames.NOTE_VIEWER_OPENED, {
            metadata: {
              ...metadata,
              ...(metadata.section_count !== undefined && {
                section_count: String(metadata.section_count),
              }),
              ...(metadata.has_patient !== undefined && {
                has_patient: String(metadata.has_patient),
              }),
              ...(metadata.has_template !== undefined && {
                has_template: String(metadata.has_template),
              }),
            },
          });
        },
        openInNewTabClicked: (metadata: { note_id: string }) => {
          trackEvent(EventNames.NOTE_VIEWER_OPEN_IN_NEW_TAB_CLICKED, { metadata });
        },
        pdfDownloaded: (metadata: { note_id: string }) => {
          trackEvent(EventNames.NOTE_VIEWER_PDF_DOWNLOADED, { metadata });
        },
        pdfDownloadFailed: (metadata: { note_id: string; error_message?: string }) => {
          trackEvent(EventNames.NOTE_VIEWER_PDF_DOWNLOAD_FAILED, { metadata });
        },
      },

      // Addon upsell modal events
      addonUpsell: {
        opened: (metadata?: { addon_id?: string; source?: string }) => {
          trackEvent(EventNames.ADDON_UPSELL_OPENED, { metadata });
        },
        slideViewed: (metadata?: {
          addon_id?: string;
          slide_index?: number;
          slide_title?: string;
        }) => {
          trackEvent(EventNames.ADDON_UPSELL_SLIDE_VIEWED, {
            metadata: {
              ...(metadata?.slide_index !== undefined && {
                slide_index: String(metadata.slide_index),
              }),
              ...metadata,
            },
          });
        },
        slideNavigated: (metadata?: {
          addon_id?: string;
          direction?: string;
          from_step?: number;
          to_step?: number;
        }) => {
          trackEvent(EventNames.ADDON_UPSELL_SLIDE_NAVIGATED, {
            metadata: {
              ...(metadata?.from_step !== undefined && {
                from_step: String(metadata.from_step),
              }),
              ...(metadata?.to_step !== undefined && {
                to_step: String(metadata.to_step),
              }),
              ...metadata,
            },
          });
        },
        purchaseViewed: (metadata?: { addon_id?: string; purchase_role?: string }) => {
          trackEvent(EventNames.ADDON_UPSELL_PURCHASE_VIEWED, { metadata });
        },
        planSelected: (metadata?: {
          addon_id?: string;
          selected_plan?: string;
          plan_name?: string;
        }) => {
          trackEvent(EventNames.ADDON_UPSELL_PLAN_SELECTED, { metadata });
        },
        clinicianSelected: (metadata?: {
          addon_id?: string;
          selected_count?: number;
          total_count?: number;
        }) => {
          trackEvent(EventNames.ADDON_UPSELL_CLINICIAN_SELECTED, {
            metadata: {
              ...(metadata?.selected_count !== undefined && {
                selected_count: String(metadata.selected_count),
              }),
              ...(metadata?.total_count !== undefined && {
                total_count: String(metadata.total_count),
              }),
              ...metadata,
            },
          });
        },
        confirmed: (metadata?: {
          addon_id?: string;
          purchase_role?: string;
          selected_plan?: string;
          total_cents?: number;
        }) => {
          trackEvent(EventNames.ADDON_UPSELL_CONFIRMED, {
            metadata: {
              ...(metadata?.total_cents !== undefined && {
                total_cents: String(metadata.total_cents),
              }),
              ...metadata,
            },
          });
        },
        succeeded: (metadata?: {
          addon_id?: string;
          purchase_role?: string;
          user_count?: number;
        }) => {
          trackEvent(EventNames.ADDON_UPSELL_SUCCEEDED, {
            metadata: {
              ...(metadata?.user_count !== undefined && {
                user_count: String(metadata.user_count),
              }),
              ...metadata,
            },
          });
        },
        failed: (metadata?: {
          addon_id?: string;
          purchase_role?: string;
          error_message?: string;
        }) => {
          trackEvent(EventNames.ADDON_UPSELL_FAILED, { metadata });
        },
        closed: (metadata?: { addon_id?: string; phase?: string; education_step?: number }) => {
          trackEvent(EventNames.ADDON_UPSELL_CLOSED, {
            metadata: {
              ...(metadata?.education_step !== undefined && {
                education_step: String(metadata.education_step),
              }),
              ...metadata,
            },
          });
        },
        accessRequested: (metadata?: { addon_id?: string }) => {
          trackEvent(EventNames.ADDON_UPSELL_ACCESS_REQUESTED, { metadata });
        },
      },

      // Addon onboarding modal events (GDN-1709) — first-run onboarding for an added capability/role.
      // onboarding_id identifies which onboarding (e.g. 'prescribing-proxy'); no PHI (never field values).
      addonOnboarding: {
        opened: (metadata?: { onboarding_id?: string }) => {
          trackEvent(EventNames.ADDON_ONBOARDING_OPENED, { metadata });
        },
        stepChanged: (metadata?: {
          onboarding_id?: string;
          step_index?: number;
          step_kind?: string;
          direction?: string;
        }) => {
          trackEvent(EventNames.ADDON_ONBOARDING_STEP_CHANGED, {
            metadata: {
              ...(metadata?.onboarding_id !== undefined && {
                onboarding_id: metadata.onboarding_id,
              }),
              ...(metadata?.step_index !== undefined && {
                step_index: String(metadata.step_index),
              }),
              ...(metadata?.step_kind !== undefined && { step_kind: metadata.step_kind }),
              ...(metadata?.direction !== undefined && { direction: metadata.direction }),
            },
          });
        },
        submitted: (metadata?: { onboarding_id?: string; step_index?: number }) => {
          trackEvent(EventNames.ADDON_ONBOARDING_SUBMITTED, {
            metadata: {
              ...(metadata?.onboarding_id !== undefined && {
                onboarding_id: metadata.onboarding_id,
              }),
              ...(metadata?.step_index !== undefined && {
                step_index: String(metadata.step_index),
              }),
            },
          });
        },
        closed: (metadata?: { onboarding_id?: string; step_index?: number; reason?: string }) => {
          trackEvent(EventNames.ADDON_ONBOARDING_CLOSED, {
            metadata: {
              ...(metadata?.onboarding_id !== undefined && {
                onboarding_id: metadata.onboarding_id,
              }),
              ...(metadata?.step_index !== undefined && {
                step_index: String(metadata.step_index),
              }),
              ...(metadata?.reason !== undefined && { reason: metadata.reason }),
            },
          });
        },
      },

      // JotMeet settings page (/user#telehealth)
      jotmeetSettings: {
        opened: (metadata: {
          /** entitled-admin | entitled-non-admin | unentitled-admin | unentitled-non-admin */
          settings_state: string;
        }) => {
          trackEvent(EventNames.JOTMEET_SETTINGS_OPENED, { metadata });
        },
        learnMoreClicked: () => {
          trackEvent(EventNames.JOTMEET_SETTINGS_LEARN_MORE_CLICKED);
        },
        nonAdminLearnMoreClicked: () => {
          trackEvent(EventNames.JOTMEET_SETTINGS_NON_ADMIN_LEARN_MORE_CLICKED);
        },
        waitingRoomLinkCopyAttempted: (metadata: {
          outcome: 'success' | 'failed' | 'unavailable';
          has_link?: boolean;
        }) => {
          trackEvent(EventNames.JOTMEET_SETTINGS_LINK_COPY_ATTEMPTED, {
            metadata: {
              ...metadata,
              ...(metadata.has_link !== undefined && {
                has_link: String(metadata.has_link),
              }),
            },
          });
        },
        waitingRoomLinkCopied: () => {
          trackEvent(EventNames.JOTMEET_SETTINGS_LINK_COPIED);
        },
        waitingRoomAlwaysOpenToggled: (metadata: { enabled: boolean }) => {
          trackEvent(EventNames.JOTMEET_SETTINGS_ALWAYS_OPEN_TOGGLED, {
            metadata: { enabled: String(metadata.enabled) },
          });
        },
      },

      // Server UI — Tile Row events
      tile: {
        actionClicked: (metadata: {
          container_key?: string;
          entity_id?: string;
          type_key: string;
          tile_id: string;
          action_key: string;
          variant: string;
          surface?: string;
        }) => {
          trackEvent(EventNames.TILE_ACTION_CLICKED, { metadata });
        },
        actionFailed: (metadata: {
          container_key?: string;
          entity_id?: string;
          type_key: string;
          tile_id: string;
          action_key: string;
          variant: string;
          error_type?: string;
          surface?: string;
        }) => {
          trackEvent(EventNames.TILE_ACTION_FAILED, { metadata });
        },
        hovered: (metadata: {
          container_key?: string;
          entity_id?: string;
          type_key: string;
          tile_id: string;
          variant: string;
          dwell_ms: string;
          surface?: string;
        }) => {
          trackEvent(EventNames.TILE_HOVERED, { metadata });
        },
      },

      patientPageV3: {
        bootstrapRequested: (metadata: { patient_id: string }) => {
          trackEvent(EventNames.PATIENT_PAGE_V3_BOOTSTRAP_REQUESTED, { metadata });
        },
        opened: (metadata: {
          patient_id: string;
          duration_ms: number;
          section_count: number;
          intelligence_state: string;
        }) => {
          trackEvent(EventNames.PATIENT_PAGE_V3_OPENED, { metadata });
        },
        sectionEntered: (metadata: {
          patient_id: string;
          section_key: string;
          trigger: string;
        }) => {
          trackEvent(EventNames.PATIENT_PAGE_V3_SECTION_ENTERED, { metadata });
        },
        railClicked: (metadata: { patient_id: string; section_key: string }) => {
          trackEvent(EventNames.PATIENT_PAGE_V3_RAIL_CLICKED, { metadata });
        },
        railSearchSubmitted: (metadata: {
          patient_id: string;
          section_key: string;
          result_count: number;
          via: string;
        }) => {
          trackEvent(EventNames.PATIENT_PAGE_V3_RAIL_SEARCH_SUBMITTED, { metadata });
        },
        unsupportedSectionOpened: (metadata: {
          patient_id: string;
          section_key: string;
          renderer_key: string;
        }) => {
          trackEvent(EventNames.PATIENT_PAGE_V3_UNSUPPORTED_SECTION_OPENED, { metadata });
        },
        staleIntelligenceEntered: (metadata: { patient_id: string; generated_at?: string }) => {
          trackEvent(EventNames.PATIENT_PAGE_V3_STALE_INTELLIGENCE_ENTERED, { metadata });
        },
        profileEditStarted: (metadata: { patient_id: string; group: string }) => {
          trackEvent(EventNames.PATIENT_PAGE_V3_PROFILE_EDIT_STARTED, { metadata });
        },
        profileEditSubmitted: (metadata: { patient_id: string; group: string }) => {
          trackEvent(EventNames.PATIENT_PAGE_V3_PROFILE_EDIT_SUBMITTED, { metadata });
        },
        profileEditClosed: (metadata: {
          patient_id: string;
          group: string;
          had_unsaved_changes: boolean;
        }) => {
          trackEvent(EventNames.PATIENT_PAGE_V3_PROFILE_EDIT_CLOSED, { metadata });
        },
      },

      patientAssessments: {
        opened: (metadata: { patient_id: string; instrument_count: number }) => {
          trackEvent(EventNames.PATIENT_ASSESSMENTS_OPENED, { metadata });
        },
        instrumentSelected: (metadata: { patient_id: string; instrument_key: string }) => {
          trackEvent(EventNames.PATIENT_ASSESSMENTS_INSTRUMENT_SELECTED, { metadata });
        },
        scoreSelected: (metadata: {
          patient_id: string;
          instrument_key: string;
          source: string;
        }) => {
          trackEvent(EventNames.PATIENT_ASSESSMENTS_SCORE_SELECTED, { metadata });
        },
        scoreSubmitted: (metadata: {
          patient_id: string;
          instrument_key: string;
          action: 'add' | 'edit';
          source: string;
        }) => {
          trackEvent(EventNames.PATIENT_ASSESSMENTS_SCORE_SUBMITTED, { metadata });
        },
        scoreDeleted: (metadata: {
          patient_id: string;
          instrument_key: string;
          source: string;
        }) => {
          trackEvent(EventNames.PATIENT_ASSESSMENTS_SCORE_DELETED, { metadata });
        },
        sendClicked: (metadata: { patient_id: string; instrument_key?: string }) => {
          trackEvent(EventNames.PATIENT_ASSESSMENTS_SEND_CLICKED, { metadata });
        },
        submissionOpened: (metadata: { patient_id: string; instrument_key: string }) => {
          trackEvent(EventNames.PATIENT_ASSESSMENTS_SUBMISSION_OPENED, { metadata });
        },
      },

      // Clinical chart events
      clinicalChart: {
        expanded: (metadata: { patient_id: string }) => {
          trackEvent(EventNames.CLINICAL_CHART_EXPANDED, { metadata });
        },
        collapsed: (metadata: { patient_id: string }) => {
          trackEvent(EventNames.CLINICAL_CHART_COLLAPSED, { metadata });
        },
        addModalOpened: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'medication' | 'allergy';
        }) => {
          trackEvent(EventNames.CLINICAL_CHART_ADD_MODAL_OPENED, { metadata });
        },
        itemAdded: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'medication' | 'allergy';
        }) => {
          trackEvent(EventNames.CLINICAL_CHART_ITEM_ADDED, { metadata });
        },
        itemAddFailed: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'medication' | 'allergy';
        }) => {
          trackEvent(EventNames.CLINICAL_CHART_ITEM_ADD_FAILED, { metadata });
        },
        itemUpdated: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'medication' | 'allergy';
        }) => {
          trackEvent(EventNames.CLINICAL_CHART_ITEM_UPDATED, { metadata });
        },
        itemUpdateFailed: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'medication' | 'allergy';
        }) => {
          trackEvent(EventNames.CLINICAL_CHART_ITEM_UPDATE_FAILED, { metadata });
        },
        itemDeleted: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'medication' | 'allergy';
        }) => {
          trackEvent(EventNames.CLINICAL_CHART_ITEM_DELETED, { metadata });
        },
        itemDeleteFailed: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'medication' | 'allergy';
        }) => {
          trackEvent(EventNames.CLINICAL_CHART_ITEM_DELETE_FAILED, { metadata });
        },
        suggestionAccepted: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'medication';
        }) => {
          trackEvent(EventNames.CLINICAL_CHART_SUGGESTION_ACCEPTED, { metadata });
        },
        suggestionDismissed: (metadata: {
          patient_id: string;
          item_type: 'diagnosis' | 'medication';
        }) => {
          trackEvent(EventNames.CLINICAL_CHART_SUGGESTION_DISMISSED, { metadata });
        },
      },

      // Appointment labels + groups metrics (GDN-1968). Consolidated events —
      // the `action` field distinguishes specific operations.
      appointmentLabels: {
        catalogChanged: (metadata: {
          action:
            | 'group_created'
            | 'label_created'
            | 'label_renamed'
            | 'label_moved'
            | 'label_deleted'
            | 'group_renamed'
            | 'group_deleted';
          organization_id?: string;
          in_group?: boolean;
          moved_to_group?: boolean;
        }) => {
          const { in_group, moved_to_group, ...rest } = metadata;
          trackEvent(EventNames.LABEL_CATALOG_CHANGED, {
            metadata: {
              ...rest,
              ...(in_group !== undefined && { in_group: String(in_group) }),
              ...(moved_to_group !== undefined && {
                moved_to_group: String(moved_to_group),
              }),
            },
          });
        },
        appointmentLabelsChanged: (metadata: {
          action: 'added' | 'removed';
          source_surface: string;
          is_grouped: boolean;
          replaced_group_label?: boolean;
        }) => {
          const { is_grouped, replaced_group_label, ...rest } = metadata;
          trackEvent(EventNames.APPOINTMENT_LABELS_CHANGED, {
            metadata: {
              ...rest,
              is_grouped: String(is_grouped),
              ...(replaced_group_label !== undefined && {
                replaced_group_label: String(replaced_group_label),
              }),
            },
          });
        },
        typeDefaultLabelsChanged: (metadata: {
          action: 'added' | 'removed';
          is_grouped: boolean;
          replaced_group_label?: boolean;
        }) => {
          const { is_grouped, replaced_group_label, ...rest } = metadata;
          trackEvent(EventNames.APPOINTMENT_TYPE_DEFAULT_LABELS_CHANGED, {
            metadata: {
              ...rest,
              is_grouped: String(is_grouped),
              ...(replaced_group_label !== undefined && {
                replaced_group_label: String(replaced_group_label),
              }),
            },
          });
        },
        calendarLensSelected: (metadata: { lens_type: 'group' | 'label' | 'off' }) => {
          trackEvent(EventNames.CALENDAR_GROUPED_BY_LABEL_SELECTED, { metadata });
        },
        calendarSplitLanesToggled: (metadata: { enabled: boolean }) => {
          trackEvent(EventNames.CALENDAR_LABEL_LANES_SPLIT_TOGGLED, {
            metadata: { enabled: String(metadata.enabled) },
          });
        },
        catalogOpened: (metadata: { source: 'picker_link' | 'schedule_documentation' }) => {
          trackEvent(EventNames.LABELS_CATALOG_OPENED, { metadata });
        },
      },
      // Self-serve scheduling settings (GDN-2187).
      schedulingSettings: {
        opened: (metadata?: { organization_id?: string }) => {
          trackEvent(EventNames.SCHEDULING_SETTINGS_OPENED, { metadata });
        },
        confirmationModeChanged: (metadata: { mode: string }) => {
          trackEvent(EventNames.BOOKING_CONFIRMATION_MODE_CHANGED, { metadata });
        },
        gateToggled: (metadata: { gate: string; enabled: boolean }) => {
          trackEvent(EventNames.BOOKING_GATE_TOGGLED, {
            metadata: { gate: metadata.gate, enabled: String(metadata.enabled) },
          });
        },
        gateChanged: (metadata: {
          gate: string;
          field: 'mode' | 'applies_to_patient' | 'checkpoint' | 'form_definition_id';
        }) => {
          trackEvent(EventNames.BOOKING_GATE_CHANGED, { metadata });
        },
        setupPaymentsClicked: () => {
          trackEvent(EventNames.BOOKING_GATE_SETUP_PAYMENTS_CLICKED);
        },
        availabilityRuleChanged: (metadata: {
          action: 'created' | 'updated' | 'deleted';
          effect: 'open' | 'block';
        }) => {
          trackEvent(EventNames.AVAILABILITY_RULE_CHANGED, { metadata });
        },
        manageNoticeChanged: (metadata: { minimum_notice_hours: number }) => {
          trackEvent(EventNames.BOOKING_MANAGE_NOTICE_CHANGED, {
            metadata: {
              minimum_notice_hours: String(metadata.minimum_notice_hours),
            },
          });
        },
        selfCancelToggled: (metadata: { enabled: boolean }) => {
          trackEvent(EventNames.BOOKING_SELF_CANCEL_TOGGLED, {
            metadata: { enabled: String(metadata.enabled) },
          });
        },
        bookingPageLinkCopied: () => {
          trackEvent(EventNames.BOOKING_PAGE_LINK_COPIED);
        },
      },
      bookingRequests: {
        opened: (metadata: { pending_count: number }) => {
          trackEvent(EventNames.BOOKING_REQUESTS_OPENED, {
            metadata: { pending_count: String(metadata.pending_count) },
          });
        },
        confirmClicked: (metadata: { appointment_id: string; source: 'queue' | 'drawer' }) => {
          trackEvent(EventNames.BOOKING_REQUEST_CONFIRM_CLICKED, { metadata });
        },
        declineClicked: (metadata: { booking_prospect_id: string; source: 'queue' | 'drawer' }) => {
          trackEvent(EventNames.BOOKING_REQUEST_DECLINE_CLICKED, { metadata });
        },
        reviewOpened: (metadata: { booking_prospect_id: string; source: 'queue' | 'drawer' }) => {
          trackEvent(EventNames.BOOKING_REQUEST_REVIEW_OPENED, { metadata });
        },
        promoteSubmitted: (metadata: {
          booking_prospect_id: string;
          action: 'link_existing' | 'create_new';
          match_count: number;
          source: 'queue' | 'drawer';
        }) => {
          trackEvent(EventNames.BOOKING_REQUEST_PROMOTE_SUBMITTED, {
            metadata: {
              booking_prospect_id: metadata.booking_prospect_id,
              action: metadata.action,
              match_count: String(metadata.match_count),
              source: metadata.source,
            },
          });
        },
      },
      // Public sample note (/sample-note) — anonymous prospects exploring the
      // real note UI on committed fictional fixtures. `note_id` is the fixture
      // id (SAMPLE_NOTE_ID), so nothing here can carry PHI; the standard
      // ids-and-enums-only rule still applies.
      sampleNote: {
        opened: (metadata: { note_id: string }) => {
          trackEvent(EventNames.SAMPLE_NOTE_OPENED, { metadata });
        },
        ctaClicked: (metadata: { note_id: string; cta: string }) => {
          trackEvent(EventNames.SAMPLE_NOTE_CTA_CLICKED, { metadata });
        },
      },
    }),
    [trackEvent],
  );
}
